import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateFormat';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { devLog, devLogWithPrefix } from '../utils/logger';

const getDayOfWeek = (date: string) => {
  const days = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
  return days[new Date(date).getDay()];
};

const toDateKey = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split('T')[0];
};

const calculateHours = (startTime: string, endTime: string): number => {
  const parseTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours + minutes / 60;
  };
  
  const start = parseTime(startTime);
  const end = parseTime(endTime);
  return Math.round((end - start) * 10) / 10; // Округление до 1 знака после запятой
};

const getDurationSlotsByHours = (durationHours: number): number => {
  if (durationHours === 0.5) return 1;
  if (durationHours === 1.0) return 2;
  if (durationHours === 1.5) return 3;
  if (durationHours === 2.5) return 5;
  return 4; // 2.0 по умолчанию
};

const TOURNAMENT_STATUS_PRIORITY: Record<string, number> = {
  ACTIVE: 0,
  PLANNED: 1,
  COMPLETED: 2,
  CANCELLED: 3
};

const TOURNAMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активные',
  PLANNED: 'Запланированные',
  COMPLETED: 'Завершенные',
  CANCELLED: 'Отмененные'
};

const isSystemLevelBracketKey = (key: string): boolean => key.startsWith('_');
const normalizeLevelKey = (value: string | null | undefined): string =>
  String(value || '').trim().toUpperCase();
const extractPlayoffStageShort = (value: string | null | undefined): string => {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.match(/\d+\s*\/\s*\d+/)?.[0]?.replace(/\s+/g, '') || '';
};
const getPlayoffStageSortOrder = (stage: string | null | undefined): number => {
  const normalized = String(stage || '').trim().toLowerCase();
  if (!normalized) return 9999;
  const fractionMatch = normalized.match(/^1\/(\d+)$/);
  if (fractionMatch?.[1]) {
    const denominator = Number(fractionMatch[1]);
    if (Number.isFinite(denominator) && denominator > 0) {
      // 1/16 раньше 1/8, 1/8 раньше 1/4 и т.д.
      return -denominator;
    }
  }
  if (normalized.includes('финал') || normalized === 'final') return 0;
  return 9999;
};

const isCompletedLikeMatchStatus = (status: string | null | undefined): boolean => {
  const normalized = String(status || '').trim().toUpperCase();
  return ['COMPLETED', 'WALKOVER', 'RETIRED', 'CANCELLED'].includes(normalized);
};

type Club = {
  id: string;
  name: string;
};

type Court = {
  id: string;
  name: string;
  clubId: string;
  club?: Club;
};

type CourtAvailability = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  isPublic?: boolean; // Публичное предложение (более темный оттенок)
  club: { id: string; name: string };
  court: { id: string; name: string };
};

type CourtReservation = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  matchCode?: string;
  comment?: string;
  price?: number;
  status: string;
  tournament: { id: string; name: string };
  court: { id: string; name: string };
  player1?: { id: string; name: string };
  player2?: { id: string; name: string };
  player3?: { id: string; name: string };
  player4?: { id: string; name: string };
  hasLinkedMatch?: boolean;
  match?: {
    id: string;
    matchCode?: string;
    status: string;
    level?: string | null;
    stageMatchNumber?: number | null;
    round?: { name?: string | null } | null;
    previousMatches?: Array<{
      id: string;
      status: string;
      level?: string | null;
      stageMatchNumber?: number | null;
      nextMatchSlot?: string | null;
      round?: { name?: string | null } | null;
      player1?: { name: string } | null;
      player2?: { name: string } | null;
      player3?: { name: string } | null;
      player4?: { name: string } | null;
    }>;
  };
  servicePurchase?: { id: string; number?: string; date: string };
};

type UnreservedMatchOption = {
  id: string;
  tournamentId: string;
  reservationId?: string | null;
  isSuspended?: boolean;
  hadReservationHistory?: boolean;
  groupId: string | null;
  groupName: string | null;
  level: string | null;
  roundId: string | null;
  roundName: string | null;
  matchNumber: number | null;
  stageMatchNumber: number | null;
  matchCode: string | null;
  status: string;
  player1Id: string | null;
  player2Id: string | null;
  player3Id: string | null;
  player4Id: string | null;
  player1Name: string;
  player2Name: string;
  player3Name: string;
  player4Name: string;
  previousMatches?: Array<{
    id: string;
    status: string;
    nextMatchSlot: string | null;
    player1Name: string;
    player2Name: string;
    player3Name: string;
    player4Name: string;
    winnerName: string;
    winner2Name: string;
  }>;
};

type PublicProposalSlot = {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  courtId: string;
  courtName: string;
  clubId: string;
  clubName: string;
  blockIds: string[];
  isAvailable?: boolean;
  tournamentId?: string | null;
  tournamentName?: string | null;
  level?: string | null;
  groupId?: string | null;
  groupName?: string | null;
};

type SlotPlayerAvailability = {
  proposalId: string;
  date: string;
  startTime: string;
  endTime: string;
  clubName: string;
  courtName: string;
  tournamentName: string | null;
  groupName: string | null;
  readyPlayers: Array<{
    tournamentId: string;
    groupId: string;
    label: string;
    players: Array<{
        playerName: string;
      line: string;
      previousMatchDate?: string;
      previousMatchOpponent?: string;
      previousMatchTournament?: string;
      previousMatchTooltip?: string;
        noGamesPreviousInfo?: string;
      nextMatchDate?: string;
      nextMatchOpponent?: string;
      nextMatchTournament?: string;
      nextMatchTooltip?: string;
        noGamesNextInfo?: string;
        opponentsDetailed?: Array<{
          id?: string;
          responseId?: string;
          name: string;
          availabilityStatus?: string;
          prev: string;
          next: string;
          notificationSentAt?: string;
          notificationSentAtRaw?: string;
          reactionAt?: string;
          reactionAtRaw?: string;
          reactionUnviewed?: boolean;
          prevTooltip?: string;
          nextTooltip?: string;
        }>;
    }>;
  }>;
  thinkingPlayers: Array<{ label: string; players: string[] }>;
  rejectedPlayers: Array<{ label: string; players: string[] }>;
  sentPlayers: Array<{ label: string; players: string[] }>;
  ignoredPlayers: Array<{ label: string; players: string[] }>;
  totalResponses: number;
  assignedMatch?: {
    id: string;
    matchCode?: string | null;
    players: string[];
    tournamentId?: string | null;
    groupId?: string | null;
    tournamentName: string | null;
    groupName: string | null;
  } | null;
};

const CURRENT_GENERATED_MATCH_VALUE = '__current_reservation_match__';

const CourtReservations: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'calendar' | 'reservations' | 'proposals' | 'opponent-matching' | 'player-availability'>(() => {
    try {
      const raw = localStorage.getItem('courtReservationsFilters');
      const saved = raw ? JSON.parse(raw) : null;
      return saved?.activeTab || 'calendar';
    } catch { return 'calendar'; }
  });
  const [selectedDateFrom, setSelectedDateFrom] = useState(() => {
    try {
      const raw = localStorage.getItem('courtReservationsFilters');
      const saved = raw ? JSON.parse(raw) : null;
      return saved?.dateFrom || new Date().toISOString().split('T')[0];
    } catch { return new Date().toISOString().split('T')[0]; }
  });
  const [selectedDateTo, setSelectedDateTo] = useState(() => {
    try {
      const raw = localStorage.getItem('courtReservationsFilters');
      const saved = raw ? JSON.parse(raw) : null;
      return saved?.dateTo || new Date().toISOString().split('T')[0];
    } catch { return new Date().toISOString().split('T')[0]; }
  });
  const [selectedClubId, setSelectedClubId] = useState<string>(() => {
    try {
      const raw = localStorage.getItem('courtReservationsFilters');
      const saved = raw ? JSON.parse(raw) : null;
      return saved?.clubId || '';
    } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [availabilities, setAvailabilities] = useState<CourtAvailability[]>([]);
  const [reservations, setReservations] = useState<CourtReservation[]>([]);
  const [availabilityMode, setAvailabilityMode] = useState(false);
  const [reservationMode, setReservationMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [lastMarkedCell, setLastMarkedCell] = useState<{courtId: string; time: string} | null>(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCourtForReservation, setSelectedCourtForReservation] = useState<string>('');
  const [selectedTimeForReservation, setSelectedTimeForReservation] = useState<string>('');
  const [editingReservation, setEditingReservation] = useState<CourtReservation | null>(null);
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [tournamentPlayers, setTournamentPlayers] = useState<any[]>([]);
  const [reservationForm, setReservationForm] = useState({
    duration: 2.0,
    tournamentId: '',
    player1Id: '',
    player2Id: '',
    player3Id: '',
    player4Id: '',
    matchCode: '',
    price: 0,
    date: '',
    startTime: '',
    endTime: '',
    courtId: ''
  });
  const [unreservedMatches, setUnreservedMatches] = useState<UnreservedMatchOption[]>([]);
  const [loadingUnreservedMatches, setLoadingUnreservedMatches] = useState(false);
  const [includeReservedMatchesInSelect, setIncludeReservedMatchesInSelect] = useState(false);
  const [selectedGeneratedMatchId, setSelectedGeneratedMatchId] = useState('');
  const [selectedGeneratedMatchDbId, setSelectedGeneratedMatchDbId] = useState<string | null>(null);
  const [notifyTelegramGroupOnCreate, setNotifyTelegramGroupOnCreate] = useState(false);
  const [draggedReservation, setDraggedReservation] = useState<CourtReservation | null>(null);
  const [showGamesPicker, setShowGamesPicker] = useState(false);
  const [selectedTournamentForGamesPicker, setSelectedTournamentForGamesPicker] = useState<string>('');
  const [selectedLevelForGamesPicker, setSelectedLevelForGamesPicker] = useState<string>('');
  const [gamesPickerDuration, setGamesPickerDuration] = useState<number>(2.0);
  const [gamesPickerPlanMode, setGamesPickerPlanMode] = useState(false);
  const [gamesPickerPlanModeLoading, setGamesPickerPlanModeLoading] = useState(false);
  const [levelsForGamesPicker, setLevelsForGamesPicker] = useState<string[]>([]);
  const [gamesForPicker, setGamesForPicker] = useState<UnreservedMatchOption[]>([]);
  const [loadingGamesForPicker, setLoadingGamesForPicker] = useState(false);
  const [draggedMatchForReservation, setDraggedMatchForReservation] = useState<UnreservedMatchOption | null>(null);
  
  // Для публичных предложений слотов
  const [generatedSlots, setGeneratedSlots] = useState<Array<{
    date: string;
    startTime: string;
    endTime: string;
    clubId: string;
    clubName: string;
    courtId: string;
    courtName: string;
    checked: boolean;
  }>>([]);
  const [showSlotsModal, setShowSlotsModal] = useState(false);
  const [generatingSlots, setGeneratingSlots] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState<any>(null);
  const [showPeriodsModal, setShowPeriodsModal] = useState(false);
  const [requestParams, setRequestParams] = useState<{startDate?: string; endDate?: string; clubId?: string}>({});
  
  // Публичные предложения (сгруппированные по 2-часовым интервалам)
  const [publicProposals, setPublicProposals] = useState<Array<{
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    courtId: string;
    courtName: string;
    clubId: string;
    clubName: string;
    blockIds: string[];
    isAvailable?: boolean;
    tournamentId?: string | null;
    tournamentName?: string | null;
    level?: string | null;
    groupId?: string | null;
    groupName?: string | null;
    responsesCount?: number;
    totalResponsesCount?: number;
    tasksCount?: number;
  }>>([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [playerSlotAvailability, setPlayerSlotAvailability] = useState<SlotPlayerAvailability[]>([]);
  const [loadingPlayerSlotAvailability, setLoadingPlayerSlotAvailability] = useState(false);
  const [availabilityTournamentId, setAvailabilityTournamentId] = useState<string>('');
  const [availabilityGroupId, setAvailabilityGroupId] = useState<string>('');
  const [availabilityPlayerName, setAvailabilityPlayerName] = useState<string>('');
  const [availabilityGroups, setAvailabilityGroups] = useState<Array<{ id: string; name: string; level?: string }>>([]);
  
  // Состояние занятых слотов (визуальное отключение)
  const [occupiedSlots, setOccupiedSlots] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('occupiedSlots');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  
  // Для привязки предложений к турниру
  const [selectedTournamentForProposal, setSelectedTournamentForProposal] = useState<string>(() => {
    try {
      return localStorage.getItem('courtReservations.selectedTournamentForProposal') || '';
    } catch {
      return '';
    }
  });
  const [selectedLevelForProposal, setSelectedLevelForProposal] = useState<string>('');
  const [selectedGroupForProposal, setSelectedGroupForProposal] = useState<string>('');
  const [levelsForProposal, setLevelsForProposal] = useState<any[]>([]);
  const [groupsForProposal, setGroupsForProposal] = useState<any[]>([]);
  
  // Для редактирования существующих предложений
  const [editingProposal, setEditingProposal] = useState<{
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    courtName: string;
    tournamentId?: string | null;
    level?: string | null;
    groupId?: string | null;
    blockIds: string[];
  } | null>(null);
  const [showEditProposalModal, setShowEditProposalModal] = useState(false);
  const [editProposalTournament, setEditProposalTournament] = useState<string>('');
  const [editProposalLevel, setEditProposalLevel] = useState<string>('');
  const [editProposalGroup, setEditProposalGroup] = useState<string>('');
  const [editProposalLevels, setEditProposalLevels] = useState<any[]>([]);
  const [editProposalGroups, setEditProposalGroups] = useState<any[]>([]);
  
  // Для управления ответами игроков
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [proposalResponses, setProposalResponses] = useState<Array<{
    id: string;
    playerId: string;
    player: { id: string; name: string };
    proposalId: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    groups?: Array<{ id: string; name: string; level: string }>;
    lastMatchDate?: string | null;
    lastMatchOpponent?: string | null;
    lastMatchTournament?: string | null;
    nextMatchDates?: string[];
    nextMatchDateKeys?: string[];
    nextMatches?: Array<{
      date: string;
      dateKey?: string | null;
      opponent?: string | null;
      tournamentName?: string | null;
    }>;
  }>>([]);
  
  // Отслеживание просмотренных ответов (хранится на сервере)
  const [viewedResponses, setViewedResponses] = useState<Set<string>>(new Set());
  
  // Загружаем просмотренные ответы с сервера
  const fetchViewedResponses = async () => {
    try {
      const response = await apiGet<{ responseIds: string[] }>('/public-proposal-responses/viewed-responses');
      setViewedResponses(new Set(response.responseIds || []));
    } catch (error) {
      console.error('Ошибка загрузки просмотренных ответов:', error);
      // В случае ошибки просто используем пустой Set
      setViewedResponses(new Set());
    }
  };
  
  // Загружаем просмотренные ответы при монтировании компонента
  useEffect(() => {
    fetchViewedResponses();
  }, []);
  const [selectedPlayerForResponse, setSelectedPlayerForResponse] = useState<string>('');
  const [selectedResponseStatus, setSelectedResponseStatus] = useState<string>('READY');
  const [filteredPlayersForResponse, setFilteredPlayersForResponse] = useState<any[]>([]);
  const [loadingFilteredPlayers, setLoadingFilteredPlayers] = useState(false);
  
  // Для отображения игроков группы
  const [showGroupPlayersModal, setShowGroupPlayersModal] = useState(false);
  const [selectedGroupForPlayers, setSelectedGroupForPlayers] = useState<{ id: string; name: string; level: string } | null>(null);
  const [clickedPlayerId, setClickedPlayerId] = useState<string | null>(null);
  const [clickedPlayerName, setClickedPlayerName] = useState<string | null>(null);
  const [groupPlayersTournamentId, setGroupPlayersTournamentId] = useState<string | null>(null);
  const [groupPlayersLevel, setGroupPlayersLevel] = useState<string | null>(null);
  const [groupPlayersProposalId, setGroupPlayersProposalId] = useState<string | null>(null);
  const [groupPlayers, setGroupPlayers] = useState<Array<{ 
    id: string; 
    name: string; 
    matchStatus?: 'played' | 'scheduled' | 'none';
    matchDate?: string;
    matchDateKey?: string | null;
    matchId?: string;
    hasResponse?: boolean;
    responseStatus?: string;
    taskId?: string;
    taskStatus?: string;
  }>>([]);
  const [loadingGroupPlayers, setLoadingGroupPlayers] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState<string | null>(null);
  const [creatingTask, setCreatingTask] = useState<string | null>(null);
  const [showAvailabilityStatusModal, setShowAvailabilityStatusModal] = useState(false);
  const [availabilityStatusTarget, setAvailabilityStatusTarget] = useState<{
    proposalId: string;
    playerId: string;
    playerName: string;
    currentStatus?: string;
  } | null>(null);
  const [updatingAvailabilityStatus, setUpdatingAvailabilityStatus] = useState(false);
  const [showAvailabilityInviteModal, setShowAvailabilityInviteModal] = useState(false);
  const [availabilityInviteTarget, setAvailabilityInviteTarget] = useState<{
    proposalId: string;
    playerId: string;
    playerName: string;
  } | null>(null);
  const [sendingAvailabilityInvite, setSendingAvailabilityInvite] = useState(false);
  const [showReactionViewedModal, setShowReactionViewedModal] = useState(false);
  const [reactionViewedTarget, setReactionViewedTarget] = useState<{
    responseId: string;
    playerName: string;
    viewed: boolean;
  } | null>(null);
  const [updatingReactionViewed, setUpdatingReactionViewed] = useState(false);
  
  // Для копирования ответов между предложениями
  const [showCopyResponsesModal, setShowCopyResponsesModal] = useState(false);
  const [sourceProposalForCopy, setSourceProposalForCopy] = useState<typeof publicProposals[0] | null>(null);
  const [selectedTargetProposalId, setSelectedTargetProposalId] = useState<string>('');
  const [copyingResponses, setCopyingResponses] = useState(false);
  
  // Для отправки уведомлений о слотах
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationProposal, setNotificationProposal] = useState<typeof publicProposals[0] | null>(null);
  const [allPlayers, setAllPlayers] = useState<Array<{ id: string; name: string; telegramChatId?: string | null }>>([]);
  const [selectedPlayersForNotification, setSelectedPlayersForNotification] = useState<Set<string>>(new Set());
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  
  // Табы для выбора получателей (Игроки / Группы)
  const [notificationTab, setNotificationTab] = useState<'players' | 'groups'>('groups');
  
  // Для групп и топиков
  interface GroupWithTopics {
    chatId: string;
    title: string | null;
    username: string | null;
    topics: Array<{
      id: string;
      threadId: string;
      name: string | null;
    }>;
  }
  const [allGroups, setAllGroups] = useState<GroupWithTopics[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupsAndTopics, setSelectedGroupsAndTopics] = useState<Set<string>>(() => {
    // Загружаем сохраненные группы/топики из localStorage
    try {
      const saved = localStorage.getItem('notificationSelectedGroupsAndTopics');
      if (saved) {
        const array = JSON.parse(saved);
        return new Set(array);
      }
    } catch (e) {
      console.error('Ошибка загрузки сохраненных групп/топиков:', e);
    }
    return new Set();
  }); // Формат: "chatId" или "chatId:threadId"
  
  // Для пакетной отправки уведомлений
  const [showBatchNotificationModal, setShowBatchNotificationModal] = useState(false);
  const [selectedProposalsForBatch, setSelectedProposalsForBatch] = useState<Set<string>>(new Set());
  const [batchAllPlayers, setBatchAllPlayers] = useState<Array<{ id: string; name: string; telegramChatId?: string | null }>>([]);
  const [selectedPlayersForBatch, setSelectedPlayersForBatch] = useState<Set<string>>(new Set());
  const [loadingBatchPlayers, setLoadingBatchPlayers] = useState(false);
  const [sendingBatchNotifications, setSendingBatchNotifications] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState<Record<string, number>>({});
  const [batchNotificationTab, setBatchNotificationTab] = useState<'groups' | 'players'>(() => {
    try {
      const saved = localStorage.getItem('batchNotificationTab');
      if (saved === 'groups' || saved === 'players') return saved;
    } catch {}
    return 'groups';
  });
  
  // Для задач подбора соперников
  const [opponentMatchingTasks, setOpponentMatchingTasks] = useState<Array<{
    id: string;
    proposalId: string;
    activePlayerId: string;
    activePlayer: { id: string; name: string };
    opponentPlayerId: string;
    opponentPlayer: { id: string; name: string };
    tournamentId: string;
    tournament: { id: string; name: string };
    groupId: string;
    group: { id: string; name: string; level: string };
    proposalDate: string;
    proposalStartTime: string;
    proposalEndTime: string;
    courtId?: string | null;
    court?: { id: string; name: string } | null;
    priority: number;
    status: string;
    matchProposalId?: string | null;
    reason?: string | null;
    gamesPlayedActive: number;
    gamesPlayedOpponent: number;
    createdAt: string;
    updatedAt: string;
    completedAt?: string | null;
    notificationSentToPlayer1?: boolean;
    notificationSentToPlayer2?: boolean;
    player1NotificationResponse?: string | null;
    player2NotificationResponse?: string | null;
    clubReservationResponse?: string | null;
    courtReservationRequestSent?: boolean;
  }>>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTaskTournament, setSelectedTaskTournament] = useState<string>('');
  const [selectedTaskGroup, setSelectedTaskGroup] = useState<string>('');
  const [selectedTaskStatus, setSelectedTaskStatus] = useState<string>('');
  const [selectedProposalForTasks, setSelectedProposalForTasks] = useState<string>('');
  const [selectedTaskPlayer, setSelectedTaskPlayer] = useState<string>('');
  const [taskFilterPlayers, setTaskFilterPlayers] = useState<any[]>([]);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [selectedTasksForBulkAction, setSelectedTasksForBulkAction] = useState<Set<string>>(new Set());
  const [showTaskHistoryModal, setShowTaskHistoryModal] = useState(false);
  const [selectedTaskForHistory, setSelectedTaskForHistory] = useState<string | null>(null);
  const [taskHistory, setTaskHistory] = useState<Array<{
    id: string;
    oldStatus: string | null;
    newStatus: string;
    comment: string | null;
    createdAt: string;
  }>>([]);
  
  // Для карточки задачи
  const [showTaskCardModal, setShowTaskCardModal] = useState(false);
  const [selectedTaskForCard, setSelectedTaskForCard] = useState<typeof opponentMatchingTasks[0] | null>(null);
  const [taskCardHistory, setTaskCardHistory] = useState<Array<{
    id: string;
    oldStatus: string | null;
    newStatus: string;
    comment: string | null;
    changedBy: string | null;
    createdAt: string;
  }>>([]);
  const [loadingTaskCard, setLoadingTaskCard] = useState(false);
  const [showTaskCancelModal, setShowTaskCancelModal] = useState(false);
  const [taskToCancel, setTaskToCancel] = useState<{ id: string; title: string } | null>(null);
  const [taskCancelReasonCode, setTaskCancelReasonCode] = useState<string>('OTHER');
  const [taskCancelComment, setTaskCancelComment] = useState<string>('');
  const [taskCancelSubmitting, setTaskCancelSubmitting] = useState(false);
  
  // Для предпросмотра задач перед созданием
  const [showTaskPreviewModal, setShowTaskPreviewModal] = useState(false);
  const [previewProposalId, setPreviewProposalId] = useState<string | null>(null);
  const [previewTasks, setPreviewTasks] = useState<Array<{
    activePlayerId: string;
    activePlayerName: string;
    opponentPlayerId: string;
    opponentPlayerName: string;
    groupId: string;
    groupName: string;
    groupLevel: string;
    priority: number;
    reason: string;
    gamesPlayedActive: number;
    gamesPlayedOpponent: number;
    wouldSkip: boolean;
    skipReason?: string;
    opponentReadyProposalInfo?: {
      courtName?: string;
      clubName?: string;
      startTime?: string;
      endTime?: string;
    };
  }>>([]);
  const [selectedPreviewTasks, setSelectedPreviewTasks] = useState<Set<string>>(new Set());
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creatingSelectedTasks, setCreatingSelectedTasks] = useState(false);

  // Получаем получасовые слоты времени с 07:00 до 23:00
  const timeSlots = Array.from({ length: 33 }, (_, i) => {
    const hour = 7 + Math.floor(i / 2);
    const minute = (i % 2) * 30;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  });

  // Генерируем даты в выбранном периоде
  const getPeriodDates = () => {
    const dates = [];
    const startDate = new Date(selectedDateFrom);
    const endDate = new Date(selectedDateTo);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  const periodDates = getPeriodDates();

  const groupedBatchProposals = useMemo(() => {
    const map = new Map<string, {
      key: string;
      date: string;
      startTime: string;
      endTime: string;
      clubName: string;
      tournamentName?: string | null;
      level?: string | null;
      groupName?: string | null;
      proposalIds: string[];
    }>();

    const sorted = [...publicProposals].sort((a, b) => {
      const dateCmp = String(a.date || '').localeCompare(String(b.date || ''));
      if (dateCmp !== 0) return dateCmp;
      const startCmp = String(a.startTime || '').localeCompare(String(b.startTime || ''));
      if (startCmp !== 0) return startCmp;
      return String(a.clubName || '').localeCompare(String(b.clubName || ''));
    });

    sorted.forEach((proposal) => {
      const key = [
        String(proposal.date || ''),
        String(proposal.startTime || ''),
        String(proposal.endTime || ''),
        String(proposal.clubId || proposal.clubName || ''),
        String(proposal.tournamentId || ''),
        String(proposal.level || ''),
        String(proposal.groupId || '')
      ].join('|');

      const existing = map.get(key);
      if (existing) {
        existing.proposalIds.push(proposal.id);
        return;
      }

      map.set(key, {
        key,
        date: proposal.date,
        startTime: proposal.startTime,
        endTime: proposal.endTime,
        clubName: proposal.clubName,
        tournamentName: proposal.tournamentName,
        level: proposal.level,
        groupName: proposal.groupName,
        proposalIds: [proposal.id]
      });
    });

    return Array.from(map.values());
  }, [publicProposals]);

  const selectedGroupedBatchProposals = useMemo(
    () => groupedBatchProposals.filter((proposalGroup) =>
      proposalGroup.proposalIds.every((id) => selectedProposalsForBatch.has(id))
    ),
    [groupedBatchProposals, selectedProposalsForBatch]
  );

  useEffect(() => {
    fetchClubs();
    fetchCourts();
    fetchTournaments();
    fetchPlayers();
    
    // Обработчик отпускания кнопки мыши на весь документ
    const handleDocumentMouseUp = () => handleMouseUp();
    document.addEventListener('mouseup', handleDocumentMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, []);

  // Гарантированный первый загрузочный вызов после монтирования
  useEffect(() => {
    if (!selectedDateFrom || !selectedDateTo) return;
    // Запуск загрузки, когда доступны даты и загружены корты (для фильтра по клубу)
    if (courts && courts.length >= 0) {
      fetchAvailabilities();
      fetchReservations();
    }
  }, [selectedDateFrom, selectedDateTo, selectedClubId, courts]);

  useEffect(() => {
    if (selectedDateFrom && selectedDateTo) {
      fetchAvailabilities();
      fetchReservations();
      if (activeTab === 'proposals') {
        fetchPublicProposals();
      }
      if (activeTab === 'player-availability') {
        fetchPlayerSlotAvailability();
      }
    }
  }, [selectedDateFrom, selectedDateTo, selectedClubId, activeTab]);

  // Загружаем предложения при переключении на вкладку
  useEffect(() => {
    if (activeTab === 'proposals') {
      fetchPublicProposals();
    }
    if (activeTab === 'opponent-matching') {
      fetchOpponentMatchingTasks();
    }
  }, [activeTab, selectedDateFrom, selectedDateTo]);

  useEffect(() => {
    const fetchAvailabilityGroups = async () => {
      if (!availabilityTournamentId) {
        setAvailabilityGroups([]);
        setAvailabilityGroupId('');
        return;
      }
      try {
        const groups = await apiGet<any[]>(`/tournament-groups/tournament/${availabilityTournamentId}`);
        const normalized = (Array.isArray(groups) ? groups : [])
          .filter((group: any) => group?.id && group?.name)
          .map((group: any) => ({
            id: group.id,
            name: group.name,
            level: group.level || ''
          }))
          .sort((a, b) => {
            const levelCompare = (a.level || '').localeCompare(b.level || '', 'ru');
            if (levelCompare !== 0) return levelCompare;
            return a.name.localeCompare(b.name, 'ru');
          });
        setAvailabilityGroups(normalized);
      } catch (error) {
        console.error('Ошибка загрузки групп для фильтра доступности:', error);
        setAvailabilityGroups([]);
      }
      setAvailabilityGroupId('');
      setAvailabilityPlayerName('');
    };

    fetchAvailabilityGroups();
  }, [availabilityTournamentId]);

  // Отключаем режимы при снятии выбора клуба
  useEffect(() => {
    if (!selectedClubId) {
      setAvailabilityMode(false);
      setReservationMode(false);
    }
  }, [selectedClubId]);

  // Загружаем игроков и группы при открытии модального окна уведомлений
  useEffect(() => {
    if (showNotificationModal && notificationProposal) {
      if (notificationTab === 'players') {
        fetchPlayersForNotification(notificationProposal);
      } else if (notificationTab === 'groups') {
        fetchGroupsForNotification();
        // Загружаем сохраненные группы/топики при открытии/переключении на вкладку "Группы"
        try {
          const saved = localStorage.getItem('notificationSelectedGroupsAndTopics');
          if (saved) {
            const array = JSON.parse(saved);
            setSelectedGroupsAndTopics(new Set(array));
          }
        } catch (e) {
          console.error('Ошибка загрузки сохраненных групп/топиков:', e);
        }
      }
    }
  }, [showNotificationModal, notificationProposal, notificationTab]);

  useEffect(() => {
    try {
      localStorage.setItem('notificationSelectedGroupsAndTopics', JSON.stringify(Array.from(selectedGroupsAndTopics)));
    } catch (e) {
      console.error('Ошибка сохранения групп/топиков:', e);
    }
  }, [selectedGroupsAndTopics]);

  useEffect(() => {
    try {
      localStorage.setItem('batchNotificationTab', batchNotificationTab);
    } catch {}
  }, [batchNotificationTab]);

  useEffect(() => {
    if (!showBatchNotificationModal) return;
    if (batchNotificationTab === 'groups') {
      fetchGroupsForNotification();
    }
  }, [showBatchNotificationModal, batchNotificationTab]);

  // Сохраняем фильтры при изменениях
  useEffect(() => {
    try {
      localStorage.setItem('courtReservationsFilters', JSON.stringify({
        activeTab,
        clubId: selectedClubId,
        dateFrom: selectedDateFrom,
        dateTo: selectedDateTo
      }));
    } catch {}
  }, [activeTab, selectedClubId, selectedDateFrom, selectedDateTo]);

  const handleResetFilters = () => {
    try { localStorage.removeItem('courtReservationsFilters'); } catch {}
    setActiveTab('calendar');
    setSelectedClubId('');
    const today = new Date().toISOString().split('T')[0];
    setSelectedDateFrom(today);
    setSelectedDateTo(today);
  };

  // Загружаем участников турнира при изменении выбранного турнира
  useEffect(() => {
    fetchTournamentPlayers(reservationForm.tournamentId);
    fetchUnreservedMatches(reservationForm.tournamentId, includeReservedMatchesInSelect);
  }, [reservationForm.tournamentId, includeReservedMatchesInSelect]);

  useEffect(() => {
    if (!reservationMode) {
      setShowGamesPicker(false);
      setDraggedMatchForReservation(null);
      setSelectedLevelForGamesPicker('');
    }
  }, [reservationMode]);

  useEffect(() => {
    setSelectedLevelForGamesPicker('');
  }, [selectedTournamentForGamesPicker]);

  useEffect(() => {
    const loadPlanMode = async () => {
      if (!selectedTournamentForGamesPicker) {
        setGamesPickerPlanMode(false);
        return;
      }
      try {
        const data = await apiGet<{ enabled?: boolean }>('/court-reservations/notification-planning-mode', {
          tournamentId: selectedTournamentForGamesPicker
        });
        setGamesPickerPlanMode(Boolean(data?.enabled));
      } catch (error) {
        console.error('Ошибка загрузки режима планирования уведомлений:', error);
        setGamesPickerPlanMode(false);
      }
    };
    loadPlanMode();
  }, [selectedTournamentForGamesPicker]);

  useEffect(() => {
    if (!showGamesPicker || !selectedTournamentForGamesPicker) {
      setGamesForPicker([]);
      return;
    }
    fetchGamesForPicker(selectedTournamentForGamesPicker);
  }, [showGamesPicker, selectedTournamentForGamesPicker, includeReservedMatchesInSelect]);

  useEffect(() => {
    if (!showGamesPicker || !selectedTournamentForGamesPicker) {
      setLevelsForGamesPicker([]);
      return;
    }
    fetchLevelsForGamesPicker(selectedTournamentForGamesPicker);
  }, [showGamesPicker, selectedTournamentForGamesPicker]);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (!showGamesPicker || !selectedTournamentForGamesPicker) return;
      fetchGamesForPicker(selectedTournamentForGamesPicker);
      fetchLevelsForGamesPicker(selectedTournamentForGamesPicker);
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [showGamesPicker, selectedTournamentForGamesPicker, includeReservedMatchesInSelect]);

  useEffect(() => {
    if (!showGamesPicker || !selectedTournamentForGamesPicker) return;

    const refreshInterval = window.setInterval(() => {
      fetchGamesForPicker(selectedTournamentForGamesPicker);
      fetchLevelsForGamesPicker(selectedTournamentForGamesPicker);
    }, 15000);

    return () => window.clearInterval(refreshInterval);
  }, [showGamesPicker, selectedTournamentForGamesPicker, includeReservedMatchesInSelect]);

  const fetchClubs = async () => {
    try {
      const data = await apiGet<Club[] | { clubs?: Club[] }>('/clubs');
      setClubs(Array.isArray(data) ? data : (data.clubs || []));
    } catch (error) {
      console.error('Ошибка загрузки клубов:', error);
    }
  };

  const fetchCourts = async () => {
    try {
      const data = await apiGet<{ items?: Court[] } | Court[]>('/courts');
      // API возвращает { items: courts }
      const courts = Array.isArray(data) ? data : (data.items || []);
      setCourts(Array.isArray(courts) ? courts : []);
    } catch (error) {
      console.error('Ошибка загрузки кортов:', error);
    }
  };

  // Загрузка турниров, уровней и групп для привязки предложений
  const fetchTournamentsForProposal = async () => {
    try {
      const data = await apiGet<{ tournaments?: Tournament[] } | Tournament[]>('/tournaments?limit=1000');
      if (Array.isArray(data)) {
        setTournaments(data);
      } else {
        setTournaments(Array.isArray(data.tournaments) ? data.tournaments : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки турниров:', error);
    }
  };

  const fetchLevelsForProposal = async () => {
    if (!selectedTournamentForProposal) {
      setLevelsForProposal([]);
      setGroupsForProposal([]);
      return;
    }
    try {
      const tournament = await apiGet<any>(`/tournaments/${selectedTournamentForProposal}`);
      const groupsData = await apiGet(`/tournament-groups/tournament/${selectedTournamentForProposal}`);
      
      // Получаем уникальные уровни из групп
      const levelMap = new Map<string, number>();
      groupsData.forEach((group: any) => {
        const count = levelMap.get(group.level) || 0;
        levelMap.set(group.level, count + 1);
      });
      
      const levelsList = Array.from(levelMap.entries()).map(([level, count]) => ({
        level,
        playerCount: count
      }));
      
      setLevelsForProposal(levelsList);
      
      // Если выбран уровень, загружаем группы
      if (selectedLevelForProposal) {
        const filteredGroups = groupsData.filter((g: any) => g.level === selectedLevelForProposal);
        setGroupsForProposal(filteredGroups);
      } else {
        setGroupsForProposal([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки уровней/групп:', error);
      setLevelsForProposal([]);
      setGroupsForProposal([]);
    }
  };

  useEffect(() => {
    fetchTournamentsForProposal();
  }, []);

  useEffect(() => {
    fetchLevelsForProposal();
  }, [selectedTournamentForProposal, selectedLevelForProposal]);

  useEffect(() => {
    try {
      if (selectedTournamentForProposal) {
        localStorage.setItem('courtReservations.selectedTournamentForProposal', selectedTournamentForProposal);
      } else {
        localStorage.removeItem('courtReservations.selectedTournamentForProposal');
      }
    } catch {
      // ignore localStorage errors
    }
  }, [selectedTournamentForProposal]);

  // Функции для редактирования предложений
  const fetchLevelsForEditProposal = async () => {
    if (!editProposalTournament) {
      setEditProposalLevels([]);
      setEditProposalGroups([]);
      return;
    }
    try {
      const groupsData = await apiGet(`/tournament-groups/tournament/${editProposalTournament}`);
      
      // Получаем уникальные уровни из групп
      const levelMap = new Map<string, number>();
      groupsData.forEach((group: any) => {
        const count = levelMap.get(group.level) || 0;
        levelMap.set(group.level, count + 1);
      });
      
      const levelsList = Array.from(levelMap.entries()).map(([level, count]) => ({
        level,
        playerCount: count
      }));
      
      setEditProposalLevels(levelsList);
      
      // Если выбран уровень, загружаем группы
      if (editProposalLevel) {
        const filteredGroups = groupsData.filter((g: any) => g.level === editProposalLevel);
        setEditProposalGroups(filteredGroups);
      } else {
        setEditProposalGroups([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки уровней/групп для редактирования:', error);
      setEditProposalLevels([]);
      setEditProposalGroups([]);
    }
  };

  useEffect(() => {
    if (showEditProposalModal && editProposalTournament) {
      fetchLevelsForEditProposal();
    }
  }, [editProposalTournament, editProposalLevel, showEditProposalModal]);

  const handleSaveProposalEdit = async () => {
    if (!editingProposal) return;

    try {
      // Обновляем только первый блок (с publicProposalStartTime)
      const firstBlockId = editingProposal.blockIds[0];
      
      await apiPut(`/court-availability/${firstBlockId}`, {
        tournamentId: editProposalTournament || null,
        level: editProposalLevel || null,
        groupId: editProposalGroup || null
      });

      alert('Предложение обновлено');
      setShowEditProposalModal(false);
      setEditingProposal(null);
      await fetchPublicProposals();
    } catch (error: any) {
      console.error('Ошибка обновления предложения:', error);
      alert('Ошибка обновления: ' + (error.response?.data?.error || error.message));
    }
  };

  // Функции для работы с ответами игроков
  const fetchProposalResponses = async (proposalId: string) => {
    try {
      // API endpoint /proposal/:proposalId теперь возвращает ответы для всех объединенных слотов
      // и информацию о последнем и следующем матче для каждого игрока
      const responses = await apiGet(`/public-proposal-responses/proposal/${proposalId}`);
      
      // Форматируем даты матчей
      const responsesWithFormattedDates = responses.map((response: any) => ({
        ...response,
        lastMatchDate: response.lastMatchDate 
          ? new Date(response.lastMatchDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : null,
        lastMatchOpponent: response.lastMatchOpponent || null,
        lastMatchTournament: response.lastMatchTournament || null,
        nextMatchDates: response.nextMatchDates && Array.isArray(response.nextMatchDates)
          ? response.nextMatchDates.map((dateStr: string) => 
              new Date(dateStr).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
            )
          : [],
        nextMatchDateKeys: response.nextMatchDates && Array.isArray(response.nextMatchDates)
          ? response.nextMatchDates
              .map((dateStr: string) => toDateKey(dateStr))
              .filter((value: string | null): value is string => Boolean(value))
          : [],
        nextMatches: response.nextMatches && Array.isArray(response.nextMatches)
          ? response.nextMatches.map((item: any) => ({
              dateKey: toDateKey(item?.date),
              date: item?.date
                ? new Date(item.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '',
              opponent: item?.opponent || null,
              tournamentName: item?.tournamentName || null
            }))
          : [],
        isUnviewed: !viewedResponses.has(response.id) // Флаг непросмотренного ответа (используем только responseId)
      }));
      
      setProposalResponses(responsesWithFormattedDates);
      
      // Отмечаем все ответы как просмотренные на сервере при открытии модалки
      try {
        const responseIds = responses.map((r: any) => r.id);
        if (responseIds.length > 0) {
          await apiPost('/public-proposal-responses/mark-as-viewed', { responseIds });
          // Обновляем локальное состояние
          setViewedResponses(prev => {
            const newViewedResponses = new Set(prev);
            responseIds.forEach((id: string) => {
              newViewedResponses.add(id);
            });
            return newViewedResponses;
          });
          // Обновляем список просмотренных ответов с сервера для гарантии актуальности
          await fetchViewedResponses();
        }
      } catch (error) {
        console.error('Ошибка отметки ответов как просмотренных:', error);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки ответов:', error);
      setProposalResponses([]);
    }
  };

  // Загрузить игроков с учетом фильтрации по турниру/уровню/группе
  const fetchFilteredPlayersForResponse = async (proposal: typeof editingProposal) => {
    if (!proposal) {
      const sortedPlayers = [...players].sort((a, b) => a.name.localeCompare(b.name));
      setFilteredPlayersForResponse(sortedPlayers);
      return;
    }

    try {
      setLoadingFilteredPlayers(true);

      // Если нет привязки к турниру - показываем всех игроков
      if (!proposal.tournamentId) {
        const sortedPlayers = [...players].sort((a, b) => a.name.localeCompare(b.name));
        setFilteredPlayersForResponse(sortedPlayers);
        return;
      }

      // Если есть привязка к группе - загружаем игроков группы
      if (proposal.groupId) {
        const groups = await apiGet(`/tournament-groups/tournament/${proposal.tournamentId}`);
        const targetGroup = groups.find((g: any) => g.id === proposal.groupId);
        
        if (targetGroup && targetGroup.memberships) {
          const groupPlayers = targetGroup.memberships.map((m: any) => m.player).filter(Boolean);
          // Удаляем дубликаты по ID
          const uniquePlayers = Array.from(new Map(groupPlayers.map((p: any) => [p.id, p])).values());
          const sortedPlayers = uniquePlayers.sort((a: any, b: any) => a.name.localeCompare(b.name));
          setFilteredPlayersForResponse(sortedPlayers);
          return;
        }
      }

      // Если есть привязка к уровню - загружаем игроков уровня
      if (proposal.level) {
        const memberships = await apiGet(`/tournaments/${proposal.tournamentId}/players?level=${proposal.level}`);
        const levelPlayers = memberships.map((m: any) => m.player).filter(Boolean);
        // Удаляем дубликаты по ID
        const uniquePlayers = Array.from(new Map(levelPlayers.map((p: any) => [p.id, p])).values());
        const sortedPlayers = uniquePlayers.sort((a: any, b: any) => a.name.localeCompare(b.name));
        setFilteredPlayersForResponse(sortedPlayers);
        return;
      }

      // Если только турнир - загружаем всех игроков турнира
      const memberships = await apiGet(`/tournaments/${proposal.tournamentId}/players`);
      const tournamentPlayers = memberships.map((m: any) => m.player).filter(Boolean);
      // Удаляем дубликаты по ID
      const uniquePlayers = Array.from(new Map(tournamentPlayers.map((p: any) => [p.id, p])).values());
      const sortedPlayers = uniquePlayers.sort((a: any, b: any) => a.name.localeCompare(b.name));
      setFilteredPlayersForResponse(sortedPlayers);
    } catch (error: any) {
      console.error('Ошибка загрузки отфильтрованных игроков:', error);
      // В случае ошибки показываем всех игроков, отсортированных
      const sortedPlayers = [...players].sort((a, b) => a.name.localeCompare(b.name));
      setFilteredPlayersForResponse(sortedPlayers);
    } finally {
      setLoadingFilteredPlayers(false);
    }
  };

  const handleAddResponse = async () => {
    if (!editingProposal || !selectedPlayerForResponse) {
      alert('Выберите игрока');
      return;
    }

    try {
      await apiPost('/public-proposal-responses', {
        playerId: selectedPlayerForResponse,
        proposalId: editingProposal.id,
        status: selectedResponseStatus
      });

      await fetchProposalResponses(editingProposal.id);
      setSelectedPlayerForResponse('');
      setSelectedResponseStatus('READY');
      
      // Обновляем счетчик ответов в списке предложений (новый ответ считается непросмотренным)
      await fetchPublicProposals();
    } catch (error: any) {
      console.error('Ошибка добавления ответа:', error);
      alert('Ошибка добавления ответа: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateResponse = async (responseId: string, newStatus: string) => {
    try {
      const response = proposalResponses.find(r => r.id === responseId);
      if (!response) return;

      await apiPost('/public-proposal-responses', {
        playerId: response.playerId,
        proposalId: response.proposalId,
        status: newStatus
      });

      await fetchProposalResponses(response.proposalId);
      
      // Количество ответов не меняется при обновлении, обновлять счетчик не нужно
    } catch (error: any) {
      console.error('Ошибка обновления ответа:', error);
      alert('Ошибка обновления ответа: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateAvailabilityStatus = async (newStatus: string) => {
    if (!availabilityStatusTarget) return;
    try {
      setUpdatingAvailabilityStatus(true);
      await apiPost('/public-proposal-responses', {
        playerId: availabilityStatusTarget.playerId,
        proposalId: availabilityStatusTarget.proposalId,
        status: newStatus
      });
      await fetchPlayerSlotAvailability();
      setShowAvailabilityStatusModal(false);
      setAvailabilityStatusTarget(null);
    } catch (error: any) {
      console.error('Ошибка обновления статуса доступности:', error);
      alert('Ошибка обновления статуса: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdatingAvailabilityStatus(false);
    }
  };

  const handleSendAvailabilityInvite = async () => {
    if (!availabilityInviteTarget) return;
    try {
      setSendingAvailabilityInvite(true);
      const result = await apiPost('/public-proposal-responses/send-notifications', {
        proposalId: availabilityInviteTarget.proposalId,
        playerIds: [availabilityInviteTarget.playerId],
        isInvitation: true
      });
      alert(result.message || 'Приглашение отправлено');
      await fetchPlayerSlotAvailability();
      setShowAvailabilityInviteModal(false);
      setAvailabilityInviteTarget(null);
    } catch (error: any) {
      console.error('Ошибка отправки персонального приглашения:', error);
      alert('Ошибка отправки приглашения: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingAvailabilityInvite(false);
    }
  };

  const handleSetReactionViewed = async () => {
    if (!reactionViewedTarget) return;
    try {
      setUpdatingReactionViewed(true);
      await apiPost('/public-proposal-responses/set-viewed', {
        responseId: reactionViewedTarget.responseId,
        viewed: reactionViewedTarget.viewed
      });
      await fetchPlayerSlotAvailability();
      await fetchPublicProposals();
      setShowReactionViewedModal(false);
      setReactionViewedTarget(null);
    } catch (error: any) {
      console.error('Ошибка изменения признака просмотра реакции:', error);
      alert('Ошибка изменения признака просмотра: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdatingReactionViewed(false);
    }
  };

  const availabilityStatusOptions: Array<{ value: string; label: string; icon: string }> = [
    { value: 'READY', label: 'Доступен', icon: '✅' },
    { value: 'THINKING', label: 'Думает', icon: '🤔' },
    { value: 'REJECTED', label: 'Отказ', icon: '❌' },
    { value: 'SENT', label: 'Приглашение отправлено', icon: '📨' },
    { value: 'IGNORED', label: 'Нет ответа', icon: '⏺' }
  ];

  const handleDeleteResponse = async (responseId: string) => {
    if (!confirm('Удалить ответ игрока?')) return;

    try {
      await apiDelete(`/public-proposal-responses/${responseId}`);
      const response = proposalResponses.find(r => r.id === responseId);
      if (response && editingProposal) {
        // Удаляем из просмотренных ответов (на сервере это произойдет автоматически при удалении ответа)
        const newViewedResponses = new Set(viewedResponses);
        newViewedResponses.delete(responseId);
        setViewedResponses(newViewedResponses);
        
        await fetchProposalResponses(editingProposal.id);
        
        // Обновляем счетчик ответов в списке предложений
        await fetchPublicProposals();
      }
    } catch (error: any) {
      console.error('Ошибка удаления ответа:', error);
      alert('Ошибка удаления ответа: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCopyResponses = async () => {
    if (!sourceProposalForCopy || !selectedTargetProposalId) {
      alert('Выберите целевое предложение');
      return;
    }

    if (sourceProposalForCopy.id === selectedTargetProposalId) {
      alert('Нельзя копировать ответы на то же предложение');
      return;
    }

    try {
      setCopyingResponses(true);
      const result = await apiPost('/public-proposal-responses/copy', {
        sourceProposalId: sourceProposalForCopy.id,
        targetProposalId: selectedTargetProposalId
      });

      alert(result.message || `Скопировано ${result.copied} ответ(ов)`);
      
      // Обновляем список предложений, чтобы обновить счетчики ответов
      await fetchPublicProposals();
      
      // Закрываем модальное окно
      setShowCopyResponsesModal(false);
      setSourceProposalForCopy(null);
      setSelectedTargetProposalId('');
    } catch (error: any) {
      console.error('Ошибка копирования ответов:', error);
      alert('Ошибка копирования ответов: ' + (error.response?.data?.error || error.message));
    } finally {
      setCopyingResponses(false);
    }
  };

  // Функции для работы с задачами подбора соперников
  const fetchOpponentMatchingTasks = async () => {
    try {
      setLoadingTasks(true);
      const params: Record<string, string> = {};
      if (selectedTaskTournament) params.tournamentId = selectedTaskTournament;
      if (selectedTaskGroup) params.groupId = selectedTaskGroup;
      if (selectedTaskStatus) params.status = selectedTaskStatus;
      if (selectedProposalForTasks) params.proposalId = selectedProposalForTasks;
      if (selectedTaskPlayer) params.playerId = selectedTaskPlayer;
      // Добавляем фильтрацию по дате
      if (selectedDateFrom) params.dateFrom = selectedDateFrom;
      if (selectedDateTo) params.dateTo = selectedDateTo;

      const tasks = await apiGet<typeof opponentMatchingTasks>('/opponent-matching-tasks', params);
      setOpponentMatchingTasks(Array.isArray(tasks) ? tasks : []);
    } catch (error: any) {
      console.error('Ошибка загрузки задач:', error);
      alert('Ошибка загрузки задач: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleCreateTasks = async (proposalId: string) => {
    try {
      setCreatingTasks(true);
      const result = await apiPost('/opponent-matching-tasks/create', { proposalId });
      alert(result.message || `Создано ${result.created} задач`);
      await fetchOpponentMatchingTasks();
    } catch (error: any) {
      console.error('Ошибка создания задач:', error);
      alert('Ошибка создания задач: ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingTasks(false);
    }
  };

  const TASK_CANCEL_REASONS: Array<{ code: string; label: string }> = [
    { code: 'PLAYER_UNAVAILABLE', label: 'Игрок не может сыграть' },
    { code: 'OPPONENT_UNAVAILABLE', label: 'Соперник не может сыграть' },
    { code: 'BOTH_PLAYERS_UNAVAILABLE_SAME_DAY', label: 'Оба игрока не могут играть в этот день' },
    { code: 'ACTIVE_PLAYS_THIS_DAY', label: 'Активный игрок играет в этот день' },
    { code: 'OPPONENT_PLAYS_THIS_DAY', label: 'Соперник играет в этот день' },
    { code: 'CLUB_DECLINED', label: 'Клуб отклонил резерв' },
    { code: 'COURT_UNAVAILABLE', label: 'Корт недоступен' },
    { code: 'WEATHER_CONDITIONS', label: 'Погодные условия' },
    { code: 'MATCH_ASSIGNMENT_ERROR', label: 'Ошибка назначения матча' },
    { code: 'MATCH_WILL_BE_PLAYED_IN_ANOTHER_SLOT', label: 'Игра состоится в другом слоте' },
    { code: 'OTHER', label: 'Другая типовая причина' }
  ];

  const closeTaskCancelModal = () => {
    setShowTaskCancelModal(false);
    setTaskToCancel(null);
    setTaskCancelReasonCode('OTHER');
    setTaskCancelComment('');
    setTaskCancelSubmitting(false);
  };

  const openTaskCancelModal = (task: { id: string; activePlayer?: { name?: string }; opponentPlayer?: { name?: string } }) => {
    const activeName = String(task.activePlayer?.name || '').trim();
    const opponentName = String(task.opponentPlayer?.name || '').trim();
    const title = [activeName, opponentName].filter(Boolean).join(' vs ') || task.id;
    setTaskToCancel({ id: task.id, title });
    setTaskCancelReasonCode('OTHER');
    setTaskCancelComment('');
    setShowTaskCancelModal(true);
  };

  const handleUpdateTaskStatus = async (
    taskId: string,
    newStatus: string,
    comment?: string,
    cancelReasonCode?: string
  ): Promise<boolean> => {
    try {
      await apiPut(`/opponent-matching-tasks/${taskId}`, {
        status: newStatus,
        comment,
        ...(cancelReasonCode ? { cancelReasonCode } : {})
      });
      await fetchOpponentMatchingTasks();
      return true;
    } catch (error: any) {
      console.error('Ошибка обновления статуса задачи:', error);
      alert('Ошибка обновления статуса: ' + (error.response?.data?.error || error.message));
      return false;
    }
  };

  const handleConfirmTaskCancellation = async () => {
    if (!taskToCancel) return;
    setTaskCancelSubmitting(true);
    const ok = await handleUpdateTaskStatus(
      taskToCancel.id,
      'CANCELLED',
      taskCancelComment.trim() || undefined,
      taskCancelReasonCode
    );
    if (ok) {
      closeTaskCancelModal();
      return;
    }
    setTaskCancelSubmitting(false);
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      if (!confirm('Вы уверены, что хотите удалить эту задачу? Это действие нельзя отменить.')) {
        return;
      }
      await apiDelete(`/opponent-matching-tasks/${taskId}`);
      alert('Задача успешно удалена');
      await fetchOpponentMatchingTasks();
    } catch (error: any) {
      console.error('Ошибка удаления задачи:', error);
      alert('Ошибка удаления задачи: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCreateMatchFromTask = async (taskId: string) => {
    try {
      if (!confirm('Создать/обновить матч из этой задачи?')) {
        return;
      }
      const result = await apiPost(`/opponent-matching-tasks/${taskId}/create-match`);
      alert(result.message || 'Матч успешно создан/обновлен');
      fetchOpponentMatchingTasks();
    } catch (error: any) {
      console.error('Ошибка создания матча:', error);
      alert(error.response?.data?.error || 'Ошибка создания матча');
    }
  };

  const handleCreateMatchProposalFromTask = async (taskId: string) => {
    try {
      const result = await apiPost(`/opponent-matching-tasks/${taskId}/create-proposal`);
      alert('Предложение матча создано успешно');
      await fetchOpponentMatchingTasks();
    } catch (error: any) {
      console.error('Ошибка создания предложения матча:', error);
      alert('Ошибка создания предложения: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleBulkCreateProposals = async () => {
    if (selectedTasksForBulkAction.size === 0) {
      alert('Выберите задачи для создания предложений');
      return;
    }

    if (!confirm(`Создать предложения матчей для ${selectedTasksForBulkAction.size} задач?`)) {
      return;
    }

    try {
      const result = await apiPost('/opponent-matching-tasks/bulk-create-proposals', {
        taskIds: Array.from(selectedTasksForBulkAction)
      });
      alert(result.message || `Создано ${result.created} предложений`);
      setSelectedTasksForBulkAction(new Set());
      await fetchOpponentMatchingTasks();
    } catch (error: any) {
      console.error('Ошибка массового создания предложений:', error);
      alert('Ошибка создания предложений: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleViewTaskHistory = async (taskId: string) => {
    try {
      const task = await apiGet(`/opponent-matching-tasks/${taskId}`);
      setTaskHistory(Array.isArray(task.statusHistory) ? task.statusHistory : []);
      setSelectedTaskForHistory(taskId);
      setShowTaskHistoryModal(true);
    } catch (error: any) {
      console.error('Ошибка загрузки истории:', error);
      alert('Ошибка загрузки истории: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOpenTaskCard = async (taskId: string) => {
    try {
      setLoadingTaskCard(true);
      const task = opponentMatchingTasks.find(t => t.id === taskId);
      if (!task) {
        alert('Задача не найдена');
        return;
      }

      // Загружаем полную информацию о задаче с историей
      const fullTask = await apiGet(`/opponent-matching-tasks/${taskId}`);
      
      // Объединяем данные из локального состояния с данными с сервера
      setSelectedTaskForCard({
        ...task,
        player1NotificationResponse: fullTask.player1NotificationResponse || null,
        player2NotificationResponse: fullTask.player2NotificationResponse || null,
        clubReservationResponse: fullTask.clubReservationResponse || null
      });
      setTaskCardHistory(Array.isArray(fullTask.statusHistory) ? fullTask.statusHistory : []);
      setShowTaskCardModal(true);
    } catch (error: any) {
      console.error('Ошибка загрузки карточки задачи:', error);
      alert('Ошибка загрузки карточки задачи: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingTaskCard(false);
    }
  };

  const handleSendMatchNotification = async (taskId: string, playerNumber: 1 | 2) => {
    try {
      const task = opponentMatchingTasks.find(t => t.id === taskId);
      if (!task) {
        alert('Задача не найдена');
        return;
      }

      // Формируем текст для копирования в буфер обмена
      const matchDate = new Date(task.proposalDate);
      
      // Названия дней недели на румынском
      const romanianDays = ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'];
      const dayOfWeek = romanianDays[matchDate.getDay()];
      
      // Форматируем дату DD.MM.YYYY
      const day = String(matchDate.getDate()).padStart(2, '0');
      const month = String(matchDate.getMonth() + 1).padStart(2, '0');
      const year = matchDate.getFullYear();
      const formattedDate = `${day}.${month}.${year}`;
      
      // Форматируем время (убираем секунды, если есть)
      const startTime = task.proposalStartTime.length === 5 ? task.proposalStartTime : task.proposalStartTime.substring(0, 5);
      const endTime = task.proposalEndTime.length === 5 ? task.proposalEndTime : task.proposalEndTime.substring(0, 5);
      
      // Номер корта
      const courtNumber = task.court?.name || '?';
      
      // Уровень, группа и игроки
      const groupName = task.group.name;
      const levelName = task.group.level;
      const groupLabel = [levelName, groupName].filter(Boolean).join('•');
      const player1Name = task.activePlayer.name;
      const player2Name = task.opponentPlayer.name;
      
      // Формируем текст для копирования
      const textToCopy = `${formattedDate} ${dayOfWeek}\n${startTime}-${endTime} cort ${courtNumber}\n(${groupLabel}) ${player1Name} - ${player2Name}`;
      
      // Копируем в буфер обмена
      try {
        await navigator.clipboard.writeText(textToCopy);
        alert(`Текст скопирован в буфер обмена:\n\n${textToCopy}`);
      } catch (clipboardError) {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          alert(`Текст скопирован в буфер обмена:\n\n${textToCopy}`);
        } catch (err) {
          alert(`Не удалось скопировать в буфер обмена. Текст:\n\n${textToCopy}`);
        }
        document.body.removeChild(textArea);
      }
      
      // Отправляем запрос на сервер для логирования
      try {
        const result = await apiPost(`/opponent-matching-tasks/${taskId}/send-notification`, {
          playerNumber
        });
        console.log('Уведомление залогировано:', result);
        
        // Получаем обновленную задачу с сервера для получения актуального статуса
        const updatedTask = await apiGet(`/opponent-matching-tasks/${taskId}`);
        
        // Обновляем задачу в локальном состоянии с новым статусом и флагами
        setOpponentMatchingTasks(prevTasks =>
          prevTasks.map(t =>
            t.id === taskId
              ? {
                  ...t,
                  status: updatedTask.status || t.status,
                  notificationSentToPlayer1: playerNumber === 1 ? true : (t.notificationSentToPlayer1 || updatedTask.notificationSentToPlayer1),
                  notificationSentToPlayer2: playerNumber === 2 ? true : (t.notificationSentToPlayer2 || updatedTask.notificationSentToPlayer2)
                }
              : t
          )
        );
        
        // Если карточка открыта, обновляем её тоже
        if (selectedTaskForCard && selectedTaskForCard.id === taskId) {
          setSelectedTaskForCard({
            ...selectedTaskForCard,
            status: updatedTask.status || selectedTaskForCard.status,
            notificationSentToPlayer1: playerNumber === 1 ? true : (selectedTaskForCard.notificationSentToPlayer1 || updatedTask.notificationSentToPlayer1),
            notificationSentToPlayer2: playerNumber === 2 ? true : (selectedTaskForCard.notificationSentToPlayer2 || updatedTask.notificationSentToPlayer2)
          });
          setTaskCardHistory(Array.isArray(updatedTask.statusHistory) ? updatedTask.statusHistory : []);
        }
      } catch (error: any) {
        console.error('Ошибка логирования уведомления:', error);
        alert('Ошибка: ' + (error.response?.data?.error || error.message));
      }
    } catch (error: any) {
      console.error('Ошибка отправки уведомления:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleReserveCourt = async (taskId: string) => {
    try {
      const task = opponentMatchingTasks.find(t => t.id === taskId);
      if (!task) {
        alert('Задача не найдена');
        return;
      }

      if (!confirm(`Отправить запрос на резерв корта для задачи?\n\nИгроки: ${task.activePlayer.name} vs ${task.opponentPlayer.name}\n${formatDate(task.proposalDate)} ${task.proposalStartTime} - ${task.proposalEndTime}`)) {
        return;
      }

      const result = await apiPost(`/opponent-matching-tasks/${taskId}/reserve-court`);
      alert(result.message || 'Запрос на резерв корта отправлен');
      
      // Получаем обновленную задачу с сервера для получения актуального статуса
      const updatedTask = await apiGet(`/opponent-matching-tasks/${taskId}`);
      
      // Обновляем локальное состояние для немедленного отображения с новым статусом
      setOpponentMatchingTasks(prevTasks =>
        prevTasks.map(t =>
          t.id === taskId
            ? {
                ...t,
                status: updatedTask.status || t.status,
                courtReservationRequestSent: true,
                clubReservationResponse: updatedTask.clubReservationResponse || t.clubReservationResponse
              }
            : t
        )
      );
      
      // Если карточка открыта, обновляем её тоже
      if (selectedTaskForCard && selectedTaskForCard.id === taskId) {
        setSelectedTaskForCard({
          ...selectedTaskForCard,
          status: updatedTask.status || selectedTaskForCard.status,
          player1NotificationResponse: updatedTask.player1NotificationResponse || selectedTaskForCard.player1NotificationResponse,
          player2NotificationResponse: updatedTask.player2NotificationResponse || selectedTaskForCard.player2NotificationResponse,
          clubReservationResponse: updatedTask.clubReservationResponse || selectedTaskForCard.clubReservationResponse,
          courtReservationRequestSent: true
        });
        setTaskCardHistory(Array.isArray(updatedTask.statusHistory) ? updatedTask.statusHistory : []);
      }
    } catch (error: any) {
      console.error('Ошибка отправки запроса на резерв корта:', error);
      alert('Ошибка отправки запроса: ' + (error.response?.data?.error || error.message));
    }
  };

  const getPlayer1NotificationIcon = (task: typeof opponentMatchingTasks[0]) => {
    if (!task.notificationSentToPlayer1) {
      return { icon: '📧1', title: 'Отправить уведомление о матче игроку №1' };
    }
    if (task.player1NotificationResponse === 'READY') {
      return { icon: '📧1+', title: 'Игрок №1 готов' };
    }
    if (task.player1NotificationResponse === 'REJECTED') {
      return { icon: '📧1-', title: 'Игрок №1 отказался' };
    }
    return { icon: '📧1?', title: 'Уведомление отправлено игроку №1 (ожидается ответ)' };
  };

  const getPlayer2NotificationIcon = (task: typeof opponentMatchingTasks[0]) => {
    if (!task.notificationSentToPlayer2) {
      return { icon: '📧2', title: 'Отправить уведомление о матче игроку №2' };
    }
    if (task.player2NotificationResponse === 'READY') {
      return { icon: '📧2+', title: 'Игрок №2 готов' };
    }
    if (task.player2NotificationResponse === 'REJECTED') {
      return { icon: '📧2-', title: 'Игрок №2 отказался' };
    }
    return { icon: '📧2?', title: 'Уведомление отправлено игроку №2 (ожидается ответ)' };
  };

  const getClubReservationIcon = (task: typeof opponentMatchingTasks[0]) => {
    if (!task.courtReservationRequestSent) {
      return { icon: '🏟️', title: 'Резерв корта' };
    }
    if (task.clubReservationResponse === 'CONFIRMED') {
      return { icon: '🏟️+', title: 'Резерв корта подтвержден клубом' };
    }
    if (task.clubReservationResponse === 'REJECTED') {
      return { icon: '🏟️-', title: 'Резерв корта отклонен клубом' };
    }
    return { icon: '🏟️?', title: 'Запрос на резерв корта отправлен (ожидается ответ клуба)' };
  };

  const getTaskStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      'PENDING': 'Ожидает',
      'REQUESTED': 'Запрошено',
      'IN_PROGRESS': 'В работе',
      'MATCH_PROPOSAL_CREATED': 'Матч',
      'MATCH_ASSIGNED': 'Матч назначен',
      'SKIPPED': 'Пропущено',
      'COMPLETED': 'Завершено',
      'CANCELLED': 'Отменено'
    };
    return labels[status] || status;
  };

  const getTaskStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'REQUESTED': 'bg-blue-100 text-blue-800',
      'IN_PROGRESS': 'bg-blue-100 text-blue-800',
      'MATCH_PROPOSAL_CREATED': 'bg-green-100 text-green-800',
      'MATCH_ASSIGNED': 'bg-purple-100 text-purple-800',
      'SKIPPED': 'bg-gray-100 text-gray-800',
      'COMPLETED': 'bg-green-200 text-green-900',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      'READY': 'Доступен',
      'REJECTED': 'Отказ',
      'IGNORED': 'Игнор',
      'SENT': 'Отправлено',
      'THINKING': 'Думает'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'READY': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'IGNORED': 'bg-gray-100 text-gray-800',
      'SENT': 'bg-blue-100 text-blue-800',
      'THINKING': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTournamentsForManualReservationSelect = (): any[] => {
    return [...tournaments].sort((a, b) => {
      const statusA = String(a?.status || '').trim().toUpperCase();
      const statusB = String(b?.status || '').trim().toUpperCase();
      const priorityA = TOURNAMENT_STATUS_PRIORITY[statusA] ?? Number.MAX_SAFE_INTEGER;
      const priorityB = TOURNAMENT_STATUS_PRIORITY[statusB] ?? Number.MAX_SAFE_INTEGER;
      if (priorityA !== priorityB) return priorityA - priorityB;
      const endTsA = a?.endDate ? new Date(a.endDate).getTime() : 0;
      const endTsB = b?.endDate ? new Date(b.endDate).getTime() : 0;
      if (endTsA !== endTsB) return endTsB - endTsA;
      return String(a?.name || '').localeCompare(String(b?.name || ''), 'ru');
    });
  };

  const getTournamentsForGamesPickerSelect = (): any[] => {
    return getTournamentsForManualReservationSelect().filter(isTournamentActiveForProposal);
  };

  const getGroupedTournamentsForManualReservation = (): Record<string, any[]> => {
    return getTournamentsForManualReservationSelect().reduce((acc: Record<string, any[]>, tournament: any) => {
      const status = String(tournament?.status || '').trim().toUpperCase() || 'OTHER';
      if (!acc[status]) acc[status] = [];
      acc[status].push(tournament);
      return acc;
    }, {});
  };

  const getOrderedManualReservationStatuses = (): string[] => {
    return Object.keys(getGroupedTournamentsForManualReservation()).sort((a, b) => {
      const pa = TOURNAMENT_STATUS_PRIORITY[a] ?? Number.MAX_SAFE_INTEGER;
      const pb = TOURNAMENT_STATUS_PRIORITY[b] ?? Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });
  };

  const isTournamentActiveForProposal = (tournament: any): boolean => {
    const status = String(tournament?.status || '').trim().toUpperCase();
    if (status !== 'ACTIVE') return false;
    if (typeof tournament?.isActive === 'boolean' && !tournament.isActive) return false;
    return true;
  };

  const getActiveTournamentsForProposalSelect = (): any[] => {
    return tournaments
      .filter(isTournamentActiveForProposal)
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ru'));
  };

  const getFallbackLevelsForSelectedTournament = (): string[] => {
    const tournament = tournaments.find((t) => t.id === selectedTournamentForGamesPicker);
    if (!tournament) return [];

    const groupLevels = Array.isArray(tournament.groups)
      ? tournament.groups
          .map((group: any) => String(group?.level || '').trim())
          .filter(Boolean)
      : [];

    let bracketLevels: string[] = [];
    try {
      if (typeof tournament.levelBrackets === 'string') {
        const parsed = JSON.parse(tournament.levelBrackets);
        if (parsed && typeof parsed === 'object') {
          bracketLevels = Object.keys(parsed)
            .map((level) => String(level || '').trim())
            .filter((level) => level && !isSystemLevelBracketKey(level));
        }
      } else if (tournament.levelBrackets && typeof tournament.levelBrackets === 'object') {
        bracketLevels = Object.keys(tournament.levelBrackets)
          .map((level) => String(level || '').trim())
          .filter((level) => level && !isSystemLevelBracketKey(level));
      }
    } catch (error) {
      console.warn('Не удалось распарсить levelBrackets для подбора игр:', error);
    }

    return Array.from(new Set([...groupLevels, ...bracketLevels])).sort((a, b) => a.localeCompare(b, 'ru'));
  };

  const fetchLevelsForGamesPicker = async (tournamentId: string) => {
    try {
      const groupsData = await apiGet<any[]>(`/tournament-groups/tournament/${tournamentId}`);
      const levelsFromGroups = Array.isArray(groupsData)
        ? groupsData
            .map((group: any) => String(group?.level || '').trim())
            .filter(Boolean)
        : [];
      const levels = Array.from(new Set([...levelsFromGroups, ...getFallbackLevelsForSelectedTournament()]))
        .sort((a, b) => a.localeCompare(b, 'ru'));
      setLevelsForGamesPicker(levels);
    } catch (error) {
      console.error('Ошибка загрузки уровней для подбора игр:', error);
      setLevelsForGamesPicker(getFallbackLevelsForSelectedTournament());
    }
  };

  const getLevelsForGamesPickerSelect = (): string[] => {
    const levelsFromGames = Array.from(
      new Set(
        gamesForPicker
          .map((match) => (match.level || '').trim())
          .filter(Boolean)
      )
    );
    const levels = Array.from(new Set([...levelsFromGames, ...levelsForGamesPicker, ...getFallbackLevelsForSelectedTournament()]));
    return levels.sort((a, b) => a.localeCompare(b, 'ru'));
  };

  const getFilteredGamesForPicker = (): UnreservedMatchOption[] => {
    const selectedLevelNormalized = normalizeLevelKey(selectedLevelForGamesPicker);
    const visibleReservationIds = new Set(reservations.map((reservation) => reservation.id));
    const filtered = gamesForPicker.filter((match) => {
      const normalizedStatus = String(match.status || '').trim().toUpperCase();
      if (isCompletedLikeMatchStatus(normalizedStatus)) {
        const canShowCancelledWithHistory =
          includeReservedMatchesInSelect &&
          normalizedStatus === 'CANCELLED' &&
          Boolean(match.hadReservationHistory);
        if (!canShowCancelledWithHistory) {
          return false;
        }
      }
      if (match.reservationId && visibleReservationIds.has(match.reservationId)) {
        return false;
      }
      if (!selectedLevelNormalized) return true;
      const level = normalizeLevelKey(match.level);
      return level === selectedLevelNormalized;
    });

    return [...filtered].sort((a, b) => {
      const levelA = String(a.level || '').trim();
      const levelB = String(b.level || '').trim();
      const levelDiff = levelB.localeCompare(levelA, 'ru');
      if (levelDiff !== 0) return levelDiff;

      const stageA = extractPlayoffStageShort(a.roundName);
      const stageB = extractPlayoffStageShort(b.roundName);
      const stageDiff = getPlayoffStageSortOrder(stageA) - getPlayoffStageSortOrder(stageB);
      if (stageDiff !== 0) return stageDiff;

      const stageNumberA = Number(a.stageMatchNumber || 0);
      const stageNumberB = Number(b.stageMatchNumber || 0);
      if (stageNumberA !== stageNumberB) return stageNumberA - stageNumberB;

      const matchNumberA = Number(a.matchNumber || 0);
      const matchNumberB = Number(b.matchNumber || 0);
      if (matchNumberA !== matchNumberB) return matchNumberA - matchNumberB;

      return String(a.matchCode || '').localeCompare(String(b.matchCode || ''), 'ru');
    });
  };

  const fetchAvailabilities = async () => {
    try {
      setLoading(true);
      
      if (!selectedDateFrom || !selectedDateTo) {
        setAvailabilities([]);
        return;
      }
      
      // Оптимизация: используем один запрос с диапазоном дат вместо цикла
      const params = new URLSearchParams({
        startDate: selectedDateFrom,
        endDate: selectedDateTo
      });
      
      if (selectedClubId) {
        params.append('clubId', selectedClubId);
      }
      
      const data = await apiGet<CourtAvailability[]>(`/court-availability?${params.toString()}`);
      
      setAvailabilities(data);
    } catch (error) {
      console.error('Ошибка загрузки доступности:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    try {
      if (!selectedDateFrom || !selectedDateTo) {
        setReservations([]);
        return;
      }
      
      // Оптимизация: используем один запрос с диапазоном дат вместо цикла
      const params = new URLSearchParams({
        startDate: selectedDateFrom,
        endDate: selectedDateTo
      });
      
      const data = await apiGet<CourtReservation[]>(`/court-reservations?${params.toString()}`);
      
      // Фильтруем по клубу на клиенте (если не фильтруется на сервере)
      const filteredData = selectedClubId
        ? data.filter((r: CourtReservation) => {
            const court = courts.find(c => c.id === r.court.id);
            return court?.clubId === selectedClubId;
          })
        : data;
      
      setReservations(filteredData);
    } catch (error) {
      console.error('Ошибка загрузки резервирований:', error);
    }
  };

  const fetchTournaments = async () => {
    try {
      const data = await apiGet<{ tournaments?: any[]; items?: any[] } | any[]>('/tournaments?limit=100');
      // API возвращает { tournaments: [...], pagination: {...} }
      const tournamentsList = Array.isArray(data) ? data : (data.tournaments || data.items || []);
      
      // Загружаем группы для каждого турнира
      const tournamentsWithGroups = await Promise.all(
        tournamentsList.map(async (tournament: any) => {
          try {
            const groupsData = await apiGet(`/tournament-groups/tournament/${tournament.id}`);
            return {
              ...tournament,
              groups: Array.isArray(groupsData) ? groupsData : (groupsData.groups || [])
            };
          } catch (error) {
            console.error(`Ошибка загрузки групп для турнира ${tournament.id}:`, error);
            return { ...tournament, groups: [] };
          }
        })
      );
      
      setTournaments(tournamentsWithGroups);
    } catch (error) {
      console.error('Ошибка загрузки турниров:', error);
    }
  };

  const fetchPlayers = async () => {
    try {
      const data = await apiGet<{ players?: any[]; items?: any[] } | any[]>('/players?limit=100');
      // API возвращает { players: [...], pagination: {...} }
      const players = Array.isArray(data) ? data : (data.players || data.items || []);
      setPlayers(players);
    } catch (error) {
      console.error('Ошибка загрузки игроков:', error);
    }
  };

  // Загрузить всех игроков с Telegram аккаунтами для отправки уведомлений
  const fetchPlayersForNotification = async (proposal?: typeof publicProposals[0] | null) => {
    const proposalToUse = proposal || notificationProposal;
    if (!proposalToUse) return;
    
    try {
      setLoadingPlayers(true);
      
      let eligiblePlayerIds: string[] = [];
      
      // Если предложение привязано к турниру/уровню/группе, фильтруем игроков
      if (proposalToUse.tournamentId) {
        if (proposalToUse.groupId) {
          // Если привязано к группе, получаем игроков группы
          try {
            const group = await apiGet<any>(`/tournament-groups/${proposalToUse.groupId}`);
            if (group && group.memberships) {
              eligiblePlayerIds = group.memberships.map((m: any) => m.playerId || m.player?.id).filter(Boolean);
            }
          } catch (error) {
            console.error('Ошибка загрузки игроков группы:', error);
            // Если не удалось загрузить игроков группы, используем уровень
            if (proposalToUse.level) {
              const memberships = await apiGet<Array<{ playerId: string }>>(`/tournaments/${proposalToUse.tournamentId}/players?level=${proposalToUse.level}`);
              eligiblePlayerIds = memberships.map((m: any) => m.playerId || m.player?.id).filter(Boolean);
            }
          }
        } else if (proposalToUse.level) {
          // Если привязано к уровню, получаем игроков уровня
          const memberships = await apiGet<Array<{ playerId: string }>>(`/tournaments/${proposalToUse.tournamentId}/players?level=${proposalToUse.level}`);
          eligiblePlayerIds = memberships.map((m: any) => m.playerId || m.player?.id).filter(Boolean);
        } else {
          // Если привязано только к турниру, получаем всех участников турнира
          const memberships = await apiGet<Array<{ playerId: string }>>(`/tournaments/${proposalToUse.tournamentId}/players`);
          eligiblePlayerIds = memberships.map((m: any) => m.playerId || m.player?.id).filter(Boolean);
        }
      }
      
      // Загружаем всех игроков с Telegram аккаунтами
      let accounts: Array<{ playerId: string; chatId: string; isPrimary?: boolean; player: { id: string; name: string } }> = [];
      try {
        const accountsResponse = await apiGet<{ accounts: Array<{ playerId: string; chatId: string; isPrimary?: boolean; player: { id: string; name: string } }> }>('/players/telegram-accounts/all');
        accounts = accountsResponse?.accounts || [];
        
        if (!accounts || accounts.length === 0) {
          console.warn('Не найдено Telegram аккаунтов. Ответ API:', accountsResponse);
        }
      } catch (error) {
        console.error('Ошибка загрузки Telegram аккаунтов:', error);
        // Продолжаем работу, даже если не удалось загрузить аккаунты
      }
      
      console.log('fetchPlayersForNotification:', {
        proposalId: proposalToUse.id,
        tournamentId: proposalToUse.tournamentId,
        level: proposalToUse.level,
        groupId: proposalToUse.groupId,
        eligiblePlayerIdsCount: eligiblePlayerIds.length,
        eligiblePlayerIds: eligiblePlayerIds.slice(0, 5), // Первые 5 для отладки
        accountsCount: accounts.length
      });
      
      // Создаем мапу игроков с их Telegram аккаунтами
      const playersMap = new Map<string, { id: string; name: string; telegramChatId: string | null }>();
      
      accounts.forEach(account => {
        if (account.player) {
          // Если есть фильтр по турниру/уровню/группе, проверяем, что игрок входит в список
          // Но только если список не пустой (если пустой, значит фильтрация не удалась или нет зарегистрированных)
          if (eligiblePlayerIds.length > 0 && !eligiblePlayerIds.includes(account.player.id)) {
            return; // Пропускаем игрока, не зарегистрированного в турнире/уровне/группе
          }
          
          // Если фильтр пустой, но предложение привязано к турниру, все равно показываем игроков с Telegram
          // (возможно, фильтрация не удалась или нет зарегистрированных игроков)
          
          const existing = playersMap.get(account.player.id);
          if (!existing || account.isPrimary) {
            playersMap.set(account.player.id, {
              id: account.player.id,
              name: account.player.name,
              telegramChatId: account.chatId
            });
          }
        }
      });
      
      // Если предложение привязано к турниру, но eligiblePlayerIds пустой (нет зарегистрированных или ошибка),
      // все равно показываем всех игроков с Telegram аккаунтами
      if (proposalToUse.tournamentId && eligiblePlayerIds.length === 0) {
        console.warn('Предложение привязано к турниру, но список зарегистрированных игроков пуст. Показываем всех игроков с Telegram аккаунтами.');
      }
      
      // Если предложение не привязано к турниру, также загружаем игроков без Telegram аккаунтов
      // (для отображения, но они не смогут получить уведомление)
      if (!proposalToUse.tournamentId) {
        const allPlayersData = await apiGet<{ players?: any[]; items?: any[] } | any[]>('/players?limit=1000&isActive=true');
        const allPlayersList = Array.isArray(allPlayersData) ? allPlayersData : (allPlayersData.players || allPlayersData.items || []);
        
        allPlayersList.forEach((player: any) => {
          if (!playersMap.has(player.id)) {
            playersMap.set(player.id, {
              id: player.id,
              name: player.name,
              telegramChatId: null
            });
          }
        });
      } else {
        // Если привязано к турниру, загружаем только зарегистрированных игроков (даже без Telegram)
        // чтобы показать полный список участников
        // Но только если есть список зарегистрированных игроков
        if (eligiblePlayerIds.length > 0) {
          const registeredPlayersData = await apiGet<{ players?: any[]; items?: any[] } | any[]>('/players?limit=1000&isActive=true');
          const registeredPlayersList = Array.isArray(registeredPlayersData) ? registeredPlayersData : (registeredPlayersData.players || registeredPlayersData.items || []);
          
          registeredPlayersList.forEach((player: any) => {
            if (eligiblePlayerIds.includes(player.id) && !playersMap.has(player.id)) {
              playersMap.set(player.id, {
                id: player.id,
                name: player.name,
                telegramChatId: null
              });
            }
          });
        }
        // Если eligiblePlayerIds пустой, но предложение привязано к турниру,
        // мы уже добавили всех игроков с Telegram аккаунтами выше, так что ничего не делаем
      }
      
      const playersList = Array.from(playersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      console.log('fetchPlayersForNotification result:', {
        totalPlayers: playersList.length,
        playersWithTelegram: playersList.filter(p => p.telegramChatId).length
      });
      
      setAllPlayers(playersList);
      
      // Изначально все галочки сняты
      setSelectedPlayersForNotification(new Set());
    } catch (error) {
      console.error('Ошибка загрузки игроков для уведомлений:', error);
      alert('Ошибка загрузки списка игроков');
    } finally {
      setLoadingPlayers(false);
    }
  };

  // Загрузить группы и топики для отправки уведомлений
  const fetchGroupsForNotification = async () => {
    try {
      setLoadingGroups(true);
      
      // Получаем все группы
      const groups = await apiGet<Array<{
        chatId: string;
        title: string | null;
        username: string | null;
      }>>('/telegram-group-settings');
      
      // Для каждой группы получаем топики с задержкой между запросами
      // чтобы избежать превышения rate limit
      const groupsWithTopics: GroupWithTopics[] = [];
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        try {
          // Добавляем задержку между запросами (кроме первого)
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 200)); // 200ms задержка
          }
          
          const topicsResponse = await apiGet<{
            topics: Array<{
              id: string;
              threadId: string;
              name: string | null;
            }>;
            mainThreadUnreadCount: number;
          }>(`/telegram-group-settings/${group.chatId}/topics`);
          
          groupsWithTopics.push({
            ...group,
            topics: topicsResponse.topics || []
          });
        } catch (error) {
          console.error(`Ошибка загрузки топиков для группы ${group.chatId}:`, error);
          groupsWithTopics.push({
            ...group,
            topics: []
          });
        }
      }
      
      setAllGroups(groupsWithTopics);
    } catch (error) {
      console.error('Ошибка загрузки групп для уведомлений:', error);
      alert('Ошибка загрузки списка групп');
    } finally {
      setLoadingGroups(false);
    }
  };

  // Загрузить игроков для пакетной отправки уведомлений
  const fetchBatchPlayers = async () => {
    try {
      setLoadingBatchPlayers(true);
      
      // Получаем все выбранные предложения
      const selectedProposals = publicProposals.filter(p => selectedProposalsForBatch.has(p.id));
      
      if (selectedProposals.length === 0) {
        setBatchAllPlayers([]);
        return;
      }
      
      // Загружаем всех игроков с Telegram аккаунтами (без фильтрации по турниру для пакетной отправки)
      let accounts: Array<{ playerId: string; chatId: string; isPrimary?: boolean; player: { id: string; name: string } }> = [];
      try {
        const accountsResponse = await apiGet<{ accounts: Array<{ playerId: string; chatId: string; isPrimary?: boolean; player: { id: string; name: string } }> }>('/players/telegram-accounts/all');
        accounts = accountsResponse?.accounts || [];
      } catch (error) {
        console.error('Ошибка загрузки Telegram аккаунтов:', error);
      }
      
      // Создаем мапу игроков с их Telegram аккаунтами
      const playersMap = new Map<string, { id: string; name: string; telegramChatId: string | null }>();
      
      accounts.forEach(account => {
        if (account.player) {
          const existing = playersMap.get(account.player.id);
          if (!existing || account.isPrimary) {
            playersMap.set(account.player.id, {
              id: account.player.id,
              name: account.player.name,
              telegramChatId: account.chatId
            });
          }
        }
      });
      
      const playersList = Array.from(playersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
      setBatchAllPlayers(playersList);
      
      // По умолчанию выбираем всех игроков с Telegram аккаунтами
      const playersWithTelegram = playersList.filter(p => p.telegramChatId).map(p => p.id);
      setSelectedPlayersForBatch(new Set(playersWithTelegram));
    } catch (error) {
      console.error('Ошибка загрузки игроков для пакетных уведомлений:', error);
      alert('Ошибка загрузки списка игроков');
    } finally {
      setLoadingBatchPlayers(false);
    }
  };

  // Загрузить количество отправленных уведомлений для предложений
  const fetchNotificationCounts = async () => {
    try {
      if (publicProposals.length === 0) return;
      
      const proposalIds = publicProposals.map(p => p.id);
      const response = await apiPost<{ counts: Record<string, number> }>('/public-proposal-responses/notification-counts', {
        proposalIds
      });
      
      setNotificationCounts(response.counts || {});
    } catch (error) {
      console.error('Ошибка загрузки количества уведомлений:', error);
      // Не показываем ошибку пользователю, просто не отображаем счетчики
    }
  };

  const fetchPublicProposals = async () => {
    try {
      setLoadingProposals(true);
      const params: Record<string, string> = {};
      if (selectedDateFrom) params.startDate = selectedDateFrom;
      if (selectedDateTo) params.endDate = selectedDateTo;
      if (selectedClubId) params.clubId = selectedClubId;

      devLogWithPrefix('Frontend', 'Fetching public proposals with params:', params);

      const response = await apiGet<{ proposals: Array<{
        id: string;
        date: string;
        startTime: string;
        endTime: string;
        courtId: string;
        courtName: string;
        clubId: string;
        clubName: string;
        blockIds: string[];
        isAvailable?: boolean;
        tournamentId?: string | null;
        tournamentName?: string | null;
        level?: string | null;
        groupId?: string | null;
        groupName?: string | null;
      }> }>('/court-availability/public-proposals', params);
      
      devLogWithPrefix('Frontend', 'Received proposals response:', response);
      devLogWithPrefix('Frontend', 'Proposals count:', response?.proposals?.length || 0);
      
      // Обновляем список просмотренных ответов перед подсчетом
      await fetchViewedResponses();
      
      // Загружаем актуальный список просмотренных ответов напрямую с сервера
      // для гарантии актуальности данных (состояние React может быть устаревшим)
      let currentViewedResponses: Set<string>;
      try {
        const response = await apiGet<{ responseIds: string[] }>('/public-proposal-responses/viewed-responses');
        currentViewedResponses = new Set(response?.responseIds || []);
      } catch (error) {
        console.error('Ошибка загрузки просмотренных ответов:', error);
        // Используем состояние как fallback
        currentViewedResponses = viewedResponses;
      }
      
      // Загружаем количество ответов и задач для каждого предложения
      // API endpoint /proposal/:proposalId теперь возвращает ответы для всех объединенных слотов
      const proposalsWithCounts = await Promise.all(
        (response.proposals || []).map(async (proposal) => {
          try {
            // Загружаем ответы через API (он уже учитывает все объединенные слоты)
            const responses = await apiGet(`/public-proposal-responses/proposal/${proposal.id}`).catch(() => []);
            const tasks = await apiGet(`/opponent-matching-tasks?proposalId=${proposal.id}`).catch(() => []);
            
            // Подсчитываем только непросмотренные ответы (исключаем SENT - отправленные запросы в приват)
            const allResponses = Array.isArray(responses) ? responses : [];
            const unviewedResponsesCount = allResponses.filter((r: any) => 
              r.status !== 'SENT' && !currentViewedResponses.has(r.id) // Исключаем SENT и просмотренные
            ).length;
            
            return {
              ...proposal,
              responsesCount: unviewedResponsesCount, // Показываем только непросмотренные
              totalResponsesCount: allResponses.length, // Общее количество для справки
              tasksCount: Array.isArray(tasks) ? tasks.length : 0
            };
          } catch (error) {
            console.error(`Ошибка загрузки ответов для предложения ${proposal.id}:`, error);
            // Если ошибка загрузки, считаем что их нет
            return {
              ...proposal,
              responsesCount: 0, // Непросмотренные
              totalResponsesCount: 0, // Всего ответов
              tasksCount: 0
            };
          }
        })
      );
      
      devLogWithPrefix('Frontend', 'Proposals with counts:', proposalsWithCounts.length);
      
      // Синхронизируем occupiedSlots с базой данных
      const newOccupiedSlots = new Set(occupiedSlots);
      proposalsWithCounts.forEach(proposal => {
        if (proposal.isAvailable === false) {
          newOccupiedSlots.add(proposal.id);
        }
      });
      setOccupiedSlots(newOccupiedSlots);
      localStorage.setItem('occupiedSlots', JSON.stringify(Array.from(newOccupiedSlots)));
      
      setPublicProposals(proposalsWithCounts);
      // Загружаем количество отправленных уведомлений после загрузки предложений
      if (proposalsWithCounts.length > 0) {
        setTimeout(() => fetchNotificationCounts(), 100);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки публичных предложений:', error);
      console.error('Error details:', error.response?.data);
      setPublicProposals([]);
    } finally {
      setLoadingProposals(false);
    }
  };

  const fetchPlayerSlotAvailability = async () => {
    try {
      setLoadingPlayerSlotAvailability(true);

      const params: Record<string, string> = {};
      if (selectedDateFrom) params.startDate = selectedDateFrom;
      if (selectedDateTo) params.endDate = selectedDateTo;
      if (selectedClubId) params.clubId = selectedClubId;

      let currentViewedResponses: Set<string>;
      try {
        const viewed = await apiGet<{ responseIds: string[] }>('/public-proposal-responses/viewed-responses');
        currentViewedResponses = new Set(viewed?.responseIds || []);
      } catch (error) {
        console.error('Ошибка загрузки просмотренных ответов для доступности:', error);
        currentViewedResponses = viewedResponses;
      }

      const response = await apiGet<{ proposals: PublicProposalSlot[] }>('/court-availability/public-proposals', params);
      const proposals = Array.isArray(response?.proposals) ? response.proposals : [];

      const tournamentNameById = new Map<string, string>();
      proposals.forEach((proposal) => {
        if (proposal.tournamentId && proposal.tournamentName) {
          tournamentNameById.set(proposal.tournamentId, proposal.tournamentName);
        }
      });

      const tournamentIds = Array.from(new Set(proposals.map(p => p.tournamentId).filter(Boolean))) as string[];
      const matchesByTournament = await Promise.all(
        tournamentIds.map(async (tournamentId) => {
          const matches = await apiGet<any[]>(`/matches/tournament/${tournamentId}`).catch(() => []);
          return { tournamentId, matches: Array.isArray(matches) ? matches : [] };
        })
      );

      const allTournamentMatches = matchesByTournament.flatMap(({ tournamentId, matches }) =>
        matches.map(match => ({
          ...match,
          tournamentId,
          tournamentName: tournamentNameById.get(tournamentId) || null
        }))
      );

      const groupMembersByGroupId = new Map<string, Array<{ id: string; name: string }>>();
      await Promise.all(
        tournamentIds.map(async (tournamentId) => {
          const groupsData = await apiGet<any[]>(`/tournament-groups/tournament/${tournamentId}`).catch(() => []);
          (Array.isArray(groupsData) ? groupsData : []).forEach((group: any) => {
            if (!group?.id) return;
            const members = Array.isArray(group.memberships)
              ? group.memberships
                  .map((membership: any) => membership?.player)
                  .filter((player: any) => player?.id && player?.name)
                  .map((player: any) => ({ id: player.id, name: player.name }))
              : [];
            const uniqueMembers = Array.from(new Map(members.map(member => [member.id, member])).values());
            groupMembersByGroupId.set(group.id, uniqueMembers);
          });
        })
      );

      const toDateOnly = (dateValue?: string) => {
        if (!dateValue) return '';
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };

      const toMinutes = (time?: string) => {
        if (!time || !time.includes(':')) return null;
        const [hours, minutes] = time.split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        return hours * 60 + minutes;
      };

      const slotsWithPlayers = await Promise.all(
        proposals.map(async (proposal) => {
          const responses = await apiGet<Array<{
            id?: string;
            playerId?: string;
            status?: string;
            updatedAt?: string;
            createdAt?: string;
            player?: { id?: string; name?: string };
            groups?: Array<{ id?: string; name?: string; level?: string }>;
          }>>(`/public-proposal-responses/proposal/${proposal.id}`).catch(() => []);

          const latestStatusByPlayerGroup = new Map<string, {
            responseId?: string;
            playerId: string;
            name: string;
            status: string;
            createdAt: string;
            updatedAt: string;
            tournamentLabel: string;
            groupLabel: string;
            tournamentId: string;
            groupId: string;
          }>();

          const toTimestamp = (value?: string) => {
            if (!value) return 0;
            const timestamp = new Date(value).getTime();
            return Number.isNaN(timestamp) ? 0 : timestamp;
          };

          responses.forEach((responseItem) => {
            const playerId = responseItem.player?.id || responseItem.playerId;
            if (!playerId) return;

            const playerName = responseItem.player?.name || 'Неизвестный игрок';
            const status = responseItem.status || 'IGNORED';
            const createdAt = responseItem.createdAt || responseItem.updatedAt || '';
            const updatedAt = responseItem.updatedAt || responseItem.createdAt || '';
            const tournamentLabel = proposal.tournamentName || 'Без турнира';
            const tournamentId = proposal.tournamentId || 'no-tournament';
            const responseGroups = Array.isArray(responseItem.groups) && responseItem.groups.length > 0
              ? responseItem.groups
              : [{ id: proposal.groupId || 'no-group', name: proposal.groupName || 'Без группы', level: proposal.level || '' }];

            responseGroups.forEach((group) => {
              const groupId = group.id || proposal.groupId || 'no-group';
              const groupLabel = group.name
                ? `${group.name}${group.level ? ` (${group.level})` : ''}`
                : 'Без группы';
              const key = `${playerId}|${groupId}`;
              const existing = latestStatusByPlayerGroup.get(key);

              if (!existing || toTimestamp(updatedAt) >= toTimestamp(existing.updatedAt)) {
                latestStatusByPlayerGroup.set(key, {
                  responseId: responseItem.id,
                  playerId,
                  name: playerName,
                  status,
                  createdAt,
                  updatedAt,
                  tournamentLabel,
                  groupLabel,
                  tournamentId,
                  groupId
                });
              }
            });
          });

          const collectPlayersByStatus = (status: string) => {
            const grouped = new Map<string, string[]>();

            Array.from(latestStatusByPlayerGroup.values())
              .filter(player => player.status === status)
              .forEach((player) => {
                const label = `${player.tournamentLabel} • ${player.groupLabel}`;
                if (!grouped.has(label)) {
                  grouped.set(label, []);
                }
                grouped.get(label)!.push(player.name);
              });

            return Array.from(grouped.entries())
              .map(([label, players]) => ({
                label,
                players: Array.from(new Set(players)).sort((a, b) => a.localeCompare(b, 'ru'))
              }))
              .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
          };

          const readyPlayersByGroup = new Map<string, {
            tournamentId: string;
            groupId: string;
            label: string;
            players: Array<{ id: string; name: string }>;
          }>();

          Array.from(latestStatusByPlayerGroup.values())
            .filter(player => player.status === 'READY')
            .forEach((player) => {
              const groupKey = `${player.tournamentId}|${player.groupId}`;
              const label = `${player.tournamentLabel} • ${player.groupLabel}`;
              if (!readyPlayersByGroup.has(groupKey)) {
                readyPlayersByGroup.set(groupKey, {
                  tournamentId: player.tournamentId,
                  groupId: player.groupId,
                  label,
                  players: []
                });
              }
              const bucket = readyPlayersByGroup.get(groupKey)!;
              if (!bucket.players.some(p => p.id === player.playerId)) {
                bucket.players.push({ id: player.playerId, name: player.name });
              }
            });

          const readyPlayers = Array.from(readyPlayersByGroup.values())
            .map(groupData => {
              const groupMatches = allTournamentMatches.filter(match =>
                match.tournamentId === groupData.tournamentId &&
                (match.groupId || match.group?.id) === groupData.groupId &&
                match.status === 'COMPLETED'
              );

              const playedPairs = new Set<string>();
              groupMatches.forEach((match) => {
                const ids = [match.player1Id, match.player2Id, match.player3Id, match.player4Id].filter(Boolean) as string[];
                for (let i = 0; i < ids.length; i++) {
                  for (let j = i + 1; j < ids.length; j++) {
                    const a = ids[i];
                    const b = ids[j];
                    const key = a < b ? `${a}|${b}` : `${b}|${a}`;
                    playedPairs.add(key);
                  }
                }
              });

              const playersScope = groupData.groupId !== 'no-group'
                ? (groupMembersByGroupId.get(groupData.groupId) || groupData.players)
                : groupData.players;

              const formatDateShort = (dateValue?: string | null) => {
                if (!dateValue) return '';
                const date = new Date(dateValue);
                if (Number.isNaN(date.getTime())) return '';
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}.${month}.${year}`;
              };

              const getMatchDateTime = (match: any): Date | null => {
                const rawDate = match.scheduledDate || match.reservation?.date;
                if (!rawDate) return null;
                const base = new Date(rawDate);
                if (Number.isNaN(base.getTime())) return null;

                const time = match.scheduledTime || match.reservation?.startTime;
                if (time && typeof time === 'string' && time.includes(':')) {
                  const [h, m] = time.split(':').map(Number);
                  if (!Number.isNaN(h) && !Number.isNaN(m)) {
                    base.setHours(h, m, 0, 0);
                  }
                } else {
                  base.setHours(0, 0, 0, 0);
                }
                return base;
              };

              const hasLastPlannedGroupMatch = (playerId: string) => {
                const remainingOpponents = playersScope
                  .filter(opponent => opponent.id !== playerId)
                  .filter((opponent) => {
                    const pairKey = playerId < opponent.id
                      ? `${playerId}|${opponent.id}`
                      : `${opponent.id}|${playerId}`;
                    return !playedPairs.has(pairKey);
                  });

                if (remainingOpponents.length !== 1) return false;

                const lastOpponent = remainingOpponents[0];
                const now = new Date();

                return allTournamentMatches.some((match) => {
                  const matchGroupId = match.groupId || match.group?.id || 'no-group';
                  const sameTournament = match.tournamentId === groupData.tournamentId;
                  const sameGroup = groupData.groupId === 'no-group'
                    ? true
                    : (matchGroupId === groupData.groupId || matchGroupId === 'no-group');
                  if (!sameTournament || !sameGroup) return false;
                  if (match.status === 'CANCELLED' || match.status === 'COMPLETED') return false;

                  const ids = [match.player1Id, match.player2Id, match.player3Id, match.player4Id].filter(Boolean) as string[];
                  const hasPlayer = ids.includes(playerId);
                  const hasOpponent = ids.includes(lastOpponent.id);
                  if (!hasPlayer || !hasOpponent) return false;

                  const matchDateTime = getMatchDateTime(match);
                  if (!matchDateTime) return false;
                  return matchDateTime.getTime() >= now.getTime();
                });
              };

              const getPlayerMatchContext = (playerId: string) => {
                const playerMatches = allTournamentMatches.filter(match => {
                  const matchGroupId = match.groupId || match.group?.id || 'no-group';
                  const sameTournament = match.tournamentId === groupData.tournamentId;
                  const sameGroup = groupData.groupId === 'no-group'
                    ? true
                    : (matchGroupId === groupData.groupId || matchGroupId === 'no-group');
                  const hasPlayer = [match.player1Id, match.player2Id, match.player3Id, match.player4Id].includes(playerId);
                  return sameTournament && sameGroup && hasPlayer && match.status !== 'CANCELLED';
                });

                const slotDateTime = new Date(`${proposal.date}T${proposal.startTime}:00`);
                const nowDateTime = new Date();
                const pastMatch = playerMatches
                  .filter(match => match.status === 'COMPLETED')
                  .map(match => ({ match, dt: getMatchDateTime(match) }))
                  .filter(item => item.dt && item.dt.getTime() < slotDateTime.getTime())
                  .sort((a, b) => (b.dt!.getTime() - a.dt!.getTime()))[0];

                const nextMatch = playerMatches
                  .map(match => ({ match, dt: getMatchDateTime(match) }))
                  .filter(item => item.dt && item.dt.getTime() >= nowDateTime.getTime())
                  .sort((a, b) => (a.dt!.getTime() - b.dt!.getTime()))[0];

                const hasCurrentDateMatch = playerMatches.some((match) => {
                  const matchDate = toDateOnly(match.scheduledDate || match.reservation?.date);
                  return matchDate === proposal.date;
                });
                const shouldGrayByLastPlannedMatch = hasLastPlannedGroupMatch(playerId);

                const buildTooltip = (match: any) => {
                  const time = match.scheduledTime || match.reservation?.startTime || 'время не указано';
                  const opponents = getOpponents(match, playerId);
                  return `${time} • ${opponents.join(', ') || 'соперник не указан'}`;
                };
                const getOpponentInline = (match: any): string | undefined => {
                  const opponents = getOpponents(match, playerId);
                  return opponents.length > 0 ? opponents.join(', ') : undefined;
                };
                const getTournamentInline = (match: any): string | undefined => {
                  const fromMatch = String(match?.tournamentName || '').trim();
                  if (fromMatch) return fromMatch;
                  const fromProposal = String(proposal?.tournamentName || '').trim();
                  if (fromProposal) return fromProposal;
                  const fromGroupLabel = String(groupData?.label || '').split(' • ')[0]?.trim();
                  return fromGroupLabel || undefined;
                };
                const getDateTimeInline = (match: any): string | undefined => {
                  const rawDate = match?.scheduledDate || match?.reservation?.date;
                  if (!rawDate) return undefined;
                  const datePart = formatDateShort(rawDate);
                  const timePart = String(match?.scheduledTime || match?.reservation?.startTime || '').trim();
                  return timePart ? `${datePart} • ${timePart}` : datePart;
                };

                return {
                  hasCurrentDateMatch: hasCurrentDateMatch || shouldGrayByLastPlannedMatch,
                  previousMatchDate: pastMatch ? getDateTimeInline(pastMatch.match) : undefined,
                  previousMatchOpponent: pastMatch ? getOpponentInline(pastMatch.match) : undefined,
                  previousMatchTournament: pastMatch ? getTournamentInline(pastMatch.match) : undefined,
                  previousMatchTooltip: pastMatch ? buildTooltip(pastMatch.match) : undefined,
                  nextMatchDate: nextMatch ? getDateTimeInline(nextMatch.match) : undefined,
                  nextMatchOpponent: nextMatch ? getOpponentInline(nextMatch.match) : undefined,
                  nextMatchTournament: nextMatch ? getTournamentInline(nextMatch.match) : undefined,
                  nextMatchTooltip: nextMatch ? buildTooltip(nextMatch.match) : undefined
                };
              };

              const getOpponents = (match: any, playerId: string): string[] => {
                const matchPlayers = [
                  match.player1,
                  match.player2,
                  match.player3,
                  match.player4
                ].filter(Boolean);
                return Array.from(new Set(
                  matchPlayers
                    .filter((p: any) => p?.id && p.id !== playerId)
                    .map((p: any) => p.name)
                    .filter(Boolean)
                ));
              };

              const playersWithNoGamesInfo = groupData.players
                .map((player) => {
                  const opponentsWithoutGames = playersScope
                    .filter(opponent => opponent.id !== player.id)
                    .filter((opponent) => {
                      const pairKey = player.id < opponent.id
                        ? `${player.id}|${opponent.id}`
                        : `${opponent.id}|${player.id}`;
                      return !playedPairs.has(pairKey);
                    })
                    .map(opponent => opponent.name)
                    .sort((a, b) => a.localeCompare(b, 'ru'));

                  if (opponentsWithoutGames.length === 0) return null;

                  const opponentsWithDates = playersScope
                    .filter(opponent => opponent.id !== player.id)
                    .filter((opponent) => {
                      const pairKey = player.id < opponent.id
                        ? `${player.id}|${opponent.id}`
                        : `${opponent.id}|${player.id}`;
                      return !playedPairs.has(pairKey);
                    })
                    .map(opponent => {
                      const context = getPlayerMatchContext(opponent.id);
                      const opponentStatus = latestStatusByPlayerGroup.get(`${opponent.id}|${groupData.groupId}`);
                      let sentAtRaw = '';
                      if (opponentStatus) {
                        const createdTs = toTimestamp(opponentStatus.createdAt);
                        const updatedTs = toTimestamp(opponentStatus.updatedAt);

                        // SENT shows last send/update time.
                        if (opponentStatus.status === 'SENT') {
                          sentAtRaw = opponentStatus.updatedAt || opponentStatus.createdAt || '';
                        } else if (createdTs > 0 && updatedTs > createdTs) {
                          // If status was changed later (e.g. SENT -> REJECTED), keep initial send time from createdAt.
                          sentAtRaw = opponentStatus.createdAt;
                        }
                      }
                      const sentAtDate = sentAtRaw ? new Date(sentAtRaw) : null;
                      const notificationSentAt = sentAtDate && !Number.isNaN(sentAtDate.getTime())
                        ? sentAtDate.toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '';
                      const reactionAtRaw = opponentStatus && opponentStatus.status !== 'SENT'
                        ? (opponentStatus.updatedAt || opponentStatus.createdAt || '')
                        : '';
                      const reactionAtDate = reactionAtRaw ? new Date(reactionAtRaw) : null;
                      const reactionAt = reactionAtDate && !Number.isNaN(reactionAtDate.getTime())
                        ? reactionAtDate.toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '';
                      const reactionUnviewed = Boolean(
                        opponentStatus &&
                        opponentStatus.status !== 'SENT' &&
                        opponentStatus.responseId &&
                        !currentViewedResponses.has(opponentStatus.responseId)
                      );
                      return {
                        id: opponent.id,
                        responseId: opponentStatus?.responseId,
                        name: opponent.name,
                        availabilityStatus: opponentStatus?.status || 'IGNORED',
                        hasCurrentDateMatch: context.hasCurrentDateMatch,
                        prev: context.previousMatchDate || '—',
                        next: context.nextMatchDate || '—',
                        notificationSentAt: notificationSentAt || undefined,
                        notificationSentAtRaw: sentAtRaw || undefined,
                        reactionAt: reactionAt || undefined,
                        reactionAtRaw: reactionAtRaw || undefined,
                        reactionUnviewed,
                        prevTooltip: context.previousMatchTooltip,
                        nextTooltip: context.nextMatchTooltip
                      };
                    })
                    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

                  const line = `${player.name}: ${opponentsWithDates.map(o => o.name).join(', ')}`;
                  const playerContext = getPlayerMatchContext(player.id);
                  const noGamesPreviousInfo = opponentsWithDates.map(o => `${o.name}: ${o.prev}`).join(', ');
                  const noGamesNextInfo = opponentsWithDates.map(o => `${o.name}: ${o.next}`).join(', ');

                  return {
                    playerName: player.name,
                    hasCurrentDateMatch: playerContext.hasCurrentDateMatch,
                    line,
                    previousMatchDate: playerContext.previousMatchDate,
                    previousMatchOpponent: playerContext.previousMatchOpponent,
                    previousMatchTournament: playerContext.previousMatchTournament,
                    previousMatchTooltip: playerContext.previousMatchTooltip,
                    noGamesPreviousInfo,
                    nextMatchDate: playerContext.nextMatchDate,
                    nextMatchOpponent: playerContext.nextMatchOpponent,
                    nextMatchTournament: playerContext.nextMatchTournament,
                    nextMatchTooltip: playerContext.nextMatchTooltip,
                    noGamesNextInfo,
                    opponentsDetailed: opponentsWithDates
                  };
                })
                .filter((line): line is {
                  playerName: string;
                  hasCurrentDateMatch?: boolean;
                  line: string;
                  previousMatchDate?: string;
                  previousMatchOpponent?: string;
                  previousMatchTournament?: string;
                  previousMatchTooltip?: string;
                  noGamesPreviousInfo?: string;
                  nextMatchDate?: string;
                  nextMatchOpponent?: string;
                  nextMatchTournament?: string;
                  nextMatchTooltip?: string;
                  noGamesNextInfo?: string;
                  opponentsDetailed?: Array<{
                    id?: string;
                    responseId?: string;
                    name: string;
                    availabilityStatus?: string;
                    hasCurrentDateMatch?: boolean;
                    prev: string;
                    next: string;
                    notificationSentAt?: string;
                    notificationSentAtRaw?: string;
                    reactionAt?: string;
                    reactionAtRaw?: string;
                    reactionUnviewed?: boolean;
                    prevTooltip?: string;
                    nextTooltip?: string;
                  }>;
                } => Boolean(line))
                .sort((a, b) => a.playerName.localeCompare(b.playerName, 'ru'));

              return {
                tournamentId: groupData.tournamentId,
                groupId: groupData.groupId,
                label: groupData.label,
                players: playersWithNoGamesInfo
              };
            })
            .filter(group => group.players.length > 0)
            .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

          const proposalStartMinutes = toMinutes(proposal.startTime);
          const proposalEndMinutes = toMinutes(proposal.endTime);
          const proposalDate = proposal.date;

          const assignedMatch = allTournamentMatches.find((match) => {
            if (match.status === 'CANCELLED') return false;

            const matchCourtId = match.court?.id || match.courtId || null;
            if (!matchCourtId || matchCourtId !== proposal.courtId) return false;

            const matchDate = toDateOnly(match.scheduledDate || match.reservation?.date);
            if (!matchDate || matchDate !== proposalDate) return false;

            const matchStartMinutes = toMinutes(match.scheduledTime || match.reservation?.startTime);
            if (matchStartMinutes === null || proposalStartMinutes === null || proposalEndMinutes === null) {
              return false;
            }

            return matchStartMinutes >= proposalStartMinutes && matchStartMinutes < proposalEndMinutes;
          });

          const assignedPlayers = assignedMatch
            ? [
                assignedMatch.player1?.name,
                assignedMatch.player2?.name,
                assignedMatch.player3?.name,
                assignedMatch.player4?.name
              ].filter((name, index, arr): name is string => Boolean(name) && arr.indexOf(name) === index)
            : [];

          return {
            proposalId: proposal.id,
            date: proposal.date,
            startTime: proposal.startTime,
            endTime: proposal.endTime,
            clubName: proposal.clubName,
            courtName: proposal.courtName,
            tournamentName: proposal.tournamentName || null,
            groupName: proposal.groupName || null,
            readyPlayers,
            thinkingPlayers: collectPlayersByStatus('THINKING'),
            rejectedPlayers: collectPlayersByStatus('REJECTED'),
            sentPlayers: collectPlayersByStatus('SENT'),
            ignoredPlayers: collectPlayersByStatus('IGNORED'),
            totalResponses: latestStatusByPlayerGroup.size,
            assignedMatch: assignedMatch
              ? {
                  id: assignedMatch.id,
                  matchCode: assignedMatch.matchCode || null,
                  players: assignedPlayers,
                  tournamentId: assignedMatch.tournamentId || proposal.tournamentId || null,
                  groupId: assignedMatch.groupId || assignedMatch.group?.id || proposal.groupId || null,
                  tournamentName: assignedMatch.tournamentName || proposal.tournamentName || null,
                  groupName: assignedMatch.group?.name || proposal.groupName || null
                }
              : null
          } satisfies SlotPlayerAvailability;
        })
      );

      const sortedSlots = [...slotsWithPlayers].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        if (a.startTime !== b.startTime) return a.startTime.localeCompare(b.startTime);
        return a.courtName.localeCompare(b.courtName, 'ru');
      });

      setPlayerSlotAvailability(sortedSlots);
    } catch (error) {
      console.error('Ошибка загрузки доступности игроков:', error);
      setPlayerSlotAvailability([]);
    } finally {
      setLoadingPlayerSlotAvailability(false);
    }
  };

  // Загрузка игроков группы с информацией о матчах
  const fetchGroupPlayers = async (groupId: string, clickedPlayerId: string | null, tournamentId?: string | null, level?: string | null, proposalId?: string | null) => {
    try {
      setLoadingGroupPlayers(true);
      const group = await apiGet<any>(`/tournament-groups/${groupId}`);
      if (group && group.memberships) {
        const players = group.memberships
          .map((m: any) => m.player || m.playerId)
          .filter(Boolean)
          .map((p: any) => ({
            id: p.id || p,
            name: p.name || 'Неизвестный игрок'
          }));
        // Убираем дубликаты
        type PlayerWithMatchInfo = { 
          id: string; 
          name: string; 
          matchStatus?: 'played' | 'scheduled' | 'none';
          matchDate?: string;
          matchDateKey?: string | null;
          matchId?: string;
          hasResponse?: boolean;
          responseStatus?: string;
          taskId?: string;
          taskStatus?: string;
        };
        let uniquePlayers: PlayerWithMatchInfo[] = Array.from(new Map(players.map((p: any) => [p.id, p])).values()) as PlayerWithMatchInfo[];
        
        // Исключаем игрока, на которого кликнули
        if (clickedPlayerId) {
          uniquePlayers = uniquePlayers.filter(p => p.id !== clickedPlayerId);
        }
        
        // Загружаем матчи для группы, если есть турнир и уровень
        if (tournamentId && level) {
          try {
            console.log('[GROUP_PLAYERS] Загрузка матчей для группы:', { tournamentId, level, groupId, clickedPlayerId });
            const matchesData = await apiGet<{ matches: any[] }>(
              `/matches/tournament/${tournamentId}/level/${level}`
            );
            console.log('[GROUP_PLAYERS] Загружено матчей всего:', matchesData.matches?.length || 0);
            
            // Фильтруем матчи по группе
            const matches = (matchesData.matches || []).filter((match: any) => match.groupId === groupId);
            console.log('[GROUP_PLAYERS] Матчей в группе:', matches.length);
            console.log('[GROUP_PLAYERS] Примеры матчей:', matches.slice(0, 3).map((m: any) => ({
              id: m.id,
              groupId: m.groupId,
              player1Id: m.player1Id,
              player2Id: m.player2Id,
              player3Id: m.player3Id,
              player4Id: m.player4Id,
              scheduledDate: m.scheduledDate
            })));
            
            // Для каждого игрока находим матчи, где он играл с кликнутым игроком
            uniquePlayers = uniquePlayers.map((player: { id: string; name: string }) => {
              // Ищем матчи, где играют оба игрока: кликнутый и текущий
              const playerMatches = matches.filter((match: any) => {
                if (!clickedPlayerId) return false;

                // Для doubles: team A = player1 + player3, team B = player2 + player4.
                // Для singles: team A = player1, team B = player2.
                // Нас интересуют только матчи, где игроки именно соперники, а не партнеры.
                const clickedOnTeamA = match.player1Id === clickedPlayerId || match.player3Id === clickedPlayerId;
                const clickedOnTeamB = match.player2Id === clickedPlayerId || match.player4Id === clickedPlayerId;
                const playerOnTeamA = match.player1Id === player.id || match.player3Id === player.id;
                const playerOnTeamB = match.player2Id === player.id || match.player4Id === player.id;

                const areOpponents =
                  (clickedOnTeamA && playerOnTeamB) ||
                  (clickedOnTeamB && playerOnTeamA);

                return areOpponents;
              });
              
              if (playerMatches.length > 0) {
                // Выбираем наиболее релевантный матч:
                // 1) сыгранный, 2) реально назначенный (а не "виртуальный" через generatedReservations), 3) любой.
                const pickRank = (match: any): number => {
                  const hasResult = match.result !== null && match.result !== undefined;
                  const normalizedStatus = String(match.status || '').trim().toUpperCase();
                  const isCancelled = normalizedStatus === 'CANCELLED' || normalizedStatus === 'SUSPENDED';
                  const hasCommittedSchedule =
                    Boolean(match.reservationId || match.reservation?.id) ||
                    Boolean(match.scheduledDate && match.scheduledTime && match.courtId);
                  if (hasResult) return 300;
                  if (!isCancelled && hasCommittedSchedule) return 200;
                  return 100;
                };
                const sortedMatches = [...playerMatches].sort((a: any, b: any) => pickRank(b) - pickRank(a));
                const match = sortedMatches[0];
                console.log(`[GROUP_PLAYERS] Найден релевантный матч для игрока ${player.name} с кликнутым игроком:`, match);
                
                // Определяем статус матча
                const hasResult = match.result !== null && match.result !== undefined;
                const normalizedStatus = String(match.status || '').trim().toUpperCase();
                const isCancelled = normalizedStatus === 'CANCELLED' || normalizedStatus === 'SUSPENDED';

                // Считаем "назначенным" только реально привязанный матч:
                // reservationId либо полный manual schedule (дата+время+корт).
                // ВАЖНО: reservation?.id может быть подмешан из generatedReservations по matchCode,
                // это не означает фактическую привязку резерва к матчу.
                const hasCommittedSchedule =
                  Boolean(match.reservationId) ||
                  Boolean(match.scheduledDate && match.scheduledTime && match.courtId);
                
                // Определяем статус:
                // - сыгран: есть result
                // - сыграет: матч реально назначен и не отменен
                // - иначе: нет матча для целей этого окна
                let matchStatus: 'played' | 'scheduled' | 'none' = 'none';
                if (hasResult) {
                  matchStatus = 'played';
                } else if (hasCommittedSchedule && !isCancelled) {
                  matchStatus = 'scheduled';
                } else {
                  matchStatus = 'none';
                }
                
                // Форматируем дату
                let matchDate = '';
                let matchDateKey: string | null = null;
                if (match.scheduledDate) {
                  const date = new Date(match.scheduledDate);
                  matchDate = date.toLocaleDateString('ru-RU');
                  matchDateKey = toDateKey(match.scheduledDate);
                } else if (match.reservation?.date) {
                  const date = new Date(match.reservation.date);
                  matchDate = date.toLocaleDateString('ru-RU');
                  matchDateKey = toDateKey(match.reservation.date);
                }
                
                // Если матч не запланирован и не сыгран, не показываем его
                if (matchStatus === 'none') {
                  return {
                    ...player,
                    matchStatus: 'none' as const,
                    matchDate: '-'
                  };
                }
                
                return {
                  ...player,
                  matchStatus,
                  matchDate: matchDate || 'Дата не указана',
                  matchDateKey,
                  matchId: match.id
                };
              }
              
              return {
                ...player,
                matchStatus: 'none' as const,
                matchDate: '-'
              };
            });
            
            console.log('[GROUP_PLAYERS] Результат обработки игроков:', uniquePlayers.map((p: any) => ({
              name: p.name,
              matchStatus: p.matchStatus,
              matchDate: p.matchDate
            })));
          } catch (error) {
            console.error('[GROUP_PLAYERS] Ошибка загрузки матчей:', error);
            // Если не удалось загрузить матчи, просто оставляем игроков без информации о матчах
          }
        }
        
        // Загружаем ответы игроков на предложение, если есть proposalId
        if (proposalId) {
          try {
            const responses = await apiGet(`/public-proposal-responses/proposal/${proposalId}`).catch(() => []);
            const responsesMap = new Map<string, any>();
            (responses || []).forEach((r: any) => {
              responsesMap.set(r.playerId, r);
            });
            
            // Загружаем задачи подбора соперников для этого предложения
            const tasks = await apiGet(`/opponent-matching-tasks?proposalId=${proposalId}`).catch(() => []);
            const tasksMap = new Map<string, any>();
            (tasks || []).forEach((t: any) => {
              // Создаем ключ для задачи: activePlayerId-opponentPlayerId или opponentPlayerId-activePlayerId
              const key1 = `${t.activePlayerId}-${t.opponentPlayerId}`;
              const key2 = `${t.opponentPlayerId}-${t.activePlayerId}`;
              if (clickedPlayerId) {
                if (t.activePlayerId === clickedPlayerId) {
                  tasksMap.set(t.opponentPlayerId, t);
                } else if (t.opponentPlayerId === clickedPlayerId) {
                  tasksMap.set(t.activePlayerId, t);
                }
              }
            });
            
            // Обновляем игроков с информацией об ответах и задачах
            uniquePlayers = uniquePlayers.map((player: any) => {
              const response = responsesMap.get(player.id);
              const task = tasksMap.get(player.id);
              
              return {
                ...player,
                hasResponse: !!response,
                responseStatus: response?.status || null,
                taskId: task?.id || null,
                taskStatus: task?.status || null
              };
            });
          } catch (error) {
            console.error('[GROUP_PLAYERS] Ошибка загрузки ответов и задач:', error);
          }
        }
        
        setGroupPlayers(uniquePlayers);
      } else {
        setGroupPlayers([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки игроков группы:', error);
      setGroupPlayers([]);
    } finally {
      setLoadingGroupPlayers(false);
    }
  };

  const handleGroupClick = async (group: { id: string; name: string; level: string }, playerId: string, playerName: string) => {
    setSelectedGroupForPlayers(group);
    setClickedPlayerId(playerId);
    setClickedPlayerName(playerName);
    setShowGroupPlayersModal(true);
    // Получаем tournamentId и proposalId из editingProposal, а level из группы
    const tournamentId = editingProposal?.tournamentId || null;
    const level = group.level || editingProposal?.level || null;
    const proposalId = editingProposal?.id || null;
    setGroupPlayersTournamentId(tournamentId);
    setGroupPlayersLevel(level);
    setGroupPlayersProposalId(proposalId);
    console.log('[GROUP_PLAYERS] handleGroupClick:', { group, playerId, playerName, tournamentId, level, proposalId, editingProposal });
    await fetchGroupPlayers(group.id, playerId, tournamentId, level, proposalId);
  };

  const fetchTournamentPlayers = async (tournamentId: string) => {
    if (!tournamentId) {
      setTournamentPlayers([]);
      return;
    }
    
    try {
      const data = await apiGet<any[]>(`/tournaments/${tournamentId}/players`);
      // API возвращает массив memberships с включенными player
      // Фильтруем дубликаты по id игрока
      const playersMap = new Map<string, any>();
      data.forEach((m: any) => {
        if (m.player && m.player.id && !playersMap.has(m.player.id)) {
          playersMap.set(m.player.id, m.player);
        }
      });
      const players = Array.from(playersMap.values());
      // Сортируем игроков по алфавиту по имени
      players.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'ru');
      });
      setTournamentPlayers(players);
      devLog('Loaded tournament players:', players.length);
    } catch (error) {
      console.error('Ошибка загрузки участников турнира:', error);
      setTournamentPlayers([]);
    }
  };

  const fetchUnreservedMatches = async (tournamentId: string, includeReserved = false) => {
    if (!tournamentId) {
      setUnreservedMatches([]);
      setSelectedGeneratedMatchId('');
      setSelectedGeneratedMatchDbId(null);
      return;
    }

    try {
      setLoadingUnreservedMatches(true);
      const query = includeReserved ? '?includeReserved=true' : '';
      const data = await apiGet<UnreservedMatchOption[]>(`/matches/tournament/${tournamentId}/unreserved${query}`);
      setUnreservedMatches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки сгенерированных матчей без резервирования:', error);
      setUnreservedMatches([]);
    } finally {
      setLoadingUnreservedMatches(false);
    }
  };

  const fetchGamesForPicker = async (tournamentId: string) => {
    if (!tournamentId) {
      setGamesForPicker([]);
      return;
    }
    try {
      setLoadingGamesForPicker(true);
      const query = includeReservedMatchesInSelect ? '?includeReserved=true' : '';
      const data = await apiGet<UnreservedMatchOption[]>(`/matches/tournament/${tournamentId}/unreserved${query}`);
      setGamesForPicker(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки игр для подбора:', error);
      setGamesForPicker([]);
    } finally {
      setLoadingGamesForPicker(false);
    }
  };

  const handleToggleGamesPickerPlanMode = async (enabled: boolean) => {
    if (!selectedTournamentForGamesPicker) {
      alert('Сначала выберите турнир в блоке "Подбор игр".');
      return;
    }
    if (gamesPickerPlanModeLoading) return;
    try {
      setGamesPickerPlanModeLoading(true);
      const response = await apiPost<{
        enabled?: boolean;
        flushResult?: {
          processed?: number;
          scheduled?: number;
          updated?: number;
          cancelled?: number;
          skipped?: number;
        };
      }>('/court-reservations/notification-planning-mode', {
        tournamentId: selectedTournamentForGamesPicker,
        enabled
      });
      setGamesPickerPlanMode(Boolean(response?.enabled));

      if (!enabled) {
        const stats = response?.flushResult || {};
        alert(
          `Режим планирования выключен.\n` +
          `Обработано матчей: ${stats.processed || 0}\n` +
          `Назначено: ${stats.scheduled || 0}\n` +
          `Обновлено: ${stats.updated || 0}\n` +
          `Отменено: ${stats.cancelled || 0}\n` +
          `Без изменений: ${stats.skipped || 0}`
        );
      }
    } catch (error: any) {
      console.error('Ошибка переключения режима планирования уведомлений:', error);
      alert(error?.message || 'Не удалось переключить режим планирования уведомлений');
    } finally {
      setGamesPickerPlanModeLoading(false);
    }
  };

  const formatUnreservedMatchLabel = (match: UnreservedMatchOption): string => {
    const sideA = getUnreservedMatchSideLabel(match, 'A') || 'TBD';
    const sideB = getUnreservedMatchSideLabel(match, 'B') || 'TBD';
    const skeletonSuffix = sideA === 'TBD' && sideB === 'TBD' ? ' [каркас]' : '';
    const matchupText = `${sideA} vs ${sideB}${skeletonSuffix}`;
    const matchCodeText = String(match.matchCode || '').trim();
    const levelFromMatch = String(match.level || '').trim();
    const stageFromMatch = extractPlayoffStageShort(match.roundName);
    const stageLocalNumber = Number(match.stageMatchNumber || 0);
    const isPlayoff = Boolean(
      /playoff|плей-офф/i.test(matchCodeText) ||
      (!match.groupName && match.roundName)
    );

    if (isPlayoff) {
      const prefixParts = [
        levelFromMatch,
        stageFromMatch,
        stageLocalNumber > 0 ? String(stageLocalNumber) : ''
      ].filter(Boolean);

      return prefixParts.length > 0
        ? `${prefixParts.join(' • ')} • ${matchupText}`
        : matchupText;
    }

    const code = match.matchCode || `Матч ${match.matchNumber || 'без номера'}`;
    const stage = [match.level, match.groupName || match.roundName].filter(Boolean).join(', ');
    return stage
      ? `${code}: ${matchupText} (${stage})`
      : `${code}: ${matchupText}`;
  };

  const formatUnreservedMatchMetaLabel = (match: UnreservedMatchOption): string => {
    const matchCodeText = String(match.matchCode || '').trim();
    const levelFromMatch = String(match.level || '').trim();
    const stageFromMatch = extractPlayoffStageShort(match.roundName);
    const stageLocalNumber = Number(match.stageMatchNumber || 0);
    const isPlayoff = Boolean(
      /playoff|плей-офф/i.test(matchCodeText) ||
      (!match.groupName && match.roundName)
    );

    if (isPlayoff) {
      const prefixParts = [
        levelFromMatch,
        stageFromMatch,
        stageLocalNumber > 0 ? String(stageLocalNumber) : ''
      ].filter(Boolean);
      return prefixParts.join(' • ');
    }

    const code = match.matchCode || `Матч ${match.matchNumber || 'без номера'}`;
    const stage = [match.level, match.groupName || match.roundName].filter(Boolean).join(', ');
    return stage
      ? `${code} (${stage})`
      : `${code}`;
  };

  const resolveUnreservedMatchPlayerName = (
    directName: string | null | undefined,
    playerId: string | null | undefined
  ): string => {
    const normalized = String(directName || '').trim();
    if (normalized) return normalized;
    if (!playerId) return '';
    return getPlayerNameById(playerId);
  };

  const getUnreservedMatchSideLabel = (
    match: UnreservedMatchOption,
    side: 'A' | 'B'
  ): string => {
    const slotName = side === 'A' ? 'home' : 'away';

    const currentSidePlayers = side === 'A'
      ? [
          resolveUnreservedMatchPlayerName(match.player1Name, match.player1Id),
          resolveUnreservedMatchPlayerName(match.player3Name, match.player3Id)
        ]
      : [
          resolveUnreservedMatchPlayerName(match.player2Name, match.player2Id),
          resolveUnreservedMatchPlayerName(match.player4Name, match.player4Id)
        ];

    const currentLabel = currentSidePlayers.filter(Boolean).join(' / ');
    if (currentLabel) return currentLabel;

    const sourceMatch = (match.previousMatches || []).find(
      (source) => String(source.nextMatchSlot || '').toLowerCase() === slotName
    );
    if (!sourceMatch) return '';

    const winnerLabel = [sourceMatch.winnerName, sourceMatch.winner2Name]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');
    if (winnerLabel) return winnerLabel;

    const sourceSingles = [sourceMatch.player1Name, sourceMatch.player2Name]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    if (sourceSingles.length === 2) {
      return `${sourceSingles[0]} / ${sourceSingles[1]}`;
    }

    const sourceDoubles = [
      [sourceMatch.player1Name, sourceMatch.player3Name]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' / '),
      [sourceMatch.player2Name, sourceMatch.player4Name]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' / ')
    ].filter(Boolean);

    if (sourceDoubles.length >= 2) {
      return `${sourceDoubles[0]} / ${sourceDoubles[1]}`;
    }

    return sourceSingles[0] || sourceDoubles[0] || '';
  };

  const getPlayerNameById = (playerId: string): string => {
    if (!playerId) return '';
    const inTournament = tournamentPlayers.find((p: any) => p.id === playerId);
    if (inTournament?.name) return inTournament.name;
    const inAll = players.find((p: any) => p.id === playerId);
    return inAll?.name || '';
  };

  const getCurrentGeneratedMatchLabel = (): string => {
    const sideA = [getPlayerNameById(reservationForm.player1Id), getPlayerNameById(reservationForm.player3Id)]
      .filter(Boolean)
      .join(' / ') || 'TBD';
    const sideB = [getPlayerNameById(reservationForm.player2Id), getPlayerNameById(reservationForm.player4Id)]
      .filter(Boolean)
      .join(' / ') || 'TBD';
    const code = reservationForm.matchCode?.trim() || 'Текущий матч';
    return `${code}: ${sideA} vs ${sideB}`;
  };

  const formatReservationMatchCodeLabel = (reservation: CourtReservation): string => {
    const rawMatchCode = String(reservation.match?.matchCode || reservation.matchCode || '').trim();
    if (!rawMatchCode) return '';

    const level = String(reservation.match?.level || '').trim();
    const stage = extractPlayoffStageShort(reservation.match?.round?.name);
    const stageNumber = Number(reservation.match?.stageMatchNumber || 0);
    const isPlayoff = /playoff|плей-офф/i.test(rawMatchCode);

    if (isPlayoff && level && stage && stageNumber > 0) {
      return `${level} • ${stage} • ${stageNumber}`;
    }

    // Для групповых матчей вида "(C)" показываем уровень слева: "(S-6 • C)".
    const groupCodeMatch = rawMatchCode.match(/^\(([^)]+)\)$/);
    if (!isPlayoff && level && groupCodeMatch?.[1]) {
      const groupCode = String(groupCodeMatch[1]).trim();
      if (groupCode) {
        return `(${level} • ${groupCode})`;
      }
    }

    return rawMatchCode;
  };

  const getShortPlayerName = (fullName?: string | null): string => {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return parts[0] || '';
    return parts.slice(1).join(' ');
  };

  const getPendingSourceReference = (
    sourceMatch: NonNullable<NonNullable<CourtReservation['match']>['previousMatches']>[number],
    fallbackLevel?: string | null
  ): string => {
    const sourceLevel = String(sourceMatch.level || fallbackLevel || '').trim() || '—';
    const sourceStage = extractPlayoffStageShort(sourceMatch.round?.name) || '—';
    const sourceNumber = Number(sourceMatch.stageMatchNumber || 0);
    return `${sourceLevel} • ${sourceStage} • ${sourceNumber > 0 ? sourceNumber : '—'}`;
  };

  const renderReservationPlayers = (reservation: CourtReservation): React.ReactNode => {
    const leftNames = [reservation.player1?.name, reservation.player3?.name].filter(Boolean) as string[];
    const rightNames = [reservation.player2?.name, reservation.player4?.name].filter(Boolean) as string[];
    const leftLabel = leftNames.join(' / ') || 'X';
    const rightLabel = rightNames.join(' / ') || 'X';
    const isDoubles = leftNames.length > 1 || rightNames.length > 1;

    const previousMatches = reservation.match?.previousMatches || [];
    const leftSource = previousMatches.find((m) => String(m.nextMatchSlot || '').toLowerCase() === 'home') || null;
    const rightSource = previousMatches.find((m) => String(m.nextMatchSlot || '').toLowerCase() === 'away') || null;

    const isLeftPending = isDoubles
      ? leftNames.length < 2 && !!leftSource
      : leftNames.length < 1 && !!leftSource;
    const isRightPending = isDoubles
      ? rightNames.length < 2 && !!rightSource
      : rightNames.length < 1 && !!rightSource;

    const renderPendingSide = (
      sourceMatch: NonNullable<NonNullable<CourtReservation['match']>['previousMatches']>[number]
    ): React.ReactNode => {
      if (isDoubles) {
        const teamA = [sourceMatch.player1?.name, sourceMatch.player3?.name].filter(Boolean) as string[];
        const teamB = [sourceMatch.player2?.name, sourceMatch.player4?.name].filter(Boolean) as string[];
        if (teamA.length < 2 || teamB.length < 2) {
          return (
            <span className="inline-block rounded border border-white px-1 py-[1px] leading-tight">
              {getPendingSourceReference(sourceMatch, reservation.match?.level)}
            </span>
          );
        }
        return (
        <span className="inline-flex flex-col rounded border border-white px-1 py-[1px] leading-tight">
            <span>{getShortPlayerName(teamA.join(' / '))}</span>
            <span>{getShortPlayerName(teamB.join(' / '))}</span>
          </span>
        );
      }

      const p1 = sourceMatch.player1?.name || '';
      const p2 = sourceMatch.player2?.name || '';
      if (!p1 || !p2) {
        return (
          <span className="inline-block rounded border border-white px-1 py-[1px] leading-tight">
            {getPendingSourceReference(sourceMatch, reservation.match?.level)}
          </span>
        );
      }

      return (
        <span className="inline-flex flex-col rounded border border-white px-1 py-[1px] leading-tight">
          <span>{getShortPlayerName(p1)}</span>
          <span>{getShortPlayerName(p2)}</span>
        </span>
      );
    };

    const leftNode = isLeftPending && leftSource
      ? renderPendingSide(leftSource)
      : <span>{leftLabel}</span>;
    const rightNode = isRightPending && rightSource
      ? renderPendingSide(rightSource)
      : <span>{rightLabel}</span>;

    return (
      <span className="inline-flex items-center gap-1">
        {leftNode}
        <span>/</span>
        {rightNode}
      </span>
    );
  };

  const applyGeneratedMatch = (matchId: string) => {
    setSelectedGeneratedMatchId(matchId);
    if (matchId === CURRENT_GENERATED_MATCH_VALUE) return;
    if (!matchId) {
      setSelectedGeneratedMatchDbId(null);
      return;
    }
    const selected = unreservedMatches.find((m) => m.id === matchId);
    if (!selected) {
      setSelectedGeneratedMatchDbId(null);
      return;
    }
    setSelectedGeneratedMatchDbId(selected.id);

    setReservationForm((prev) => ({
      ...prev,
      player1Id: selected.player1Id || '',
      player2Id: selected.player2Id || '',
      player3Id: selected.player3Id || '',
      player4Id: selected.player4Id || '',
      matchCode: selected.matchCode || ''
    }));
  };

  const handleOpenReservationModal = (courtId: string, time: string, date: string) => {
    if (!reservationMode) return;
    setSelectedCourtForReservation(courtId);
    setSelectedTimeForReservation(time);
    setNotifyTelegramGroupOnCreate(false);
    setReservationForm({
      ...reservationForm,
      date: date,
      startTime: time,
      courtId: courtId
    });
    setIsReservationModalOpen(true);
  };

  const handleCloseReservationModal = () => {
    setIsReservationModalOpen(false);
    setSelectedGeneratedMatchId('');
    setSelectedGeneratedMatchDbId(null);
    setNotifyTelegramGroupOnCreate(false);
    setReservationForm({
      duration: 2.0,
      tournamentId: '',
      player1Id: '',
      player2Id: '',
      player3Id: '',
      player4Id: '',
      matchCode: '',
      price: 0,
      date: '',
      startTime: '',
      endTime: '',
      courtId: ''
    });
  };

  const applyReservationToEditForm = (reservation: CourtReservation) => {
    setEditingReservation(reservation);
    setSelectedGeneratedMatchDbId(reservation.match?.id || null);
    const isLinkedReservation = Boolean(reservation.hasLinkedMatch || reservation.match?.id);
    const hasExplicitGeneratedMatch = typeof reservation.matchCode === 'string' && reservation.matchCode.startsWith('Playoff-');
    setSelectedGeneratedMatchId(isLinkedReservation || hasExplicitGeneratedMatch ? CURRENT_GENERATED_MATCH_VALUE : '');
    setReservationForm({
      tournamentId: reservation.tournament.id,
      player1Id: reservation.player1?.id || '',
      player2Id: reservation.player2?.id || '',
      player3Id: reservation.player3?.id || '',
      player4Id: reservation.player4?.id || '',
      matchCode: reservation.matchCode || '',
      price: reservation.price || 0,
      date: reservation.date.split('T')[0], // Извлекаем дату из ISO строки
      startTime: reservation.startTime,
      endTime: reservation.endTime,
      courtId: reservation.court.id
    });
    fetchTournamentPlayers(reservation.tournament.id);
  };

  const handleOpenEditModal = async (reservation: CourtReservation) => {
    applyReservationToEditForm(reservation);
    setIsEditModalOpen(true);

    try {
      const detailedReservation = await apiGet<CourtReservation>(`/court-reservations/${reservation.id}`);
      if (detailedReservation && detailedReservation.id === reservation.id) {
        applyReservationToEditForm(detailedReservation);
      }
    } catch (error) {
      console.error('Ошибка загрузки детального резерва:', error);
    }
  };

  const handleCreateServicePurchase = async (date: string) => {
    if (!selectedClubId) {
      alert('Пожалуйста, выберите клуб.');
      return;
    }

    const dayReservations = reservations.filter(
      (res) => {
        // Находим корт по courtId и получаем его clubId
        const court = courts.find(c => c.id === res.courtId);
        const courtClubId = court?.clubId;
        
        const matchesClub = courtClubId === selectedClubId;
        const matchesDate = new Date(res.date).toDateString() === new Date(date).toDateString();
        
        return matchesClub && matchesDate;
      }
    );

    if (dayReservations.length === 0) {
      alert('На этот день нет резервов для создания закупки услуг.');
      return;
    }

    const tournamentDetailsCache = new Map<string, any>();

    const resolveReservationRatePerHour = async (reservation: CourtReservation): Promise<number> => {
      const directRate = Number(reservation.price);
      if (Number.isFinite(directRate) && directRate > 0) {
        return directRate;
      }

      const tournamentId = reservation.tournament?.id;
      if (!tournamentId) return 0;

      const listedTournament = tournaments.find((t) => t.id === tournamentId);
      const listedTournamentRate = Number(getCourtRentalPriceForTournament(listedTournament));
      if (Number.isFinite(listedTournamentRate) && listedTournamentRate > 0) {
        return listedTournamentRate;
      }

      let detailedTournament = tournamentDetailsCache.get(tournamentId);
      if (!detailedTournament) {
        try {
          detailedTournament = await apiGet<any>(`/tournaments/${tournamentId}`);
          tournamentDetailsCache.set(tournamentId, detailedTournament);
        } catch (error) {
          console.error(`Ошибка загрузки тарифа турнира ${tournamentId}:`, error);
          return 0;
        }
      }

      const detailedTournamentRate = Number(getCourtRentalPriceForTournament(detailedTournament));
      return Number.isFinite(detailedTournamentRate) ? detailedTournamentRate : 0;
    };

    const servicePurchaseItems = await Promise.all(dayReservations.map(async (res) => {
          const startTimeIndex = timeSlots.indexOf(res.startTime);
          const endTimeIndex = timeSlots.indexOf(res.endTime);
          const durationSlots = endTimeIndex - startTimeIndex;
          const durationHours = durationSlots * 0.5; // Каждый слот = 30 минут
          const ratePerHour = await resolveReservationRatePerHour(res);

          return {
            tournamentId: res.tournament.id,
            courtId: res.court.id,
            startTime: res.startTime,
            endTime: res.endTime,
            hours: durationHours,
            ratePerHour,
            amount: ratePerHour * durationHours,
            comment: '' // Не заполняем комментарий автоматически
          };
        }));

        // Находим поставщика для выбранного клуба
        const club = clubs.find(c => c.id === selectedClubId);
        let supplier = null;
        
        if (club) {
          // Получаем поставщиков для этого клуба
          try {
            const suppliersResponse = await apiGet<{ suppliers?: any[] } | any[]>(`/suppliers?clubId=${club.id}&isActive=true`);
            
            console.log('Загружены поставщики для клуба:', club.name, suppliersResponse);
            
            // API возвращает { suppliers: [...], pagination: {...} }
            const suppliersData = suppliersResponse.suppliers || suppliersResponse;
            
            if (suppliersData.length > 0) {
              // Берем первого активного поставщика
              const clubSupplier = suppliersData[0];
              supplier = {
                id: clubSupplier.id,
                name: clubSupplier.name,
                clubId: club.id
              };
              console.log('Выбран поставщик:', supplier);
            } else {
              // Если поставщик не найден, используем имя клуба
              supplier = {
                id: '', // Будет найден в форме по имени
                name: club.name,
                clubId: club.id
              };
              console.log('Поставщик не найден, используем имя клуба:', supplier);
            }
          } catch (error) {
            console.error('Ошибка загрузки поставщиков:', error);
            // Fallback к имени клуба
            supplier = {
              id: '', // Будет найден в форме по имени
              name: club.name,
              clubId: club.id
            };
            console.log('Ошибка загрузки, используем имя клуба:', supplier);
          }
        }

        const servicePurchaseData = {
          date: date,
          clubId: selectedClubId,
          supplier: supplier,
          items: servicePurchaseItems,
        };

    sessionStorage.setItem('newServicePurchaseData', JSON.stringify(servicePurchaseData));
    navigate('/service-purchases/new');
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingReservation(null);
    setSelectedGeneratedMatchId('');
    setSelectedGeneratedMatchDbId(null);
    setReservationForm({
      duration: 2.0,
      tournamentId: '',
      player1Id: '',
      player2Id: '',
      player3Id: '',
      player4Id: '',
      matchCode: '',
      price: 0,
      date: '',
      startTime: '',
      endTime: '',
      courtId: ''
    });
  };

  const handleUpdateReservation = async () => {
    if (!editingReservation) return;

    try {
      const selectedTournament = tournaments.find(t => t.id === reservationForm.tournamentId);
      if (!selectedTournament) {
        alert('Выберите турнир.');
        return;
      }

      const updateData = {
        courtId: reservationForm.courtId,
        date: reservationForm.date,
        startTime: reservationForm.startTime,
        endTime: reservationForm.endTime,
        tournamentId: reservationForm.tournamentId,
        player1Id: reservationForm.player1Id || null,
        player2Id: reservationForm.player2Id || null,
        player3Id: reservationForm.player3Id || null,
        player4Id: reservationForm.player4Id || null,
        matchCode: reservationForm.matchCode || null,
        generatedMatchId: selectedGeneratedMatchDbId,
        price: reservationForm.price,
        status: editingReservation.status
      };

      await apiPut(`/court-reservations/${editingReservation.id}`, updateData);
      await fetchReservations();
      handleCloseEditModal();
    } catch (error: any) {
      console.error('Ошибка обновления резервирования:', error);
      alert(error.message || 'Ошибка обновления резервирования');
    }
  };

  const handleDeleteReservation = async () => {
    if (!editingReservation) return;

    if (!confirm('Вы уверены, что хотите удалить это резервирование?')) {
      return;
    }

    try {
      await apiDelete(`/court-reservations/${editingReservation.id}`);
      await fetchReservations();
      handleCloseEditModal();
    } catch (error: any) {
      console.error('Ошибка удаления резервирования:', error);
      alert(error.message || 'Ошибка удаления резервирования');
    }
  };

  const handleQuickDeleteReservation = async (reservationId: string) => {
    try {
      await apiDelete(`/court-reservations/${reservationId}`);
      await fetchReservations();
      if (showGamesPicker && selectedTournamentForGamesPicker) {
        await fetchGamesForPicker(selectedTournamentForGamesPicker);
      }
    } catch (error: any) {
      console.error('Ошибка быстрого удаления резервирования:', error);
      alert(error.message || 'Ошибка удаления резервирования');
    }
  };

  const handleCreateReservation = async () => {
    try {
      const court = courts.find(c => c.id === selectedCourtForReservation);
      if (!court) return;
      const selectedTournament = tournaments.find(t => t.id === reservationForm.tournamentId);
      if (!selectedTournament) {
        alert('Выберите турнир.');
        return;
      }

      const startTimeIndex = timeSlots.indexOf(selectedTimeForReservation);
      const durationSlots = getDurationSlotsByHours(reservationForm.duration);
      // endTime должен быть началом следующего слота после последнего
      const endTime = timeSlots[startTimeIndex + durationSlots] || '23:30';
      
      const reservationData = {
        tournamentId: reservationForm.tournamentId,
        courtId: reservationForm.courtId || selectedCourtForReservation,
        date: reservationForm.date || selectedDateFrom,
        startTime: reservationForm.startTime || selectedTimeForReservation,
        endTime: reservationForm.endTime || endTime,
        player1Id: reservationForm.player1Id || null,
        player2Id: reservationForm.player2Id || null,
        player3Id: reservationForm.player3Id || null,
        player4Id: reservationForm.player4Id || null,
        matchCode: reservationForm.matchCode || null,
        generatedMatchId: selectedGeneratedMatchDbId,
        price: reservationForm.price,
        status: 'RESERVED',
        notifyTelegramGroup: notifyTelegramGroupOnCreate
      };
      
      console.log('Creating reservation:', reservationData);
      
      await apiPost('/court-reservations', reservationData);
      handleCloseReservationModal();
      await fetchReservations();
    } catch (error: any) {
      console.error('Ошибка создания резервирования:', error);
      alert(error.message || 'Ошибка создания резервирования');
    }
  };

  const getCourtRentalPriceForTournament = (tournament: any): number => {
    if (!tournament) return 0;
    if (selectedClubId) {
      const tournamentClub = tournament.tournamentClubs?.find(
        (tc: any) => tc.clubId === selectedClubId
      );
      return tournamentClub?.courtRentalPrice || tournament?.courtRentalPrice || 0;
    }
    return tournament?.courtRentalPrice || 0;
  };

  const handleCreateReservationFromDraggedMatch = async (
    match: UnreservedMatchOption,
    courtId: string,
    startTime: string,
    date: string
  ) => {
    try {
      const matchStatus = String(match.status || '').toUpperCase();
      const canCreateAdditionalReservation = Boolean(
        match.reservationId && (matchStatus === 'IN_PROGRESS' || match.isSuspended)
      );

      if (match.reservationId && !canCreateAdditionalReservation) {
        await handleMoveReservation(match.reservationId, courtId, startTime, date);
        if (showGamesPicker && selectedTournamentForGamesPicker) {
          await fetchGamesForPicker(selectedTournamentForGamesPicker);
        }
        return;
      }

      const startTimeIndex = timeSlots.indexOf(startTime);
      if (startTimeIndex < 0) return;

      const durationSlots = getDurationSlotsByHours(gamesPickerDuration);
      const endTime = timeSlots[startTimeIndex + durationSlots] || '23:30';
      const tournament = tournaments.find(t => t.id === match.tournamentId);

      await apiPost('/court-reservations', {
        tournamentId: match.tournamentId,
        courtId,
        date,
        startTime,
        endTime,
        player1Id: match.player1Id || null,
        player2Id: match.player2Id || null,
        player3Id: match.player3Id || null,
        player4Id: match.player4Id || null,
        matchCode: match.matchCode || null,
        generatedMatchId: match.id,
        price: getCourtRentalPriceForTournament(tournament),
        status: 'RESERVED'
      });

      await fetchReservations();
      if (showGamesPicker && selectedTournamentForGamesPicker) {
        await fetchGamesForPicker(selectedTournamentForGamesPicker);
      }
    } catch (error: any) {
      console.error('Ошибка создания резерва перетаскиванием игры:', error);
      alert(error.message || 'Не удалось создать резерв для выбранной игры');
    }
  };

  const handleMoveReservation = async (
    reservationId: string,
    newCourtId: string,
    newStartTime: string,
    newDate?: string
  ) => {
    try {
      const reservation = reservations.find(r => r.id === reservationId);
      if (!reservation) {
        console.error('Reservation not found:', reservationId);
        return;
      }

      console.log('Moving reservation:', {
        id: reservationId,
        from: { courtId: reservation.court.id, startTime: reservation.startTime },
        to: { courtId: newCourtId, startTime: newStartTime }
      });

      const startTimeIndex = timeSlots.indexOf(newStartTime);
      const durationSlots = getReservationDuration(reservation);
      const endTime = timeSlots[startTimeIndex + durationSlots] || '23:30';

      await apiPut(`/court-reservations/${reservationId}`, {
        courtId: newCourtId,
        date: newDate || reservation.date,
        startTime: newStartTime,
        endTime
      });

      console.log('Reservation moved successfully');
      await fetchReservations();
    } catch (error: any) {
      console.error('Ошибка обновления резервирования:', error);
      alert(error.message || 'Ошибка при перемещении резервирования');
    }
  };

  // Получаем корты выбранного клуба
  const getClubCourts = () => {
    if (!selectedClubId) return courts;
    return courts.filter(c => c.clubId === selectedClubId);
  };

  // Проверяем доступность корта в указанное время
  const getAvailability = (courtId: string, time: string, date: string): boolean => {
    const availability = availabilities.find(
      a => a.court.id === courtId && a.startTime === time && a.date.split('T')[0] === date
    );
    return availability ? availability.isAvailable : false;
  };

  // Проверяем, является ли слот публичным предложением
  const isPublicSlot = (courtId: string, time: string, date: string): boolean => {
    const availability = availabilities.find(
      a => a.court.id === courtId && a.startTime === time && a.date.split('T')[0] === date
    );
    return availability ? (availability.isPublic === true) : false;
  };

  // Проверяем резервирование корта в указанное время
  const getReservation = (courtId: string, time: string, date: string): CourtReservation | undefined => {
    return reservations.find(r => {
      if (r.court.id !== courtId) return false;
      if (r.date.split('T')[0] !== date) return false;
      // Проверяем, находится ли time в диапазоне [startTime, endTime)
      return time >= r.startTime && time < r.endTime;
    });
  };

  // Получаем количество получасовых блоков для резервирования
  const getReservationDuration = (reservation: CourtReservation): number => {
    const startMinutes = parseInt(reservation.startTime.split(':')[0]) * 60 + parseInt(reservation.startTime.split(':')[1]);
    const endMinutes = parseInt(reservation.endTime.split(':')[0]) * 60 + parseInt(reservation.endTime.split(':')[1]);
    const durationMinutes = endMinutes - startMinutes;
    return Math.round(durationMinutes / 30); // Количество получасовых блоков
  };
  
  // Проверяем, является ли ячейка началом резервирования
  const isReservationStart = (reservation: CourtReservation, time: string): boolean => {
    return reservation.startTime === time;
  };

  const getReservationColor = (status: string): string => {
    switch (status) {
      case 'RESERVED': return 'bg-orange-200';
      case 'PLAYED': return 'bg-green-200';
      case 'CANCELLED': return 'bg-gray-200';
      default: return 'bg-gray-200';
    }
  };

  // Обработчик клика/выделения по ячейке для режима "Доступность"
  const handleCellClick = async (courtId: string, time: string, date: string) => {
    if (!availabilityMode) return;
    
    // Проверяем, не повторяем ли одну и ту же ячейку
    if (lastMarkedCell && lastMarkedCell.courtId === courtId && lastMarkedCell.time === time) {
      return;
    }
    
    setLastMarkedCell({ courtId, time });

    try {
      // Проверяем, есть ли уже запись о доступности
      const existing = availabilities.find(
        a => a.court.id === courtId && a.startTime === time && a.date.split('T')[0] === date
      );

      if (existing) {
        // Переключаем статус доступности
        const newAvailability = !existing.isAvailable;
        await apiPut(`/court-availability/${existing.id}`, { isAvailable: newAvailability });
      } else {
        // Создаем новую запись
        const court = courts.find(c => c.id === courtId);
        if (court) {
          await apiPost('/court-availability', {
              clubId: court.clubId,
              courtId,
              date,
              startTime: time,
              endTime: timeSlots[timeSlots.indexOf(time) + 1] || '23:00',
              isAvailable: true,
            });
        }
      }

      // Обновляем данные
      await fetchAvailabilities();
    } catch (error) {
      console.error('Ошибка обновления доступности:', error);
    }
  };

  const handleMouseDown = (courtId: string, time: string, date: string) => {
    if (!availabilityMode) return;
    setIsMouseDown(true);
    setLastMarkedCell(null);
    handleCellClick(courtId, time, date).catch(err => console.error('Ошибка в handleCellClick:', err));
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    setLastMarkedCell(null);
  };

  const handleMouseEnter = (courtId: string, time: string, date: string) => {
    if (!availabilityMode || !isMouseDown) return;
    handleCellClick(courtId, time, date).catch(err => console.error('Ошибка в handleCellClick:', err));
  };

  // Получение доступных периодов по кортам
  const handleShowAvailablePeriods = async () => {
    console.log('=== handleShowAvailablePeriods called ===');
    console.log('selectedDateFrom:', selectedDateFrom);
    console.log('selectedDateTo:', selectedDateTo);
    console.log('selectedClubId:', selectedClubId);
    
    if (!selectedDateFrom || !selectedDateTo) {
      alert('Выберите период (дата от и дата до)');
      return;
    }

    const params = {
      startDate: selectedDateFrom,
      endDate: selectedDateTo,
      clubId: selectedClubId || undefined
    };

    console.log('Request params:', params);
    console.log('Sending request to /court-availability/available-periods');

    setRequestParams(params);
    setGeneratingSlots(true);
    try {
      const response = await apiPost<{ periodsByCourt: any[]; summary: any }>('/court-availability/available-periods', params);
      
      console.log('Response received:', response);
      console.log('Periods by court:', response.periodsByCourt);
      console.log('Summary:', response.summary);

      setAvailablePeriods(response);
      setShowPeriodsModal(true);
    } catch (error: any) {
      console.error('Ошибка получения доступных периодов:', error);
      console.error('Error response:', error.response?.data);
      alert('Ошибка получения доступных периодов: ' + (error.response?.data?.error || error.message));
    } finally {
      setGeneratingSlots(false);
    }
  };

  // Генерация публичных предложений слотов
  const handleGenerateSlots = async () => {
    if (!selectedDateFrom || !selectedDateTo) {
      alert('Выберите период (дата от и дата до)');
      return;
    }

    setGeneratingSlots(true);
    try {
      const response = await apiPost<{ slots: any[] }>('/court-availability/generate-slots', {
        startDate: selectedDateFrom,
        endDate: selectedDateTo,
        clubId: selectedClubId || undefined
      });

      const slotsWithChecked = (response.slots || []).map(slot => ({
        ...slot,
        checked: false // По умолчанию все сняты
      }));

      setGeneratedSlots(slotsWithChecked);
      setShowSlotsModal(true);
    } catch (error: any) {
      console.error('Ошибка генерации слотов:', error);
      alert('Ошибка генерации слотов: ' + (error.response?.data?.error || error.message));
    } finally {
      setGeneratingSlots(false);
    }
  };

  // Отправка выбранных слотов
  const handleSendSlots = async () => {
    const selectedSlots = generatedSlots.filter(s => s.checked);
    
    console.log('\n=== handleSendSlots ===');
    console.log('Total generated slots:', generatedSlots.length);
    console.log('Checked slots:', generatedSlots.filter(s => s.checked).length);
    console.log('Unchecked slots:', generatedSlots.filter(s => !s.checked).length);
    console.log('Selected slots to send:', selectedSlots.length);
    console.log('Selected slots details:', selectedSlots.map(s => `${s.date} ${s.startTime}-${s.endTime} ${s.courtName}`));
    console.log('Unchecked slots details:', generatedSlots.filter(s => !s.checked).map(s => `${s.date} ${s.startTime}-${s.endTime} ${s.courtName}`));
    
    if (selectedSlots.length === 0) {
      alert('Выберите хотя бы один слот для отправки');
      return;
    }

    if (!confirm(`Создать ${selectedSlots.length} публичных предложений слотов?`)) {
      return;
    }

    try {
      const items = selectedSlots.map(slot => ({
        clubId: slot.clubId,
        courtId: slot.courtId,
        date: slot.date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        isAvailable: true,
        // Добавляем привязку к турниру (опционально)
        tournamentId: selectedTournamentForProposal || null,
        level: selectedLevelForProposal || null,
        groupId: selectedGroupForProposal || null
      }));

      console.log('Sending items to /bulk:', items.length);
      console.log('Items:', items.map(i => `${i.date} ${i.startTime}-${i.endTime} court: ${i.courtId}`));

      const response = await apiPost<{ count: number }>('/court-availability/bulk', { items });
      
      alert(`Создано ${response.count} публичных предложений слотов`);
      setShowSlotsModal(false);
      setGeneratedSlots([]);
      
      // Обновляем доступность и предложения
      fetchAvailabilities();
      if (activeTab === 'proposals') {
        fetchPublicProposals();
      }
    } catch (error: any) {
      console.error('Ошибка отправки слотов:', error);
      alert('Ошибка отправки слотов: ' + (error.response?.data?.error || error.message));
    }
  };

  const getFilteredReadyGroups = (slot: SlotPlayerAvailability, playerFilter?: string) => {
    let groups = slot.readyPlayers;

    if (availabilityTournamentId) {
      groups = groups.filter(group => group.tournamentId === availabilityTournamentId);
    }
    if (availabilityGroupId) {
      groups = groups.filter(group => group.groupId === availabilityGroupId);
    }
    if (playerFilter) {
      groups = groups
        .map(group => ({
          ...group,
          players: group.players.filter(player => player.playerName === playerFilter)
        }))
        .filter(group => group.players.length > 0);
    }

    return groups;
  };

  const availablePlayerFilterOptions = Array.from(
    new Set(
      playerSlotAvailability
        .flatMap(slot => getFilteredReadyGroups(slot).flatMap(group => group.players.map(player => player.playerName)))
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, 'ru'));

  const filteredPlayerSlotAvailability = playerSlotAvailability
    .map((slot) => {
      const filteredReadyPlayers = getFilteredReadyGroups(slot, availabilityPlayerName);

      if (slot.assignedMatch) {
        if (availabilityPlayerName) {
          return null;
        }
        if (availabilityTournamentId && slot.assignedMatch.tournamentId !== availabilityTournamentId) {
          return null;
        }
        if (availabilityGroupId && slot.assignedMatch.groupId !== availabilityGroupId) {
          return null;
        }
        return slot;
      }

      if (filteredReadyPlayers.length === 0) {
        return null;
      }

      return {
        ...slot,
        readyPlayers: filteredReadyPlayers
      };
    })
    .filter((slot): slot is SlotPlayerAvailability => Boolean(slot));

  const playerAvailabilityByDate = filteredPlayerSlotAvailability.reduce<Record<string, SlotPlayerAvailability[]>>((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const renderAvailabilityPlayers = (
    groupedPlayers: Array<{
      label: string;
      players: Array<{
        playerName: string;
        hasCurrentDateMatch?: boolean;
        line: string;
        previousMatchDate?: string;
        previousMatchOpponent?: string;
        previousMatchTournament?: string;
        previousMatchTooltip?: string;
        noGamesPreviousInfo?: string;
        nextMatchDate?: string;
        nextMatchOpponent?: string;
        nextMatchTournament?: string;
        nextMatchTooltip?: string;
        noGamesNextInfo?: string;
        opponentsDetailed?: Array<{
          id?: string;
          responseId?: string;
          name: string;
          availabilityStatus?: string;
          hasCurrentDateMatch?: boolean;
          prev: string;
          next: string;
          notificationSentAt?: string;
          notificationSentAtRaw?: string;
          reactionAt?: string;
          reactionAtRaw?: string;
          reactionUnviewed?: boolean;
          prevTooltip?: string;
          nextTooltip?: string;
        }>;
      }>;
    }>,
    proposalId: string
  ) => {
    const totalPlayers = groupedPlayers.reduce((sum, group) => sum + group.players.length, 0);
    const getAvailabilityStatusMeta = (status?: string) => {
      switch (status) {
        case 'READY':
          return { icon: '✅', label: 'Доступен', className: 'text-green-600' };
        case 'THINKING':
          return { icon: '🤔', label: 'Думает', className: 'text-yellow-600' };
        case 'REJECTED':
          return { icon: '❌', label: 'Отказ', className: 'text-red-600' };
        case 'SENT':
          return { icon: '📨', label: 'Приглашение отправлено', className: 'text-blue-600' };
        default:
          return { icon: '⏺', label: 'Нет ответа', className: 'text-gray-400' };
      }
    };

    if (totalPlayers === 0) {
      return <span className="text-gray-400">—</span>;
    }

    return (
      <div className="text-left">
        <div className="space-y-2">
          {groupedPlayers.map((group, index) => (
            <div
              key={`${group.label}-${index}`}
              className={`text-sm text-gray-800 leading-4 text-left ${index > 0 ? 'border-t-2 border-gray-300 pt-2 mt-2' : ''}`}
            >
              <div className="text-xs text-gray-500 text-center">{group.label}</div>
              <div>
                {group.players.map((player, playerIndex) => (
                  <div
                    key={`${group.label}-${player.line}-${playerIndex}`}
                    className={playerIndex > 0 ? 'border-t border-gray-300 mt-1 pt-1' : ''}
                  >
                    <div className="grid grid-cols-[220px_1fr] gap-3 items-stretch">
                      <div className="flex flex-col justify-center">
                        {player.previousMatchDate && (
                          <div
                            className={`text-[10px] leading-3 ${player.hasCurrentDateMatch ? 'text-gray-300' : 'text-gray-500'}`}
                            title={player.previousMatchTooltip || ''}
                          >
                            <div>{player.previousMatchDate}{player.previousMatchOpponent ? ` • ${player.previousMatchOpponent}` : ''}</div>
                            {player.previousMatchTournament && (
                              <div>{player.previousMatchTournament}</div>
                            )}
                          </div>
                        )}
                        <div className={`font-medium ${player.hasCurrentDateMatch ? 'text-gray-300' : ''}`}>{player.playerName}</div>
                        {player.nextMatchDate && (
                          <div
                            className={`text-[10px] leading-3 ${player.hasCurrentDateMatch ? 'text-gray-300' : 'text-gray-500'}`}
                            title={player.nextMatchTooltip || ''}
                          >
                            <div>{player.nextMatchDate}{player.nextMatchOpponent ? ` • ${player.nextMatchOpponent}` : ''}</div>
                            {player.nextMatchTournament && (
                              <div>{player.nextMatchTournament}</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {player.opponentsDetailed && player.opponentsDetailed.length > 0 ? (
                          player.opponentsDetailed.map((opponent, idx) => (
                            <div key={`${player.playerName}-opponent-${idx}`} className="grid grid-cols-[72px_72px_18px_1fr_130px_130px] gap-1 items-center">
                              <div className={`text-[10px] leading-3 ${(opponent.hasCurrentDateMatch || opponent.availabilityStatus === 'REJECTED') ? 'text-gray-300' : 'text-gray-500'}`} title={opponent.prevTooltip || ''}>
                                {opponent.prev}
                              </div>
                              <div className={`text-[10px] leading-3 ${(opponent.hasCurrentDateMatch || opponent.availabilityStatus === 'REJECTED') ? 'text-gray-300' : 'text-gray-500'}`} title={opponent.nextTooltip || ''}>
                                {opponent.next}
                              </div>
                              <div
                                className="text-center"
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!opponent.id) return;
                                    setAvailabilityStatusTarget({
                                      proposalId,
                                      playerId: opponent.id,
                                      playerName: opponent.name,
                                      currentStatus: opponent.availabilityStatus
                                    });
                                    setShowAvailabilityStatusModal(true);
                                  }}
                                  className={`text-xs leading-3 ${getAvailabilityStatusMeta(opponent.availabilityStatus).className} hover:opacity-80`}
                                  title={`Изменить статус: ${getAvailabilityStatusMeta(opponent.availabilityStatus).label}`}
                                  disabled={!opponent.id}
                                >
                                  {getAvailabilityStatusMeta(opponent.availabilityStatus).icon}
                                </button>
                              </div>
                              <div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!opponent.id) return;
                                    setAvailabilityInviteTarget({
                                      proposalId,
                                      playerId: opponent.id,
                                      playerName: opponent.name
                                    });
                                    setShowAvailabilityInviteModal(true);
                                  }}
                                  className={`${(opponent.hasCurrentDateMatch || opponent.availabilityStatus === 'REJECTED') ? 'text-gray-300' : ''} hover:underline text-left disabled:cursor-not-allowed`}
                                  title="Отправить персональное оповещение"
                                  disabled={!opponent.id}
                                >
                                  {opponent.name}
                                </button>
                              </div>
                              <div
                                className={`text-[10px] leading-3 text-right ${(opponent.hasCurrentDateMatch || opponent.availabilityStatus === 'REJECTED') ? 'text-gray-300' : 'text-gray-500'}`}
                                title={opponent.notificationSentAtRaw ? `Отправлено: ${new Date(opponent.notificationSentAtRaw).toLocaleString('ru-RU')}` : ''}
                              >
                                {opponent.notificationSentAt || '—'}
                              </div>
                              <div className="text-right">
                                {opponent.responseId ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReactionViewedTarget({
                                        responseId: opponent.responseId!,
                                        playerName: opponent.name,
                                        viewed: Boolean(opponent.reactionUnviewed)
                                      });
                                      setShowReactionViewedModal(true);
                                    }}
                                  className={`text-[10px] leading-3 inline-block rounded px-1 ${
                                    opponent.reactionUnviewed
                                      ? 'bg-yellow-100 ring-1 ring-yellow-400 text-yellow-800'
                                      : ((opponent.hasCurrentDateMatch || opponent.availabilityStatus === 'REJECTED') ? 'text-gray-300' : 'text-gray-500')
                                  }`}
                                  title={opponent.reactionAtRaw ? `Реакция: ${new Date(opponent.reactionAtRaw).toLocaleString('ru-RU')}` : 'Изменить признак просмотра'}
                                >
                                  {opponent.reactionAt || '—'}
                                </button>
                                ) : (
                                <span
                                  className={`text-[10px] leading-3 inline-block rounded px-1 ${
                                    opponent.reactionUnviewed
                                      ? 'bg-yellow-100 ring-1 ring-yellow-400 text-yellow-800'
                                      : ((opponent.hasCurrentDateMatch || opponent.availabilityStatus === 'REJECTED') ? 'text-gray-300' : 'text-gray-500')
                                  }`}
                                >
                                  {opponent.reactionAt || '—'}
                                </span>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const isLinkedReservation = Boolean(editingReservation?.hasLinkedMatch || editingReservation?.match?.id);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Резервирование кортов</h1>

      {/* Вкладки */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-1">
        <button
          onClick={() => setActiveTab('calendar')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'calendar'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          📅 Календарь
        </button>
        <button
          onClick={() => setActiveTab('reservations')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'reservations'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          🎾 Резервы
        </button>
        <button
          onClick={() => setActiveTab('proposals')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'proposals'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          💬 Предложения
        </button>
        <button
          onClick={() => {
            setActiveTab('opponent-matching');
            fetchOpponentMatchingTasks();
          }}
          className={`px-4 py-2 font-medium ${
            activeTab === 'opponent-matching'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          👥 Подбор соперников
        </button>
        <button
          onClick={() => setActiveTab('player-availability')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'player-availability'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          🕓 Доступность игроков
        </button>
      </div>

      {/* Фильтры */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Клуб
          </label>
          <select
            value={selectedClubId}
            onChange={(e) => setSelectedClubId(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">Выберите клуб</option>
            {clubs.map(club => (
              <option key={club.id} value={club.id}>{club.name}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Дата с
            </label>
            <input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => setSelectedDateFrom(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-40"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Дата по
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={selectedDateTo}
                onChange={(e) => setSelectedDateTo(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 w-40"
              />
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Сброс
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Кнопки режимов - фиксированные */}
      {activeTab === 'calendar' && selectedClubId && (
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm mb-4">
          <div className="px-4 py-2 flex gap-2 items-center flex-nowrap overflow-x-auto">
            <button
              onClick={() => {
                setAvailabilityMode(!availabilityMode);
                setReservationMode(false);
              }}
              className={`px-2 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                availabilityMode
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {availabilityMode ? '✅ Доступность ВКЛ' : '⚪ Доступность ВЫКЛ'}
            </button>
            <button
              onClick={() => {
                setReservationMode(!reservationMode);
                setAvailabilityMode(false);
                setEditMode(false);
              }}
              className={`px-2 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                reservationMode
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {reservationMode ? '🎾 Резервирование ВКЛ' : '⚪ Резервирование ВЫКЛ'}
            </button>
            <button
              onClick={() => {
                setEditMode(!editMode);
                setAvailabilityMode(false);
                setReservationMode(false);
              }}
              className={`px-2 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                editMode
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {editMode ? '✏️ Редактирование ВКЛ' : '⚪ Редактирование ВЫКЛ'}
            </button>
            <button
              onClick={(e) => {
                console.log('=== BUTTON CLICKED ===');
                console.log('Button disabled?', generatingSlots || !selectedDateFrom || !selectedDateTo);
                console.log('generatingSlots:', generatingSlots);
                console.log('selectedDateFrom:', selectedDateFrom);
                console.log('selectedDateTo:', selectedDateTo);
                e.preventDefault();
                handleShowAvailablePeriods();
              }}
              disabled={generatingSlots || !selectedDateFrom || !selectedDateTo}
              className="px-2 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium transition-colors whitespace-nowrap"
            >
              {generatingSlots ? 'Загрузка...' : '📢 Доступные периоды'}
            </button>
            {reservationMode && (
              <button
                onClick={() => {
                  setShowGamesPicker(prev => {
                    const next = !prev;
                    if (next && !selectedTournamentForGamesPicker) {
                      const firstTournament = getTournamentsForGamesPickerSelect()[0];
                      if (firstTournament) {
                        setSelectedTournamentForGamesPicker(firstTournament.id);
                      }
                    }
                    return next;
                  });
                }}
                className={`px-2 py-1.5 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                  showGamesPicker
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                }`}
              >
                🧩 Подбор игр
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1 px-4">
            Генерирует все доступные 2-часовые слоты для выбранного периода (исключая уже занятые)
          </p>
        </div>
      )}

      {/* Контент вкладок */}
      {activeTab === 'calendar' && (
        <>
          {!selectedClubId ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              <p>Выберите клуб для отображения календаря</p>
            </div>
          ) : getClubCourts().length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              <p>У выбранного клуба нет кортов</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow relative">
              <div className="overflow-x-auto p-2">
                {/* Отображаем календарь для каждой даты в периоде */}
                {periodDates.map((date, dateIndex) => (
                  <div key={date} className="mb-6">
                    <div className="flex items-center justify-between mb-2 p-2 bg-gray-100">
                      <h3 className="text-lg font-semibold">
                        {new Date(date).toLocaleDateString('ru-RU', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </h3>
                      <button
                        onClick={() => handleCreateServicePurchase(date)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      >
                        Создать закупку услуг
                      </button>
                    </div>
                    <table className="min-w-full border-collapse mb-4">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 p-2 bg-gray-50 text-xs font-medium text-gray-700 w-16 sticky left-0 z-10">
                        Время
                      </th>
                      {getClubCourts().map(court => (
                        <th key={court.id} className="border border-gray-300 p-1 bg-gray-50 text-xs font-medium text-gray-700 w-24">
                          {court.name}
                        </th>
                      ))}
                      <th className="border border-gray-300 p-2 bg-gray-50 text-xs font-medium text-gray-700 w-16 sticky right-0 z-10">
                        Время
                      </th>
                    </tr>
                  </thead>
              <tbody>
                {timeSlots.map((time, index) => {
                  // Определяем, нужно ли добавить горизонтальную полосу 2px снизу
                  // Полоса нужна между слотами ХХ:30 и ХХ:00 (т.е. когда текущий слот заканчивается на :30)
                  const needsBottomBorder = time.endsWith(':30');
                  
                  return (
                  <tr key={index} className="h-4" style={needsBottomBorder ? { borderBottom: '2px solid #d1d5db' } : {}}>
                    <td className="border border-gray-300 px-2 py-0 text-xs text-gray-600 text-center bg-white sticky left-0 z-10 h-4 w-16" style={needsBottomBorder ? { borderBottom: '2px solid #d1d5db' } : {}}>
                      {time}
                    </td>
                            {getClubCourts().map(court => {
                              const reservation = getReservation(court.id, time, date);
                              const isAvailable = getAvailability(court.id, time, date);
                      
                      if (reservation && isReservationStart(reservation, time)) {
                        const duration = getReservationDuration(reservation);
                        const showReservation = true; // Показываем только в начальной ячейке резервирования
                        
                        return (
                          <td
                            key={court.id}
                            draggable={!availabilityMode && !reservationMode}
                            onDragStart={(e) => {
                              if (!availabilityMode && !reservationMode) {
                                setDraggedReservation(reservation);
                                e.dataTransfer.effectAllowed = 'move';
                              }
                            }}
                                    onMouseDown={() => {
                                      if (availabilityMode) handleMouseDown(court.id, time, date);
                                      else if (editMode) handleOpenEditModal(reservation);
                                    }}
                                    onMouseUp={() => availabilityMode && handleMouseUp()}
                                    onMouseEnter={() => availabilityMode && handleMouseEnter(court.id, time, date)}
                            onMouseLeave={() => {}} // Предотвращаем повторное выделение
                            className={`border border-gray-300 px-1 py-0 text-xs w-24 ${getReservationColor(reservation.status)} ${
                              availabilityMode ? 'cursor-pointer hover:opacity-70' : 
                              editMode ? 'cursor-pointer hover:opacity-70' :
                              (!availabilityMode && !reservationMode) ? 'cursor-move' : ''
                            }`}
                            rowSpan={showReservation ? duration : 0}
                            style={{ 
                              display: showReservation ? 'table-cell' : 'none', 
                              backgroundColor: (reservation.tournament as any)?.color || '#FDEAD7',
                              borderBottom: needsBottomBorder ? '2px solid #d1d5db' : undefined
                            }}
                            title={`${reservation.tournament.name} ${reservation.player1?.name || ''} / ${reservation.player2?.name || ''}`}
                          >
                            {showReservation && (
                              <div className="relative text-center p-1 h-full flex flex-col justify-center gap-0.5 break-words">
                                {reservationMode && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickDeleteReservation(reservation.id);
                                    }}
                                    className="absolute right-0.5 top-0.5 text-red-500 hover:text-red-700 text-[10px] leading-none p-0 m-0 h-3 w-3 flex items-center justify-center"
                                    title="Удалить резерв"
                                  >
                                    ✕
                                  </button>
                                )}
                                <div className="text-[9px] text-gray-500 leading-tight break-words font-bold">
                                  {reservation.startTime}-{reservation.endTime} cort {court.name}
                                </div>
                                <div className="font-medium text-[9px] leading-tight break-words">{reservation.tournament.name}</div>
                                {formatReservationMatchCodeLabel(reservation) && (
                                  <div className="text-[9px] text-gray-600 leading-tight break-words">
                                    {formatReservationMatchCodeLabel(reservation)}
                                  </div>
                                )}
                                {(reservation.player1 || reservation.player2 || reservation.match?.previousMatches?.length) && (
                                  <div className="text-[9px] text-gray-700 leading-tight break-words">
                                    {renderReservationPlayers(reservation)}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      }
                      
                      // Проверяем, является ли ячейка частью резервирования (но не началом)
                      if (reservation && !isReservationStart(reservation, time)) {
                        return null; // Скрываем ячейку, так как она уже объединена через rowSpan
                      }
                      
                      return (
                        <td
                          key={court.id}
                          onDragOver={(e) => {
                            if (draggedReservation && !availabilityMode && !reservationMode) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                            }
                            if (draggedMatchForReservation && reservationMode) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'copy';
                            }
                          }}
                          onDrop={async (e) => {
                            e.preventDefault();
                            if (draggedReservation && !availabilityMode && !reservationMode) {
                              await handleMoveReservation(draggedReservation.id, court.id, time, date);
                              setDraggedReservation(null);
                            }
                            if (draggedMatchForReservation && reservationMode) {
                              await handleCreateReservationFromDraggedMatch(draggedMatchForReservation, court.id, time, date);
                              setDraggedMatchForReservation(null);
                            }
                          }}
                          onMouseDown={() => {
                            if (availabilityMode) handleMouseDown(court.id, time, date);
                            else if (reservationMode) handleOpenReservationModal(court.id, time, date);
                            else if (editMode) {
                              const reservation = getReservation(court.id, time, date);
                              if (reservation) {
                                handleOpenEditModal(reservation);
                              }
                            }
                          }}
                          onMouseUp={() => {
                            if (availabilityMode) handleMouseUp();
                          }}
                          onMouseEnter={() => {
                            if (availabilityMode) handleMouseEnter(court.id, time, date);
                          }}
                          onMouseLeave={() => {}} // Предотвращаем повторное выделение
                          className={`border border-gray-300 px-1 py-0 w-24 ${
                            isAvailable 
                              ? (isPublicSlot(court.id, time, date) ? 'bg-green-400' : 'bg-lime-100') // Публичное предложение - более темный оттенок
                              : 'bg-gray-100'
                          } h-4 ${
                            (availabilityMode || reservationMode) ? 'cursor-pointer hover:opacity-70' : ''
                          }`}
                          style={{ borderBottom: needsBottomBorder ? '2px solid #d1d5db' : undefined }}
                          title={
                            isAvailable 
                              ? (isPublicSlot(court.id, time, date) ? 'Публичное предложение' : 'Доступен')
                              : 'Недоступен'
                          }
                        >
                          <div className="h-full"></div>
                        </td>
                      );
                    })}
                    <td className="border border-gray-300 px-2 py-0 text-xs text-gray-600 text-center bg-white sticky right-0 z-10 h-4 w-16" style={needsBottomBorder ? { borderBottom: '2px solid #d1d5db' } : {}}>
                      {time}
                    </td>
                  </tr>
                  );
                })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              {reservationMode && showGamesPicker && (
                <div className="absolute left-full ml-3 top-3 z-30 w-[260px] border border-gray-200 rounded-md bg-gray-50 p-3 shadow-lg">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-800">Подбор игр</h4>
                      <div className="flex items-center gap-2">
                        <select
                          value={String(gamesPickerDuration)}
                          onChange={(e) => setGamesPickerDuration(Number(e.target.value))}
                          className="h-7 border border-gray-300 rounded px-1.5 text-xs bg-white text-gray-700"
                          title="Длительность матча при переносе в резервы"
                        >
                          <option value="1">1,0</option>
                          <option value="1.5">1,5</option>
                          <option value="2">2,0</option>
                          <option value="2.5">2,5</option>
                        </select>
                        <label className="flex items-center gap-1 text-xs text-gray-700 select-none">
                          <input
                            type="checkbox"
                            checked={gamesPickerPlanMode}
                            disabled={!selectedTournamentForGamesPicker || gamesPickerPlanModeLoading}
                            onChange={(e) => handleToggleGamesPickerPlanMode(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          план (без уведомлений)
                        </label>
                      </div>
                    </div>
                    <select
                      value={selectedTournamentForGamesPicker}
                      onChange={(e) => setSelectedTournamentForGamesPicker(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">Выберите турнир</option>
                      {getTournamentsForGamesPickerSelect().map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={selectedLevelForGamesPicker}
                      onChange={(e) => setSelectedLevelForGamesPicker(e.target.value)}
                      disabled={!selectedTournamentForGamesPicker || loadingGamesForPicker}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      <option value="">Все уровни</option>
                      {getLevelsForGamesPickerSelect().map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-700 select-none">
                      <input
                        type="checkbox"
                        checked={includeReservedMatchesInSelect}
                        onChange={(e) => setIncludeReservedMatchesInSelect(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      и с резервами
                    </label>
                    <p className="text-[11px] text-gray-500">
                      Перетащите игру в нужную ячейку сетки.
                    </p>
                  </div>
                  <div className="mt-3 max-h-[70vh] overflow-y-auto space-y-2 pr-1">
                    {loadingGamesForPicker ? (
                      <div className="text-xs text-gray-500">Загрузка игр...</div>
                    ) : !selectedTournamentForGamesPicker ? (
                      <div className="text-xs text-gray-500">Выберите турнир для подбора игр.</div>
                    ) : getFilteredGamesForPicker().length === 0 ? (
                      <div className="text-xs text-gray-500">Нет доступных игр для резервирования.</div>
                    ) : (
                      getFilteredGamesForPicker().map((match) => (
                        <div
                          key={match.id}
                          draggable
                          onDragStart={(e) => {
                            setDraggedMatchForReservation(match);
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          onDragEnd={() => setDraggedMatchForReservation(null)}
                          className="cursor-grab active:cursor-grabbing border border-indigo-200 bg-white rounded px-2 py-1.5 text-[11px] text-gray-700 shadow-sm hover:border-indigo-400"
                          title="Перетащите в таблицу справа"
                        >
                          <div className="space-y-0.5">
                            <div className="font-medium text-gray-800">{getUnreservedMatchSideLabel(match, 'A') || 'TBD'}</div>
                            <div className="font-medium text-gray-800">{getUnreservedMatchSideLabel(match, 'B') || 'TBD'}</div>
                            <div className="text-[10px] text-gray-500">{formatUnreservedMatchMetaLabel(match)}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'reservations' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Резервы с {formatDate(selectedDateFrom)} по {formatDate(selectedDateTo)}</h2>
            <button
              onClick={() => {
                setActiveTab('calendar');
                setReservationMode(true);
                setAvailabilityMode(false);
                setEditMode(false);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              + Создать резерв
            </button>
          </div>
          
          {loading ? (
            <div className="text-center py-8">Загрузка...</div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Нет резервов</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Клуб</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">День</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Время</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Часы</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Цена</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Корт</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Турнир</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Игроки</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Статус</th>
                    <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation) => (
                    <tr key={reservation.id}>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        {reservation.court.club?.name || 'Не указан'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        {getDayOfWeek(reservation.date)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        {new Date(reservation.date).toLocaleDateString('ru-RU')}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        {reservation.startTime} - {reservation.endTime}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900 text-right">
                        {calculateHours(reservation.startTime, reservation.endTime)} ч.
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900 text-right">
                        {reservation.price ? `${reservation.price.toFixed(2)} MDL` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-900">{reservation.court.name}</td>
                      <td className="px-3 py-2 text-xs text-gray-900">{reservation.tournament.name}</td>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        {reservation.player1?.name || reservation.player2?.name
                          ? formatReservationMatchCodeLabel(reservation)
                            ? `${formatReservationMatchCodeLabel(reservation)} ${reservation.player1?.name && reservation.player2?.name 
                                ? `${reservation.player1.name} / ${reservation.player2.name}`
                                : reservation.player1?.name || reservation.player2?.name
                              }`
                            : reservation.player1?.name && reservation.player2?.name 
                              ? `${reservation.player1.name} / ${reservation.player2.name}`
                              : reservation.player1?.name || reservation.player2?.name
                          : formatReservationMatchCodeLabel(reservation) || '—'
                        }
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${getReservationColor(reservation.status)}`}>
                          {reservation.status === 'RESERVED' ? 'Зарезервировано' : 
                           reservation.status === 'PLAYED' ? 'Сыграно' : 'Отменено'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <button 
                          onClick={() => handleOpenEditModal(reservation)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Редактировать
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'proposals' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Публичные предложения слотов</h2>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  setShowBatchNotificationModal(true);
                  setSelectedProposalsForBatch(new Set());
                  setSelectedPlayersForBatch(new Set());
                  setBatchAllPlayers([]);
                  // Загружаем количество отправленных уведомлений
                  await fetchNotificationCounts();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Пакетное уведомление
              </button>
            <button
              onClick={fetchPublicProposals}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Обновить
            </button>
            </div>
          </div>
          
          {loadingProposals ? (
            <div className="text-center py-8">Загрузка...</div>
          ) : publicProposals.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600 text-center">Нет публичных предложений слотов</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Дата
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Время
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Клуб
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Корт
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Турнир / Уровень / Группа
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {publicProposals.map((proposal) => {
                    // Проверяем состояние из базы данных (isAvailable) и из localStorage
                    const isOccupied = !proposal.isAvailable || occupiedSlots.has(proposal.id);
                    // Проверяем, есть ли резервирование с матчем, которое полностью покрывает это предложение
                    // Матч должен начинаться не позже начала предложения и заканчиваться не раньше конца предложения
                    const hasMatch = reservations.some(r => {
                      if (r.court.id !== proposal.courtId) return false;
                      if (r.date.split('T')[0] !== proposal.date) return false;
                      if (!r.matchCode) return false;
                      // Проверяем, что матч полностью покрывает предложение
                      const proposalStart = proposal.startTime;
                      const proposalEnd = proposal.endTime;
                      const reservationStart = r.startTime;
                      const reservationEnd = r.endTime;
                      // Матч должен начинаться не позже начала предложения и заканчиваться не раньше конца предложения
                      return reservationStart <= proposalStart && reservationEnd >= proposalEnd;
                    });
                    const textWeightClass = hasMatch ? 'font-bold' : '';
                    return (
                    <tr key={proposal.id} className={isOccupied ? 'bg-gray-100' : ''}>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm ${isOccupied ? 'text-gray-400' : 'text-gray-900'} ${textWeightClass}`}>
                        {formatDate(proposal.date)}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm ${isOccupied ? 'text-gray-400' : 'text-gray-900'} ${textWeightClass}`}>
                        {proposal.startTime} - {proposal.endTime}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm ${isOccupied ? 'text-gray-400' : 'text-gray-900'} ${textWeightClass}`}>
                        {proposal.clubName}
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm ${isOccupied ? 'text-gray-400' : 'text-gray-900'} ${textWeightClass}`}>
                        Корт {proposal.courtName}
                      </td>
                      <td className={`px-3 py-4 text-sm ${isOccupied ? 'text-gray-400' : 'text-gray-600'} ${textWeightClass}`}>
                        {proposal.tournamentId ? (
                          <div className="space-y-1">
                            <div className="font-medium">
                              {proposal.tournamentName || tournaments.find(t => t.id === proposal.tournamentId)?.name || 'Турнир'}
                            </div>
                            {proposal.level && (
                              <div className="text-xs text-gray-500">Уровень: {proposal.level}</div>
                            )}
                            {proposal.groupId && (
                              <div className="text-xs text-gray-500">
                                Группа: {proposal.groupName || proposal.groupId}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Не привязано</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={async () => {
                              const newOccupiedSlots = new Set(occupiedSlots);
                              const newIsOccupied = !isOccupied;
                              
                              if (newIsOccupied) {
                                newOccupiedSlots.add(proposal.id);
                              } else {
                                newOccupiedSlots.delete(proposal.id);
                              }
                              setOccupiedSlots(newOccupiedSlots);
                              localStorage.setItem('occupiedSlots', JSON.stringify(Array.from(newOccupiedSlots)));
                              
                              // Обновляем состояние в базе данных для всех блоков предложения
                              // Но только для тех блоков, которые не используются в других активных предложениях
                              try {
                                const blocksToUpdate: string[] = [];
                                
                                // Для каждого блока проверяем, не используется ли он в других предложениях
                                for (const blockId of proposal.blockIds) {
                                  // Проверяем, есть ли другие предложения, которые используют этот блок
                                  const isUsedInOtherProposals = publicProposals.some(otherProposal => 
                                    otherProposal.id !== proposal.id && 
                                    otherProposal.isAvailable !== false &&
                                    otherProposal.blockIds.includes(blockId)
                                  );
                                  
                                  // Обновляем блок только если он не используется в других активных предложениях
                                  if (!isUsedInOtherProposals) {
                                    blocksToUpdate.push(blockId);
                                  }
                                }
                                
                                if (blocksToUpdate.length > 0) {
                                  const updatePromises = blocksToUpdate.map(blockId =>
                                    apiPut(`/court-availability/${blockId}`, {
                                      isAvailable: !newIsOccupied
                                    })
                                  );
                                  await Promise.all(updatePromises);
                                }
                              } catch (error) {
                                console.error('Ошибка обновления состояния слота:', error);
                                // Откатываем изменения в UI при ошибке
                                setOccupiedSlots(occupiedSlots);
                                localStorage.setItem('occupiedSlots', JSON.stringify(Array.from(occupiedSlots)));
                                alert('Не удалось обновить состояние слота');
                              }
                            }}
                            className={`text-lg ${isOccupied ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
                            title={isOccupied ? 'Слот занят (нажмите, чтобы освободить)' : 'Слот свободен (нажмите, чтобы отметить как занятый)'}
                          >
                            {isOccupied ? '🔒' : '🔓'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingProposal(proposal);
                              setEditProposalTournament(proposal.tournamentId || '');
                              setEditProposalLevel(proposal.level || '');
                              setEditProposalGroup(proposal.groupId || '');
                              setShowEditProposalModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 text-lg"
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                        <button
                          onClick={async () => {
                            setEditingProposal(proposal);
                            setShowResponsesModal(true);
                            // Сначала загружаем ответы и отмечаем как просмотренные
                            await fetchProposalResponses(proposal.id);
                            // Небольшая задержка для гарантии обновления на сервере
                            await new Promise(resolve => setTimeout(resolve, 100));
                            // Обновляем список просмотренных ответов перед обновлением счетчиков
                            await fetchViewedResponses();
                            // Загружаем игроков параллельно с обновлением списка
                            await Promise.all([
                              fetchFilteredPlayersForResponse(proposal),
                              fetchPublicProposals()
                            ]);
                          }}
                            className={`text-lg relative ${proposal.totalResponsesCount && proposal.totalResponsesCount > 0 ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`}
                            title={
                              proposal.totalResponsesCount && proposal.totalResponsesCount > 0
                                ? proposal.responsesCount && proposal.responsesCount > 0
                                  ? `Ответы (${proposal.totalResponsesCount} всего, ${proposal.responsesCount} непросмотренных)`
                                  : `Ответы (${proposal.totalResponsesCount} всего, все просмотрены)`
                                : 'Ответы'
                            }
                          >
                            {/* Показываем зеленое сердечко, если есть ответы (даже если все просмотрены) */}
                            {proposal.totalResponsesCount && proposal.totalResponsesCount > 0 ? '💚' : '💬'}
                            {/* Показываем цифру только для непросмотренных ответов */}
                            {proposal.responsesCount && proposal.responsesCount > 0 && (
                              <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {proposal.responsesCount}
                              </span>
                            )}
                          </button>
                          {proposal.responsesCount && proposal.responsesCount > 0 && (
                            <button
                              onClick={() => {
                                setSourceProposalForCopy(proposal);
                                setSelectedTargetProposalId('');
                                setShowCopyResponsesModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-900 text-lg"
                              title="Копировать ответы на другое предложение"
                            >
                              📋
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (!proposal.tournamentId) {
                                alert('Нельзя создать задачи: предложение не привязано к турниру.\n\nСначала привяжите предложение к турниру через кнопку редактирования.');
                                return;
                              }
                              
                              if (!proposal.responsesCount || proposal.responsesCount === 0) {
                                alert('Нельзя создать задачи: нет ответов игроков на это предложение.\n\nСначала дождитесь ответов от игроков.');
                                return;
                              }

                              // Показываем предпросмотр задач
                              try {
                                setLoadingPreview(true);
                                setPreviewProposalId(proposal.id);
                                const preview = await apiGet(`/opponent-matching-tasks/preview/${proposal.id}`);
                                setPreviewTasks(preview.tasks || []);
                                // Выбираем все задачи, которые будут созданы (не пропущенные)
                                const tasksToSelect = (preview.tasks || [])
                                  .filter((t: any) => !t.wouldSkip)
                                  .map((t: any) => `${t.activePlayerId}-${t.opponentPlayerId}-${t.groupId}`);
                                setSelectedPreviewTasks(new Set(tasksToSelect));
                                setShowTaskPreviewModal(true);
                              } catch (error: any) {
                                console.error('Ошибка предпросмотра задач:', error);
                                alert('Ошибка загрузки предпросмотра: ' + (error.response?.data?.error || error.message));
                              } finally {
                                setLoadingPreview(false);
                              }
                            }}
                            className={`text-lg relative ${
                              !proposal.tournamentId || !proposal.responsesCount || proposal.responsesCount === 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : proposal.tasksCount && proposal.tasksCount > 0
                                  ? 'text-green-600 hover:text-green-900'
                                  : 'text-orange-500 hover:text-orange-700'
                            }`}
                            title={
                              !proposal.tournamentId
                                ? 'Нельзя создать задачи: предложение не привязано к турниру'
                                : !proposal.responsesCount || proposal.responsesCount === 0
                                  ? 'Нельзя создать задачи: нет ответов игроков'
                                  : proposal.tasksCount && proposal.tasksCount > 0
                                    ? `Задачи созданы (${proposal.tasksCount})`
                                    : 'Создать задачи подбора соперников'
                            }
                            disabled={!proposal.tournamentId || !proposal.responsesCount || proposal.responsesCount === 0}
                          >
                            {proposal.tasksCount && proposal.tasksCount > 0 ? '✅' : '👥'}
                            {proposal.tasksCount && proposal.tasksCount > 0 && (
                              <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {proposal.tasksCount}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={async () => {
                              setNotificationProposal(proposal);
                              setShowNotificationModal(true);
                              await fetchPlayersForNotification(proposal);
                            }}
                            className="text-blue-600 hover:text-blue-900 text-lg"
                            title="Отправить уведомление игрокам"
                          >
                            📤
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                // Проверяем, есть ли ответы на это предложение
                                const responses = await apiGet(`/public-proposal-responses/proposal/${proposal.id}`);
                                
                                if (responses && responses.length > 0) {
                                  alert(`Невозможно удалить предложение. У него есть ${responses.length} ответ(ов) игроков.\n\nСначала удалите все ответы через кнопку "Ответы", затем можно будет удалить предложение.`);
                                  return;
                                }
                                
                                if (confirm(`Удалить публичное предложение?\n${formatDate(proposal.date)} ${proposal.startTime}-${proposal.endTime}, Корт ${proposal.courtName}\n\nДоступность сохранится, но предложение больше не будет публичным.`)) {
                                  console.log(`\n=== Deleting proposal ===`);
                                  console.log(`Proposal: ${formatDate(proposal.date)} ${proposal.startTime}-${proposal.endTime}, Корт ${proposal.courtName}`);
                                  console.log(`Block IDs:`, proposal.blockIds);
                                  
                                  // Снимаем флаг isPublic для всех 4 блоков (не удаляем записи)
                                  for (const blockId of proposal.blockIds) {
                                    console.log(`Updating block ${blockId} with isPublic: false`);
                                    const response = await apiPut(`/court-availability/${blockId}`, { isPublic: false });
                                    console.log(`Block ${blockId} updated:`, response);
                                  }
                                  
                                  console.log('All blocks updated, refreshing proposals...');
                                  await fetchPublicProposals();
                                  await fetchAvailabilities(); // Обновляем календарь
                                  devLog('Proposals refreshed');
                                }
                              } catch (error: any) {
                                console.error('Ошибка удаления предложения:', error);
                                console.error('Error details:', error.response?.data);
                                alert('Ошибка удаления: ' + (error.response?.data?.error || error.message));
                              }
                            }}
                            className="text-red-600 hover:text-red-900 text-lg"
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Вкладка "Доступность игроков" */}
      {activeTab === 'player-availability' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Доступность игроков по дням и слотам</h2>
            <button
              onClick={fetchPlayerSlotAvailability}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              disabled={loadingPlayerSlotAvailability}
            >
              {loadingPlayerSlotAvailability ? 'Загрузка...' : 'Обновить'}
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Турнир</label>
                <div className="relative">
                  <select
                    value={availabilityTournamentId}
                    onChange={(e) => {
                      setAvailabilityTournamentId(e.target.value);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm"
                  >
                    <option value="">Все турниры</option>
                    {tournaments.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {availabilityTournamentId && (
                    <button
                      type="button"
                      onClick={() => {
                        setAvailabilityTournamentId('');
                        setAvailabilityGroupId('');
                        setAvailabilityPlayerName('');
                      }}
                      className="absolute right-7 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
                      title="Сбросить фильтр турнира"
                    >
                      X
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Уровень - Группа</label>
                <div className="relative">
                  <select
                    value={availabilityGroupId}
                    onChange={(e) => {
                      setAvailabilityGroupId(e.target.value);
                      setAvailabilityPlayerName('');
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm"
                    disabled={!availabilityTournamentId}
                  >
                    <option value="">Все группы</option>
                    {availabilityGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.level ? `${group.level} - ${group.name}` : group.name}
                      </option>
                    ))}
                  </select>
                  {availabilityGroupId && (
                    <button
                      type="button"
                      onClick={() => {
                        setAvailabilityGroupId('');
                        setAvailabilityPlayerName('');
                      }}
                      className="absolute right-7 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
                      title="Сбросить фильтр группы"
                    >
                      X
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Доступный игрок</label>
                <div className="relative">
                  <select
                    value={availabilityPlayerName}
                    onChange={(e) => setAvailabilityPlayerName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm"
                  >
                    <option value="">Все игроки</option>
                    {availablePlayerFilterOptions.map((playerName) => (
                      <option key={playerName} value={playerName}>{playerName}</option>
                    ))}
                  </select>
                  {availabilityPlayerName && (
                    <button
                      type="button"
                      onClick={() => setAvailabilityPlayerName('')}
                      className="absolute right-7 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
                      title="Сбросить фильтр игрока"
                    >
                      X
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {loadingPlayerSlotAvailability ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              <p>Загрузка доступности игроков...</p>
            </div>
          ) : playerSlotAvailability.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              <p>Нет данных по доступности игроков за выбранный период</p>
            </div>
          ) : filteredPlayerSlotAvailability.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              <p>По выбранным фильтрам нет данных</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(playerAvailabilityByDate).map(([date, slots]) => (
                <div key={date} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <h3 className="text-lg font-semibold">
                      {formatDate(date)} ({getDayOfWeek(date)})
                    </h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Слот / Корт</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Доступны</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {slots.map((slot) => (
                          <tr key={slot.proposalId} className="border-b-[3px] border-gray-300">
                            <td className="px-3 py-3 text-sm text-gray-900 align-top min-w-[220px]">
                              <div className="font-semibold">{slot.startTime} - {slot.endTime}</div>
                              <div className="font-medium">{slot.clubName} / {slot.courtName}</div>
                              {(slot.tournamentName || slot.groupName) && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {slot.tournamentName || 'Без турнира'}
                                  {slot.groupName ? ` • ${slot.groupName}` : ''}
                                </div>
                              )}
                            </td>
                            <td className={`px-3 py-3 align-top min-w-[200px] ${slot.assignedMatch ? 'text-center' : 'text-left'}`}>
                              {slot.assignedMatch ? (
                                <div className="text-sm text-gray-800 leading-5 inline-block text-center">
                                  <div className="text-xs text-gray-500 mb-1 text-center">
                                    {slot.assignedMatch.tournamentName || 'Без турнира'}
                                    {slot.assignedMatch.groupName ? ` • ${slot.assignedMatch.groupName}` : ''}
                                  </div>
                                  <div>{slot.assignedMatch.players.join(' - ') || 'Игроки не указаны'}</div>
                                </div>
                              ) : (
                                renderAvailabilityPlayers(slot.readyPlayers, slot.proposalId)
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Вкладка "Подбор соперников" */}
      {activeTab === 'opponent-matching' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Задачи подбора соперников</h2>
            <button
              onClick={fetchOpponentMatchingTasks}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loadingTasks}
            >
              {loadingTasks ? 'Загрузка...' : 'Обновить'}
            </button>
          </div>

          {/* Фильтры и создание задач */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Турнир
                </label>
                <select
                  value={selectedTaskTournament}
                  onChange={async (e) => {
                    const tournamentId = e.target.value;
                    setSelectedTaskTournament(tournamentId);
                    setSelectedTaskGroup('');
                    setSelectedTaskPlayer(''); // Сбрасываем выбор игрока при смене турнира
                    
                    // Загружаем игроков выбранного турнира
                    if (tournamentId) {
                      try {
                        const data = await apiGet<any[]>(`/tournaments/${tournamentId}/players`);
                        const playersMap = new Map<string, any>();
                        data.forEach((m: any) => {
                          if (m.player && m.player.id && !playersMap.has(m.player.id)) {
                            playersMap.set(m.player.id, m.player);
                          }
                        });
                        const players = Array.from(playersMap.values());
                        players.sort((a, b) => {
                          const nameA = (a.name || '').toLowerCase();
                          const nameB = (b.name || '').toLowerCase();
                          return nameA.localeCompare(nameB, 'ru');
                        });
                        setTaskFilterPlayers(players);
                      } catch (error) {
                        console.error('Ошибка загрузки игроков турнира:', error);
                        setTaskFilterPlayers([]);
                      }
                    } else {
                      setTaskFilterPlayers([]);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Все турниры</option>
                  {getOrderedManualReservationStatuses().map((status) => (
                    <optgroup
                      key={status}
                      label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                    >
                      {(getGroupedTournamentsForManualReservation()[status] || []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Группа
                </label>
                <select
                  value={selectedTaskGroup}
                  onChange={(e) => setSelectedTaskGroup(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={!selectedTaskTournament}
                >
                  <option value="">Все группы</option>
                  {(() => {
                    // Загружаем группы для выбранного турнира
                    const tournament = tournaments.find(t => t.id === selectedTaskTournament);
                    if (!tournament) return null;
                    // Если группы не загружены в объекте турнира, используем пустой массив
                    const groups = tournament.groups || [];
                    return groups.map((g: any) => (
                      <option key={g.id} value={g.id}>{g.name} ({g.level})</option>
                    ));
                  })()}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Статус
                </label>
                <select
                  value={selectedTaskStatus}
                  onChange={(e) => setSelectedTaskStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Все статусы</option>
                  <option value="PENDING">Ожидает</option>
                  <option value="IN_PROGRESS">В работе</option>
                  <option value="MATCH_PROPOSAL_CREATED">Матч</option>
                  <option value="SKIPPED">Пропущено</option>
                  <option value="COMPLETED">Завершено</option>
                  <option value="CANCELLED">Отменено</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Предложение
                </label>
                <select
                  value={selectedProposalForTasks}
                  onChange={(e) => setSelectedProposalForTasks(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Все предложения</option>
                  {publicProposals.map(p => (
                    <option key={p.id} value={p.id}>
                      {formatDate(p.date)} {p.startTime}-{p.endTime}, Корт {p.courtName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Игрок
                </label>
                <select
                  value={selectedTaskPlayer}
                  onChange={(e) => setSelectedTaskPlayer(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  disabled={!selectedTaskTournament}
                >
                  <option value="">{selectedTaskTournament ? 'Все игроки' : 'Выберите турнир'}</option>
                  {taskFilterPlayers.map(player => (
                    <option key={player.id} value={player.id}>{player.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchOpponentMatchingTasks}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
              >
                Применить фильтры
              </button>
              <button
                onClick={() => {
                  setSelectedTaskTournament('');
                  setSelectedTaskGroup('');
                  setSelectedTaskStatus('');
                  setSelectedProposalForTasks('');
                  setSelectedTaskPlayer('');
                  setTaskFilterPlayers([]);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm"
              >
                Сбросить фильтры
              </button>
            </div>
          </div>

          {/* Массовые действия */}
          {selectedTasksForBulkAction.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">
                  Выбрано задач: {selectedTasksForBulkAction.size}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleBulkCreateProposals}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                    Создать предложения матчей
                  </button>
                  <button
                    onClick={() => setSelectedTasksForBulkAction(new Set())}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm"
                  >
                    Снять выбор
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Таблица задач */}
          {loadingTasks ? (
            <div className="text-center py-8">Загрузка задач...</div>
          ) : opponentMatchingTasks.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-600 text-center">Нет задач подбора соперников</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                        <input
                          type="checkbox"
                          checked={opponentMatchingTasks.length > 0 && opponentMatchingTasks.every(t => selectedTasksForBulkAction.has(t.id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTasksForBulkAction(new Set(opponentMatchingTasks.map(t => t.id)));
                            } else {
                              setSelectedTasksForBulkAction(new Set());
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Активный игрок</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Соперник</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Группа</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Предложение</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Приоритет</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[...opponentMatchingTasks].sort((a, b) => {
                      // Сортировка по дате
                      const dateA = a.proposalDate ? new Date(a.proposalDate).getTime() : 0;
                      const dateB = b.proposalDate ? new Date(b.proposalDate).getTime() : 0;
                      if (dateA !== dateB) {
                        return dateA - dateB;
                      }
                      
                      // Если даты одинаковые, сортируем по времени
                      const timeA = a.proposalStartTime || '';
                      const timeB = b.proposalStartTime || '';
                      if (timeA !== timeB) {
                        return timeA.localeCompare(timeB);
                      }
                      
                      // Если дата и время одинаковые, сортируем по корту
                      const courtA = a.court?.name || '';
                      const courtB = b.court?.name || '';
                      return courtA.localeCompare(courtB);
                    }).map((task) => (
                      <tr key={task.id} className={selectedTasksForBulkAction.has(task.id) ? 'bg-blue-50' : ''}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedTasksForBulkAction.has(task.id)}
                            onChange={(e) => {
                              const newSet = new Set(selectedTasksForBulkAction);
                              if (e.target.checked) {
                                newSet.add(task.id);
                              } else {
                                newSet.delete(task.id);
                              }
                              setSelectedTasksForBulkAction(newSet);
                            }}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="font-medium text-gray-900">{task.activePlayer.name}</div>
                          <div className="text-xs text-gray-500">{task.gamesPlayedActive} игр</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="font-medium text-gray-900">{task.opponentPlayer.name}</div>
                          <div className="text-xs text-gray-500">{task.gamesPlayedOpponent} игр</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="font-medium text-gray-900">{task.group.name}</div>
                          <div className="text-xs text-gray-500">{task.group.level}</div>
                          <div className="text-xs text-gray-500">{task.tournament.name}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>{formatDate(task.proposalDate)}</div>
                          <div className="text-xs">{task.proposalStartTime} - {task.proposalEndTime}</div>
                          {task.court && (
                            <div className="text-xs">Корт {task.court.name}</div>
                          )}
                          {task.reason && (
                            <div className="text-xs text-gray-500 mt-1">{task.reason}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            task.priority >= 900 ? 'bg-green-100 text-green-800' :
                            task.priority >= 700 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {task.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getTaskStatusColor(task.status)}`}>
                            {getTaskStatusLabel(task.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <div className="flex flex-col gap-1">
                            {/* Первый ряд: основные действия */}
                            <div className="flex items-center gap-2">
                              {task.status === 'PENDING' && (
                                <button
                                  onClick={() => handleCreateMatchProposalFromTask(task.id)}
                                  className="text-green-600 hover:text-green-900 text-xs"
                                  title="Создать предложение матча"
                                >
                                  ✅
                                </button>
                              )}
                              {task.status !== 'SKIPPED' && task.status !== 'CANCELLED' && task.status !== 'MATCH_PROPOSAL_CREATED' && task.status !== 'MATCH_ASSIGNED' && (
                                <button
                                  onClick={() => handleUpdateTaskStatus(task.id, 'SKIPPED')}
                                  className="text-gray-600 hover:text-gray-900 text-xs"
                                  title="Пропустить"
                                >
                                  ⏭️
                                </button>
                              )}
                              {task.status !== 'CANCELLED' && task.status !== 'MATCH_PROPOSAL_CREATED' && task.status !== 'MATCH_ASSIGNED' && (
                                <button
                                  onClick={() => {
                                    openTaskCancelModal(task);
                                  }}
                                  className="text-red-600 hover:text-red-900 text-xs"
                                  title="Отменить"
                                >
                                  ❌
                                </button>
                              )}
                              <button
                                onClick={() => handleViewTaskHistory(task.id)}
                                className="text-blue-600 hover:text-blue-900 text-xs"
                                title="История"
                              >
                                📋
                              </button>
                              {(task.status === 'MATCH' || task.status === 'MATCH_PROPOSAL_CREATED' || task.status === 'COMPLETED') && task.matchProposalId && (
                                <button
                                  onClick={() => handleCreateMatchFromTask(task.id)}
                                  className="text-green-600 hover:text-green-900 text-xs"
                                  title="Создать/обновить матч"
                                >
                                  ➕
                                </button>
                              )}
                            </div>
                            {/* Второй ряд: карточка и запросы */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleOpenTaskCard(task.id)}
                                className="text-indigo-600 hover:text-indigo-900 text-xs"
                                title="Открыть карточку задачи"
                              >
                                📄
                              </button>
                              {(() => {
                                const { icon, title } = getPlayer1NotificationIcon(task);
                                return (
                                  <button
                                    onClick={() => handleSendMatchNotification(task.id, 1)}
                                    className={`text-xs ${
                                      task.notificationSentToPlayer1
                                        ? 'text-green-600 hover:text-green-900'
                                        : 'text-purple-600 hover:text-purple-900'
                                    }`}
                                    title={title}
                                  >
                                    {icon}
                                  </button>
                                );
                              })()}
                              {(() => {
                                const { icon, title } = getPlayer2NotificationIcon(task);
                                return (
                                  <button
                                    onClick={() => handleSendMatchNotification(task.id, 2)}
                                    className={`text-xs ${
                                      task.notificationSentToPlayer2
                                        ? 'text-green-600 hover:text-green-900'
                                        : 'text-purple-600 hover:text-purple-900'
                                    }`}
                                    title={title}
                                  >
                                    {icon}
                                  </button>
                                );
                              })()}
                              {(() => {
                                const { icon, title } = getClubReservationIcon(task);
                                return (
                                  <button
                                    onClick={() => handleReserveCourt(task.id)}
                                    className={`text-xs ${
                                      task.courtReservationRequestSent
                                        ? 'text-green-600 hover:text-green-900'
                                        : 'text-orange-600 hover:text-orange-900'
                                    }`}
                                    title={title}
                                  >
                                    {icon}
                                  </button>
                                );
                              })()}
                            </div>
                            {/* Третий ряд: удаление */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                className="text-red-700 hover:text-red-900 text-xs"
                                title="Удалить задачу"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Модальное окно для создания резервирования */}
      {isReservationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-2">Создать резервирование корта</h2>
            {reservationForm.date && (selectedTimeForReservation || reservationForm.startTime) && selectedCourtForReservation && (() => {
              const court = courts.find(c => c.id === selectedCourtForReservation);
              const date = new Date(reservationForm.date);
              const formattedDate = date.toLocaleDateString('ru-RU', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              });
              const time = selectedTimeForReservation || reservationForm.startTime;
              return (
                <p className="text-sm text-gray-600 mb-4">
                  {formattedDate}, {time}, корт {court?.name || selectedCourtForReservation}
                </p>
              );
            })()}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Длительность матча
                </label>
                <select
                  value={reservationForm.duration}
                  onChange={(e) => setReservationForm({...reservationForm, duration: parseFloat(e.target.value)})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value={0.5}>0.5 часа</option>
                  <option value={1.0}>1.0 часа</option>
                  <option value={1.5}>1.5 часа</option>
                  <option value={2.0}>2.0 часа</option>
                  <option value={2.5}>2.5 часа</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Турнир *
                </label>
                <select
                  value={reservationForm.tournamentId}
                  onChange={(e) => {
                    const selectedTournament = tournaments.find(t => t.id === e.target.value);
                    // Ищем цену из TournamentClub для выбранного клуба
                    let price = 0;
                    if (selectedTournament && selectedClubId) {
                      const tournamentClub = selectedTournament.tournamentClubs?.find(
                        (tc: any) => tc.clubId === selectedClubId
                      );
                      price = tournamentClub?.courtRentalPrice || selectedTournament?.courtRentalPrice || 0;
                    } else if (selectedTournament) {
                      price = selectedTournament?.courtRentalPrice || 0;
                    }
                    setReservationForm({
                      ...reservationForm, 
                      tournamentId: e.target.value,
                      player3Id: '',
                      player4Id: '',
                      player1Id: '', // Сбрасываем игроков при смене турнира
                      player2Id: '',
                      matchCode: '',
                      price: price // Автоматически заполняем цену из TournamentClub или Tournament
                    });
                    setSelectedGeneratedMatchId('');
                    setSelectedGeneratedMatchDbId(null);
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isLinkedReservation}
                  required
                >
                  <option value="">Выберите турнир</option>
                  {getOrderedManualReservationStatuses().map((status) => (
                    <optgroup
                      key={status}
                      label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                    >
                      {(getGroupedTournamentsForManualReservation()[status] || []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Сгенерированный матч (без резерва)
                  </label>
                  <label className="flex items-center gap-1 text-xs text-gray-700 select-none">
                    <input
                      type="checkbox"
                      checked={includeReservedMatchesInSelect}
                      onChange={(e) => setIncludeReservedMatchesInSelect(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    и с резервами
                  </label>
                </div>
                <select
                  value={selectedGeneratedMatchId}
                  onChange={(e) => applyGeneratedMatch(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isLinkedReservation || !reservationForm.tournamentId || loadingUnreservedMatches}
                >
                  <option value="">
                    {!reservationForm.tournamentId
                      ? 'Сначала выберите турнир'
                      : loadingUnreservedMatches
                        ? 'Загрузка матчей...'
                        : 'Выберите матч'}
                  </option>
                  {selectedGeneratedMatchId === CURRENT_GENERATED_MATCH_VALUE && (
                    <option value={CURRENT_GENERATED_MATCH_VALUE}>
                      {getCurrentGeneratedMatchLabel()}
                    </option>
                  )}
                  {unreservedMatches.map((m) => (
                    <option key={m.id} value={m.id}>
                      {formatUnreservedMatchLabel(m)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Игрок 1
                </label>
                <select
                  value={reservationForm.player1Id}
                  onChange={(e) => setReservationForm({...reservationForm, player1Id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isLinkedReservation || !reservationForm.tournamentId}
                >
                  <option value="">Выберите игрока</option>
                  {tournamentPlayers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Игрок 2
                </label>
                <select
                  value={reservationForm.player2Id}
                  onChange={(e) => setReservationForm({...reservationForm, player2Id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isLinkedReservation || !reservationForm.tournamentId}
                >
                  <option value="">Выберите игрока</option>
                  {tournamentPlayers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Код матча
                </label>
                <input
                  type="text"
                  value={reservationForm.matchCode}
                  onChange={(e) => setReservationForm({...reservationForm, matchCode: e.target.value})}
                  placeholder="например, FIN S5-G"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={isLinkedReservation}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Стоимость аренды (MDL/час)
                </label>
                <input
                  type="number"
                  value={reservationForm.price}
                  onChange={(e) => setReservationForm({...reservationForm, price: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div className="pt-1">
                <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                  <input
                    type="checkbox"
                    checked={notifyTelegramGroupOnCreate}
                    onChange={(e) => setNotifyTelegramGroupOnCreate(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Уведомление в группу Telegram
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCloseReservationModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateReservation}
                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
              >
                Создать резервирование
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для редактирования резервирования */}
      {isEditModalOpen && editingReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Редактировать резервирование корта</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата *
                </label>
                <input
                  type="date"
                  value={reservationForm.date}
                  onChange={(e) => setReservationForm({...reservationForm, date: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Корты *
                </label>
                <select
                  value={reservationForm.courtId}
                  onChange={(e) => setReservationForm({...reservationForm, courtId: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">Выберите корт</option>
                  {getClubCourts().map(court => (
                    <option key={court.id} value={court.id}>{court.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Время начала *
                  </label>
                  <select
                    value={reservationForm.startTime}
                    onChange={(e) => setReservationForm({...reservationForm, startTime: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Выберите время</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Время окончания *
                  </label>
                  <select
                    value={reservationForm.endTime}
                    onChange={(e) => setReservationForm({...reservationForm, endTime: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Выберите время</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Турнир *
                </label>
                <select
                  value={reservationForm.tournamentId}
                  onChange={(e) => {
                    const selectedTournament = tournaments.find(t => t.id === e.target.value);
                    // Ищем цену из TournamentClub для выбранного клуба
                    let price = 0;
                    if (selectedTournament && selectedClubId) {
                      const tournamentClub = selectedTournament.tournamentClubs?.find(
                        (tc: any) => tc.clubId === selectedClubId
                      );
                      price = tournamentClub?.courtRentalPrice || selectedTournament?.courtRentalPrice || 0;
                    } else if (selectedTournament) {
                      price = selectedTournament?.courtRentalPrice || 0;
                    }
                    setReservationForm({
                      ...reservationForm, 
                      tournamentId: e.target.value,
                      player3Id: '',
                      player4Id: '',
                      player1Id: '', // Сбрасываем игроков при смене турнира
                      player2Id: '',
                      matchCode: '',
                      price: price // Автоматически заполняем цену из TournamentClub или Tournament
                    });
                    setSelectedGeneratedMatchId('');
                    setSelectedGeneratedMatchDbId(null);
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="">Выберите турнир</option>
                  {getOrderedManualReservationStatuses().map((status) => (
                    <optgroup
                      key={status}
                      label={`──────── ${TOURNAMENT_STATUS_LABELS[status] || status} ────────`}
                    >
                      {(getGroupedTournamentsForManualReservation()[status] || []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Сгенерированный матч (без резерва)
                  </label>
                  <label className="flex items-center gap-1 text-xs text-gray-700 select-none">
                    <input
                      type="checkbox"
                      checked={includeReservedMatchesInSelect}
                      onChange={(e) => setIncludeReservedMatchesInSelect(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    и с резервами
                  </label>
                </div>
                <select
                  value={selectedGeneratedMatchId}
                  onChange={(e) => applyGeneratedMatch(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={!reservationForm.tournamentId || loadingUnreservedMatches}
                >
                  <option value="">
                    {!reservationForm.tournamentId
                      ? 'Сначала выберите турнир'
                      : loadingUnreservedMatches
                        ? 'Загрузка матчей...'
                        : 'Выберите матч'}
                  </option>
                  {selectedGeneratedMatchId === CURRENT_GENERATED_MATCH_VALUE && (
                    <option value={CURRENT_GENERATED_MATCH_VALUE}>
                      {getCurrentGeneratedMatchLabel()}
                    </option>
                  )}
                  {unreservedMatches.map((m) => (
                    <option key={m.id} value={m.id}>
                      {formatUnreservedMatchLabel(m)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Игрок 1
                </label>
                <select
                  value={reservationForm.player1Id}
                  onChange={(e) => setReservationForm({...reservationForm, player1Id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={!reservationForm.tournamentId}
                >
                  <option value="">Выберите игрока</option>
                  {tournamentPlayers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Игрок 2
                </label>
                <select
                  value={reservationForm.player2Id}
                  onChange={(e) => setReservationForm({...reservationForm, player2Id: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  disabled={!reservationForm.tournamentId}
                >
                  <option value="">Выберите игрока</option>
                  {tournamentPlayers.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Код матча
                </label>
                <input
                  type="text"
                  value={reservationForm.matchCode}
                  onChange={(e) => setReservationForm({...reservationForm, matchCode: e.target.value})}
                  placeholder="например, FIN S5-G"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Стоимость аренды (MDL/час)
                </label>
                <input
                  type="number"
                  value={reservationForm.price}
                  onChange={(e) => setReservationForm({...reservationForm, price: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={handleDeleteReservation}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Удалить
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Отмена
                </button>
                <button
                  onClick={handleUpdateReservation}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для публичных предложений слотов */}
      {showSlotsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Публичные предложения слотов ({generatedSlots.length} шт.)
            </h2>
            
            {/* Поля для привязки к турниру (опционально) */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">Привязка к турниру (необязательно)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Турнир
                  </label>
                  <select
                    value={selectedTournamentForProposal}
                    onChange={(e) => {
                      setSelectedTournamentForProposal(e.target.value);
                      setSelectedLevelForProposal('');
                      setSelectedGroupForProposal('');
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Без привязки</option>
                    {getActiveTournamentsForProposalSelect().map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Уровень
                  </label>
                  <select
                    value={selectedLevelForProposal}
                    onChange={(e) => {
                      setSelectedLevelForProposal(e.target.value);
                      setSelectedGroupForProposal('');
                    }}
                    disabled={!selectedTournamentForProposal}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Все уровни</option>
                    {levelsForProposal.map(l => (
                      <option key={l.level} value={l.level}>{l.level} ({l.playerCount})</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Группа
                  </label>
                  <select
                    value={selectedGroupForProposal}
                    onChange={(e) => setSelectedGroupForProposal(e.target.value)}
                    disabled={!selectedLevelForProposal}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Все группы</option>
                    {groupsForProposal.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.level})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="mb-4 flex justify-between items-center">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setGeneratedSlots(slots => slots.map(s => ({ ...s, checked: true })));
                  }}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Отметить все
                </button>
                <button
                  onClick={() => {
                    setGeneratedSlots(slots => slots.map(s => ({ ...s, checked: false })));
                  }}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Снять все
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Выбрано: {generatedSlots.filter(s => s.checked).length} из {generatedSlots.length}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 max-h-[60vh] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                      <input
                        type="checkbox"
                        checked={generatedSlots.every(s => s.checked)}
                        onChange={(e) => {
                          setGeneratedSlots(slots => slots.map(s => ({ ...s, checked: e.target.checked })));
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Время</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Клуб</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Корт</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {generatedSlots.map((slot, index) => {
                    const slotKey = `${slot.date}-${slot.startTime}-${slot.courtId}`;
                    return (
                    <tr key={slotKey} className={slot.checked ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={slot.checked}
                          onChange={(e) => {
                            console.log(`\n=== Checkbox changed ===`);
                            console.log(`Slot: ${slot.date} ${slot.startTime}-${slot.endTime} ${slot.courtName}`);
                            console.log(`Old checked state: ${slot.checked}`);
                            console.log(`New checked state: ${e.target.checked}`);
                            const newSlots = [...generatedSlots];
                            newSlots[index].checked = e.target.checked;
                            console.log(`Updated slots state:`, newSlots.map(s => `${s.date} ${s.startTime} checked: ${s.checked}`));
                            setGeneratedSlots(newSlots);
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {formatDate(slot.date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {slot.startTime} - {slot.endTime}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {slot.clubName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {slot.courtName}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSlotsModal(false);
                  setGeneratedSlots([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSendSlots}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                disabled={generatedSlots.filter(s => s.checked).length === 0}
              >
                ✅ Отправлено
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно доступных периодов */}
      {showPeriodsModal && availablePeriods && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Доступные периоды по кортам
            </h2>
            
            {/* Параметры запроса */}
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold text-sm mb-2">Параметры запроса:</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="font-medium">Дата с:</span> {requestParams.startDate || '-'}</div>
                <div><span className="font-medium">Дата по:</span> {requestParams.endDate || '-'}</div>
                <div><span className="font-medium">Время с:</span> 07:00</div>
                <div><span className="font-medium">Время по:</span> 23:00</div>
                <div><span className="font-medium">Клуб:</span> {requestParams.clubId ? clubs.find(c => c.id === requestParams.clubId)?.name || requestParams.clubId : 'Все клубы'}</div>
                <div><span className="font-medium">Статус блока:</span> isAvailable = true</div>
              </div>
            </div>

            <div className="mb-4 text-sm text-gray-600 space-y-1">
              <div>Найдено кортов: {availablePeriods.summary?.totalCourts || 0}</div>
              <div>Всего 30-минутных блоков: {availablePeriods.summary?.totalBlocks || 0}</div>
              <div>Всего 2-часовых периодов: {availablePeriods.summary?.totalPeriods || 0}</div>
            </div>

            <div className="space-y-4">
              {availablePeriods.periodsByCourt?.map((court: any) => (
                <div key={court.courtId} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3">
                    Корт {court.courtName} ({court.clubName})
                  </h3>
                  
                  {/* 30-минутные блоки */}
                  <div className="mb-3">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">
                      30-минутные блоки ({court.blocks?.length || 0}):
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {court.blocks?.map((block: any, idx: number) => (
                        <div key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {block.date} {block.startTime}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 2-часовые периоды */}
                  {court.periods && court.periods.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm text-gray-700 mb-2">
                        2-часовые периоды ({court.periods.length}):
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {court.periods.map((period: any, idx: number) => (
                          <div key={idx} className="text-sm bg-blue-50 text-blue-800 p-2 rounded">
                            <span className="font-medium">{period.date}</span> - {period.startTime} - {period.endTime}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {(!availablePeriods.periodsByCourt || availablePeriods.periodsByCourt.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                Нет доступных периодов для выбранного диапазона дат
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowPeriodsModal(false);
                  setAvailablePeriods(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Закрыть
              </button>
              <button
                onClick={async () => {
                  setShowPeriodsModal(false);
                  // Используем те же параметры, что и для поиска доступных периодов
                  if (!requestParams.startDate || !requestParams.endDate) {
                    alert('Нет параметров запроса');
                    return;
                  }
                  
                  console.log('=== Generating slots with params ===', requestParams);
                  
                  setGeneratingSlots(true);
                  try {
                    const response = await apiPost<{ slots: any[] }>('/court-availability/generate-slots', {
                      startDate: requestParams.startDate,
                      endDate: requestParams.endDate,
                      clubId: requestParams.clubId || undefined
                    });

                    const slotsWithChecked = (response.slots || []).map(slot => ({
                      ...slot,
                      checked: false
                    }));

                    setGeneratedSlots(slotsWithChecked);
                    setShowSlotsModal(true);
                  } catch (error: any) {
                    console.error('Ошибка генерации слотов:', error);
                    alert('Ошибка генерации слотов: ' + (error.response?.data?.error || error.message));
                  } finally {
                    setGeneratingSlots(false);
                  }
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Сгенерировать слоты
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования предложения */}
      {showEditProposalModal && editingProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Редактировать публичное предложение
            </h2>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="font-medium">Дата:</span> {formatDate(editingProposal.date)}</div>
                <div><span className="font-medium">Время:</span> {editingProposal.startTime} - {editingProposal.endTime}</div>
                <div><span className="font-medium">Корт:</span> {editingProposal.courtName}</div>
              </div>
            </div>

            {/* Поля для привязки к турниру */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">Привязка к турниру (необязательно)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Турнир
                  </label>
                  <select
                    value={editProposalTournament}
                    onChange={(e) => {
                      setEditProposalTournament(e.target.value);
                      setEditProposalLevel('');
                      setEditProposalGroup('');
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Без привязки</option>
                    {tournaments.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Уровень
                  </label>
                  <select
                    value={editProposalLevel}
                    onChange={(e) => {
                      setEditProposalLevel(e.target.value);
                      setEditProposalGroup('');
                    }}
                    disabled={!editProposalTournament}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Все уровни</option>
                    {editProposalLevels.map(l => (
                      <option key={l.level} value={l.level}>{l.level} ({l.playerCount})</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Группа
                  </label>
                  <select
                    value={editProposalGroup}
                    onChange={(e) => setEditProposalGroup(e.target.value)}
                    disabled={!editProposalLevel}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Все группы</option>
                    {editProposalGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.level})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEditProposalModal(false);
                  setEditingProposal(null);
                  setEditProposalTournament('');
                  setEditProposalLevel('');
                  setEditProposalGroup('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSaveProposalEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно ответов игроков */}
      {showResponsesModal && editingProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Ответы игроков на предложение
            </h2>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="font-medium">Дата:</span> {formatDate(editingProposal.date)}</div>
                <div><span className="font-medium">Время:</span> {editingProposal.startTime} - {editingProposal.endTime}</div>
                <div><span className="font-medium">Корт:</span> {editingProposal.courtName}</div>
              </div>
            </div>

            {/* Добавление нового ответа */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">Добавить ответ игрока</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Игрок
                  </label>
                  <select
                    value={selectedPlayerForResponse}
                    onChange={(e) => setSelectedPlayerForResponse(e.target.value)}
                    disabled={loadingFilteredPlayers}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
                  >
                    <option value="">
                      {loadingFilteredPlayers 
                        ? 'Загрузка...' 
                        : editingProposal?.tournamentId 
                          ? `Игроки${editingProposal?.level ? ` (${editingProposal.level})` : ''}${editingProposal?.groupId ? ` (группа)` : ''}`
                          : 'Выберите игрока'}
                    </option>
                    {filteredPlayersForResponse.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {editingProposal?.tournamentId && (
                    <div className="text-xs text-gray-500 mt-1">
                      {filteredPlayersForResponse.length} игрок(ов) 
                      {editingProposal.level && ` уровня ${editingProposal.level}`}
                      {editingProposal.groupId && ' в группе'}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Статус
                  </label>
                  <select
                    value={selectedResponseStatus}
                    onChange={(e) => setSelectedResponseStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="READY">Доступен</option>
                    <option value="REJECTED">Отказ</option>
                    <option value="IGNORED">Игнор</option>
                    <option value="SENT">Отправлено</option>
                    <option value="THINKING">Думает</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleAddResponse}
                    disabled={!selectedPlayerForResponse}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Добавить
                  </button>
                </div>
              </div>
            </div>

            {/* Список ответов */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">
                Ответы игроков ({proposalResponses.length})
              </h3>
              
              {proposalResponses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Нет ответов игроков
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-1 text-left text-xs font-medium text-gray-500 uppercase">Группа</th>
                        <th className="p-1 text-left text-xs font-medium text-gray-500 uppercase">Игрок</th>
                        <th className="p-1 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                        <th className="p-1 text-left text-xs font-medium text-gray-500 uppercase">Обновлено</th>
                        <th className="p-1 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {[...proposalResponses].sort((a, b) => {
                        // Сортировка по группе (первая группа игрока)
                        const groupA = a.groups && a.groups.length > 0 ? a.groups[0] : null;
                        const groupB = b.groups && b.groups.length > 0 ? b.groups[0] : null;
                        
                        if (groupA && groupB) {
                          // Сначала по level, затем по name
                          if (groupA.level !== groupB.level) {
                            return groupA.level.localeCompare(groupB.level);
                          }
                          if (groupA.name !== groupB.name) {
                            return groupA.name.localeCompare(groupB.name);
                          }
                        } else if (groupA && !groupB) {
                          return -1; // Группа A идет первой
                        } else if (!groupA && groupB) {
                          return 1; // Группа B идет первой
                        } else if (!groupA && !groupB) {
                          // Оба без группы - сортируем по статусу
                        } else {
                          // Если группы одинаковые, сортируем по статусу
                        }
                        
                        // Если группы одинаковые или оба без группы, сортируем по статусу
                        const statusOrder: Record<string, number> = {
                          'READY': 1,      // Доступен
                          'THINKING': 2,   // Думает
                          'SENT': 3,       // Отправлено
                          'IGNORED': 4,    // Игнор
                          'REJECTED': 5    // Отказ
                        };
                        const statusA = statusOrder[a.status] || 99;
                        const statusB = statusOrder[b.status] || 99;
                        return statusA - statusB;
                      }).map((response, index, sortedArray) => {
                        const firstGroup = response.groups && response.groups.length > 0 ? response.groups[0] : null;
                        const groupName = firstGroup ? `${firstGroup.level}-${firstGroup.name}` : '-';
                        
                        // Проверяем, отличается ли группа от предыдущей строки
                        const prevResponse = index > 0 ? sortedArray[index - 1] : null;
                        const prevGroup = prevResponse?.groups && prevResponse.groups.length > 0 ? prevResponse.groups[0] : null;
                        const isNewGroup = index > 0 && (
                          !prevGroup || !firstGroup || 
                          prevGroup.level !== firstGroup.level || 
                          prevGroup.name !== firstGroup.name
                        );
                        
                        // Проверяем, является ли ответ непросмотренным
                        const isUnviewed = (response as any).isUnviewed === true;
                        const slotDateKey = toDateKey(editingProposal.date);
                        const hasScheduledMatchOnSlotDate = Boolean(
                          slotDateKey &&
                          (
                            (response.nextMatches || []).some(nextMatch => nextMatch?.dateKey === slotDateKey) ||
                            (response.nextMatchDateKeys || []).includes(slotDateKey)
                          )
                        );
                        
                        return (
                          <tr 
                      key={response.id}
                            className={`hover:bg-gray-50 ${isNewGroup ? 'border-t-2 border-gray-400' : 'border-t border-gray-200'} ${hasScheduledMatchOnSlotDate ? 'bg-orange-50' : ''} ${isUnviewed ? 'bg-yellow-50' : ''}`}
                            style={isNewGroup ? { borderTopWidth: '2px', borderTopColor: '#9ca3af' } : undefined}
                          >
                            <td className="p-1 whitespace-nowrap text-sm text-gray-900">
                              {firstGroup ? (
                                <button
                                  onClick={() => handleGroupClick(firstGroup, response.playerId, response.player.name)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                  title="Нажмите, чтобы посмотреть игроков группы и их матчи"
                                >
                                  {groupName}
                                </button>
                              ) : (
                                groupName
                              )}
                            </td>
                            <td className={`p-1 whitespace-nowrap text-sm font-medium ${hasScheduledMatchOnSlotDate ? 'text-orange-900' : 'text-gray-900'}`}>
                              <div className="flex flex-col">
                                {response.lastMatchDate && (
                                  <div className="mb-0.5 leading-tight" style={{ fontSize: '0.65rem' }}>
                                    <div>
                                      <span className="font-semibold text-gray-700">{response.lastMatchDate}</span>
                                      {response.lastMatchOpponent && (
                                        <span className="text-gray-500">{` • ${response.lastMatchOpponent}`}</span>
                                      )}
                                    </div>
                                    {response.lastMatchTournament && (
                                      <div className="mt-0 text-gray-400">{response.lastMatchTournament}</div>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <span>{response.player.name}</span>
                                  {hasScheduledMatchOnSlotDate && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                                      Игра в этот день
                                    </span>
                                  )}
                                </div>
                                {response.nextMatchDates && response.nextMatchDates.length > 0 && (
                                  <div className="mt-0.5 leading-tight" style={{ fontSize: '0.65rem' }}>
                                    {response.nextMatches && response.nextMatches.length > 0
                                      ? response.nextMatches.map((nextMatch: any, index: number) => (
                                          <div key={index}>
                                            <div>
                                              <span className="font-semibold text-gray-700">{nextMatch.date}</span>
                                              {nextMatch.opponent && (
                                                <span className="text-gray-500">{` • ${nextMatch.opponent}`}</span>
                                              )}
                                            </div>
                                            {nextMatch.tournamentName && (
                                              <div className="mt-0 text-gray-400">{nextMatch.tournamentName}</div>
                                            )}
                                          </div>
                                        ))
                                      : response.nextMatchDates.map((date: string, index: number) => (
                                          <div key={index}>{date}</div>
                                        ))
                                    }
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-1 whitespace-nowrap">
                        <select
                          value={response.status}
                          onChange={(e) => handleUpdateResponse(response.id, e.target.value)}
                          className={`px-3 py-1 rounded-md text-sm border border-gray-300 ${getStatusColor(response.status)}`}
                        >
                                <option value="READY">Доступен</option>
                          <option value="THINKING">Думает</option>
                                <option value="SENT">Отправлено</option>
                                <option value="IGNORED">Игнор</option>
                                <option value="REJECTED">Отказ</option>
                        </select>
                            </td>
                            <td className="p-1 whitespace-nowrap text-xs text-gray-500">
                              {new Date(response.updatedAt).toLocaleString('ru-RU')}
                            </td>
                            <td className="p-1 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteResponse(response.id)}
                                className="text-red-600 hover:text-red-900"
                      >
                        Удалить
                      </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={async () => {
                  setShowResponsesModal(false);
                  setEditingProposal(null);
                  setProposalResponses([]);
                  setSelectedPlayerForResponse('');
                  setSelectedResponseStatus('READY');
                  setFilteredPlayersForResponse([]);
                  // Обновляем список просмотренных ответов перед обновлением счетчиков
                  await fetchViewedResponses();
                  // Обновляем счетчики после закрытия модалки
                  await fetchPublicProposals();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {showAvailabilityStatusModal && availabilityStatusTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Изменить статус доступности</h3>
            <p className="text-sm text-gray-600 mb-4">
              Игрок: <span className="font-medium text-gray-900">{availabilityStatusTarget.playerName}</span>
            </p>
            <div className="space-y-2">
              {availabilityStatusOptions.map((option) => {
                const isCurrent = availabilityStatusTarget.currentStatus === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleUpdateAvailabilityStatus(option.value)}
                    disabled={updatingAvailabilityStatus}
                    className={`w-full text-left px-3 py-2 rounded-md border text-sm hover:bg-gray-50 disabled:opacity-50 ${
                      isCurrent ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{option.icon}</span>
                    {option.label}
                    {isCurrent ? ' (текущий)' : ''}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAvailabilityStatusModal(false);
                  setAvailabilityStatusTarget(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={updatingAvailabilityStatus}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {showReactionViewedModal && reactionViewedTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Признак просмотра реакции</h3>
            <p className="text-sm text-gray-700 mb-4">
              {reactionViewedTarget.viewed
                ? 'Пометить эту реакцию как просмотренную?'
                : 'Снять признак просмотра с этой реакции?'}
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Игрок: <span className="font-medium text-gray-900">{reactionViewedTarget.playerName}</span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowReactionViewedModal(false);
                  setReactionViewedTarget(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={updatingReactionViewed}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSetReactionViewed}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                disabled={updatingReactionViewed}
              >
                {updatingReactionViewed ? 'Сохранение...' : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAvailabilityInviteModal && availabilityInviteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Персональное оповещение</h3>
            <p className="text-sm text-gray-700 mb-4">
              Отправить персонально оповещение о наличии соперника и корта?
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Игрок: <span className="font-medium text-gray-900">{availabilityInviteTarget.playerName}</span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAvailabilityInviteModal(false);
                  setAvailabilityInviteTarget(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={sendingAvailabilityInvite}
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSendAvailabilityInvite}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                disabled={sendingAvailabilityInvite}
              >
                {sendingAvailabilityInvite ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно игроков группы */}
      {showGroupPlayersModal && selectedGroupForPlayers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Игроки группы: {selectedGroupForPlayers.level}-{selectedGroupForPlayers.name}
              {clickedPlayerName && ` (${clickedPlayerName})`}
            </h2>
            
            {loadingGroupPlayers ? (
              <div className="text-center py-8 text-gray-500">
                Загрузка игроков...
              </div>
            ) : groupPlayers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                В группе нет других игроков
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Игрок</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата матча</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groupPlayers.map((player) => {
                      const getStatusText = () => {
                        if (player.matchStatus === 'played') return 'играл';
                        if (player.matchStatus === 'scheduled') return 'сыграет';
                        // Если нет матча, но есть отказ в ответе на предложение
                        if (player.matchStatus === 'none' && player.responseStatus === 'REJECTED') return 'отказ';
                        // Если нет матча, но игрок думает
                        if (player.matchStatus === 'none' && player.responseStatus === 'THINKING') return 'думает';
                        // Если нет матча, но игрок приглашен
                        if (player.matchStatus === 'none' && player.responseStatus === 'SENT') return 'приглашен';
                        return null;
                      };
                      
                      const statusText = getStatusText();
                      const canInvite = player.matchStatus === 'none' && !player.hasResponse && groupPlayersProposalId;
                      const canCreateTask = player.matchStatus === 'none' && player.hasResponse && player.responseStatus === 'READY' && groupPlayersProposalId && clickedPlayerId;
                      const hasTask = !!player.taskId;
                      const slotDateKey = toDateKey(editingProposal?.date || undefined);
                      const hasScheduledMatchOnSlotDate =
                        player.matchStatus === 'scheduled' &&
                        Boolean(slotDateKey) &&
                        player.matchDateKey === slotDateKey;
                      
                      return (
                        <tr key={player.id} className={`hover:bg-gray-50 ${hasScheduledMatchOnSlotDate ? 'bg-orange-50' : ''}`}>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${hasScheduledMatchOnSlotDate ? 'text-orange-900' : 'text-gray-900'}`}>
                            <div className="flex items-center gap-2">
                              <span>{player.name}</span>
                              {hasScheduledMatchOnSlotDate && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                                  Игра в этот день
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {statusText || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {player.matchDate || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {canInvite && (
                              <button
                                onClick={async () => {
                                  if (!groupPlayersProposalId) return;
                                  try {
                                    setSendingInvitation(player.id);
                                    const result = await apiPost('/public-proposal-responses/send-notifications', {
                                      proposalId: groupPlayersProposalId,
                                      playerIds: [player.id],
                                      isInvitation: true // Указываем, что это приглашение из модального окна "Игроки группы"
                                    });
                                    alert(result.message || 'Приглашение отправлено');
                                    // Обновляем данные
                                    await fetchGroupPlayers(
                                      selectedGroupForPlayers.id,
                                      clickedPlayerId,
                                      groupPlayersTournamentId,
                                      groupPlayersLevel,
                                      groupPlayersProposalId
                                    );
                                  } catch (error: any) {
                                    console.error('Ошибка отправки приглашения:', error);
                                    alert('Ошибка отправки приглашения: ' + (error.response?.data?.error || error.message));
                                  } finally {
                                    setSendingInvitation(null);
                                  }
                                }}
                                disabled={sendingInvitation === player.id}
                                className="text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                              >
                                {sendingInvitation === player.id ? 'Отправка...' : 'пригласить'}
                              </button>
                            )}
                            {canCreateTask && (
                              <button
                                onClick={async () => {
                                  if (!groupPlayersProposalId || !clickedPlayerId) return;
                                  try {
                                    setCreatingTask(player.id);
                                    // Создаем задачу для двух игроков
                                    const result = await apiPost('/opponent-matching-tasks/create-selected', {
                                      proposalId: groupPlayersProposalId,
                                      selectedTasks: [{
                                        activePlayerId: clickedPlayerId,
                                        opponentPlayerId: player.id,
                                        groupId: selectedGroupForPlayers.id
                                      }]
                                    });
                                    alert(result.message || 'Задача создана');
                                    // Обновляем данные
                                    await fetchGroupPlayers(
                                      selectedGroupForPlayers.id,
                                      clickedPlayerId,
                                      groupPlayersTournamentId,
                                      groupPlayersLevel,
                                      groupPlayersProposalId
                                    );
                                  } catch (error: any) {
                                    console.error('Ошибка создания задачи:', error);
                                    alert('Ошибка создания задачи: ' + (error.response?.data?.error || error.message));
                                  } finally {
                                    setCreatingTask(null);
                                  }
                                }}
                                disabled={creatingTask === player.id}
                                className="text-green-600 hover:text-green-800 hover:underline disabled:opacity-50 ml-2"
                              >
                                {creatingTask === player.id ? 'Создание...' : 'создать задачу'}
                              </button>
                            )}
                            {hasTask && player.taskStatus && (
                              <span className="text-xs text-gray-500 ml-2">
                                Задача: {player.taskStatus}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowGroupPlayersModal(false);
                  setSelectedGroupForPlayers(null);
                  setClickedPlayerId(null);
                  setClickedPlayerName(null);
                  setGroupPlayersTournamentId(null);
                  setGroupPlayersLevel(null);
                  setGroupPlayersProposalId(null);
                  setGroupPlayers([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для копирования ответов */}
      {showCopyResponsesModal && sourceProposalForCopy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Копировать ответы с предложения
            </h2>
            
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="font-medium">Источник:</span> {formatDate(sourceProposalForCopy.date)} {sourceProposalForCopy.startTime} - {sourceProposalForCopy.endTime}, Корт {sourceProposalForCopy.courtName}</div>
                <div><span className="font-medium">Ответов:</span> {sourceProposalForCopy.responsesCount || 0}</div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Выберите целевое предложение:
              </label>
              <select
                value={selectedTargetProposalId}
                onChange={(e) => setSelectedTargetProposalId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">-- Выберите предложение --</option>
                {publicProposals
                  .filter(p => p.id !== sourceProposalForCopy.id)
                  .map(proposal => (
                    <option key={proposal.id} value={proposal.id}>
                      {formatDate(proposal.date)} {proposal.startTime} - {proposal.endTime}, {proposal.clubName}, Корт {proposal.courtName}
                      {proposal.tournamentName && ` (${proposal.tournamentName}${proposal.level ? `, ${proposal.level}` : ''})`}
                    </option>
                  ))}
              </select>
              <div className="text-xs text-gray-500 mt-1">
                Будет скопировано {sourceProposalForCopy.responsesCount || 0} ответ(ов)
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCopyResponsesModal(false);
                  setSourceProposalForCopy(null);
                  setSelectedTargetProposalId('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                disabled={copyingResponses}
              >
                Отмена
              </button>
              <button
                onClick={handleCopyResponses}
                disabled={!selectedTargetProposalId || copyingResponses}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {copyingResponses ? 'Копирование...' : 'Копировать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно отмены задачи с выбором причины */}
      {showTaskCancelModal && taskToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-2">Отмена задачи</h2>
            <p className="text-sm text-gray-600 mb-4">{taskToCancel.title}</p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Причина отмены
            </label>
            <select
              value={taskCancelReasonCode}
              onChange={(e) => setTaskCancelReasonCode(String(e.target.value || 'OTHER'))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
              disabled={taskCancelSubmitting}
            >
              {TASK_CANCEL_REASONS.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Комментарий (необязательно)
            </label>
            <textarea
              value={taskCancelComment}
              onChange={(e) => setTaskCancelComment(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-6"
              rows={3}
              placeholder="Добавьте комментарий к отмене"
              disabled={taskCancelSubmitting}
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={closeTaskCancelModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-60"
                disabled={taskCancelSubmitting}
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmTaskCancellation}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
                disabled={taskCancelSubmitting}
              >
                {taskCancelSubmitting ? 'Сохраняю...' : 'Подтвердить отмену'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно истории статусов задачи */}
      {showTaskHistoryModal && selectedTaskForHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              История изменений статусов задачи
            </h2>
            
            {taskHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                История изменений отсутствует
              </div>
            ) : (
              <div className="space-y-3">
                {taskHistory.map((entry, index) => (
                  <div key={entry.id || index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          entry.newStatus === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          entry.newStatus === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                          entry.newStatus === 'MATCH_PROPOSAL_CREATED' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getTaskStatusLabel(entry.newStatus)}
                        </span>
                        {entry.oldStatus && (
                          <span className="ml-2 text-xs text-gray-500">
                            (было: {getTaskStatusLabel(entry.oldStatus)})
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(entry.createdAt).toLocaleString('ru-RU')}
                      </div>
                    </div>
                    {entry.comment && (
                      <div className="text-sm text-gray-700 mt-2">
                        {entry.comment}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowTaskHistoryModal(false);
                  setSelectedTaskForHistory(null);
                  setTaskHistory([]);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно карточки задачи */}
      {showTaskCardModal && selectedTaskForCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              Карточка задачи подбора соперников
            </h2>
            
            {loadingTaskCard ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Загрузка...</div>
              </div>
            ) : (
              <>
                {/* Основная информация о задаче */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold mb-4">Основная информация</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Активный игрок</div>
                      <div className="text-sm text-gray-900">{selectedTaskForCard.activePlayer.name}</div>
                      <div className="text-xs text-gray-500">Игр: {selectedTaskForCard.gamesPlayedActive}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Соперник</div>
                      <div className="text-sm text-gray-900">{selectedTaskForCard.opponentPlayer.name}</div>
                      <div className="text-xs text-gray-500">Игр: {selectedTaskForCard.gamesPlayedOpponent}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Турнир</div>
                      <div className="text-sm text-gray-900">{selectedTaskForCard.tournament.name}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Группа</div>
                      <div className="text-sm text-gray-900">{selectedTaskForCard.group.name} ({selectedTaskForCard.group.level})</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Дата и время</div>
                      <div className="text-sm text-gray-900">
                        {formatDate(selectedTaskForCard.proposalDate)} {selectedTaskForCard.proposalStartTime} - {selectedTaskForCard.proposalEndTime}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Корт</div>
                      <div className="text-sm text-gray-900">
                        {selectedTaskForCard.court ? `Корт ${selectedTaskForCard.court.name}` : 'Не указан'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Приоритет</div>
                      <div className="text-sm text-gray-900">{selectedTaskForCard.priority}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Статус</div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getTaskStatusColor(selectedTaskForCard.status)}`}>
                        {getTaskStatusLabel(selectedTaskForCard.status)}
                      </span>
                    </div>
                    {selectedTaskForCard.reason && (
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-gray-700 mb-1">Причина</div>
                        <div className="text-sm text-gray-900">{selectedTaskForCard.reason}</div>
                      </div>
                    )}
                    {selectedTaskForCard.matchProposalId && (
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-gray-700 mb-1">Созданное предложение матча</div>
                        <div className="text-sm text-gray-900">ID: {selectedTaskForCard.matchProposalId}</div>
                      </div>
                    )}
                    <div className="col-span-2">
                      <div className="text-sm font-medium text-gray-700 mb-2">Уведомления</div>
                      <div className="space-y-3">
                        {/* Игрок №1 */}
                        <div className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={selectedTaskForCard.notificationSentToPlayer1 ? 'text-green-600' : 'text-gray-400'}>
                                {selectedTaskForCard.notificationSentToPlayer1 ? '✅' : '📧'}
                              </span>
                              <span className="text-sm font-medium text-gray-700">
                                Игрок №1: {selectedTaskForCard.activePlayer.name}
                              </span>
                              {selectedTaskForCard.notificationSentToPlayer1 && selectedTaskForCard.player1NotificationResponse && (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  selectedTaskForCard.player1NotificationResponse === 'READY' ? 'bg-green-100 text-green-800' :
                                  selectedTaskForCard.player1NotificationResponse === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  selectedTaskForCard.player1NotificationResponse === 'THINKING' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {selectedTaskForCard.player1NotificationResponse === 'READY' ? 'Доступен' :
                                   selectedTaskForCard.player1NotificationResponse === 'REJECTED' ? 'Отказ' :
                                   selectedTaskForCard.player1NotificationResponse === 'THINKING' ? 'Думает' : 'Игнор'}
                                </span>
                              )}
                            </div>
                            {selectedTaskForCard.notificationSentToPlayer1 && (
                              <select
                                value={selectedTaskForCard.player1NotificationResponse || ''}
                                onChange={async (e) => {
                                  const newResponse = e.target.value;
                                  if (!newResponse) return;
                                  
                                  try {
                                    const result = await apiPut(`/opponent-matching-tasks/${selectedTaskForCard.id}/notification-response`, {
                                      playerNumber: 1,
                                      response: newResponse
                                    });
                                    
                                    // Получаем обновленную задачу с сервера для получения актуального статуса
                                    const fullTask = await apiGet(`/opponent-matching-tasks/${selectedTaskForCard.id}`);
                                    
                                    // Обновляем локальное состояние с новым статусом
                                    setSelectedTaskForCard({
                                      ...selectedTaskForCard,
                                      status: fullTask.status || selectedTaskForCard.status,
                                      player1NotificationResponse: newResponse,
                                      player2NotificationResponse: fullTask.player2NotificationResponse || selectedTaskForCard.player2NotificationResponse
                                    });
                                    
                                    // Обновляем задачу в списке задач
                                    setOpponentMatchingTasks(prevTasks =>
                                      prevTasks.map(t =>
                                        t.id === selectedTaskForCard.id
                                          ? {
                                              ...t,
                                              status: fullTask.status || t.status,
                                              player1NotificationResponse: newResponse,
                                              player2NotificationResponse: fullTask.player2NotificationResponse || t.player2NotificationResponse
                                            }
                                          : t
                                      )
                                    );
                                    
                                    // Перезагружаем историю
                                    setTaskCardHistory(Array.isArray(fullTask.statusHistory) ? fullTask.statusHistory : []);
                                  } catch (error: any) {
                                    console.error('Ошибка обновления статуса ответа:', error);
                                    alert('Ошибка обновления статуса: ' + (error.response?.data?.error || error.message));
                                  }
                                }}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="">-- Выберите статус --</option>
                                <option value="READY">Доступен</option>
                                <option value="REJECTED">Отказ</option>
                                <option value="THINKING">Думает</option>
                                <option value="IGNORED">Игнор</option>
                              </select>
                            )}
                          </div>
                        </div>
                        
                        {/* Игрок №2 */}
                        <div className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={selectedTaskForCard.notificationSentToPlayer2 ? 'text-green-600' : 'text-gray-400'}>
                                {selectedTaskForCard.notificationSentToPlayer2 ? '✅' : '📧'}
                              </span>
                              <span className="text-sm font-medium text-gray-700">
                                Игрок №2: {selectedTaskForCard.opponentPlayer.name}
                              </span>
                              {selectedTaskForCard.notificationSentToPlayer2 && selectedTaskForCard.player2NotificationResponse && (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  selectedTaskForCard.player2NotificationResponse === 'READY' ? 'bg-green-100 text-green-800' :
                                  selectedTaskForCard.player2NotificationResponse === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  selectedTaskForCard.player2NotificationResponse === 'THINKING' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {selectedTaskForCard.player2NotificationResponse === 'READY' ? 'Доступен' :
                                   selectedTaskForCard.player2NotificationResponse === 'REJECTED' ? 'Отказ' :
                                   selectedTaskForCard.player2NotificationResponse === 'THINKING' ? 'Думает' : 'Игнор'}
                                </span>
                              )}
                            </div>
                            {selectedTaskForCard.notificationSentToPlayer2 && (
                              <select
                                value={selectedTaskForCard.player2NotificationResponse || ''}
                                onChange={async (e) => {
                                  const newResponse = e.target.value;
                                  if (!newResponse) return;
                                  
                                  try {
                                    const result = await apiPut(`/opponent-matching-tasks/${selectedTaskForCard.id}/notification-response`, {
                                      playerNumber: 2,
                                      response: newResponse
                                    });
                                    
                                    // Получаем обновленную задачу с сервера для получения актуального статуса
                                    const fullTask = await apiGet(`/opponent-matching-tasks/${selectedTaskForCard.id}`);
                                    
                                    // Обновляем локальное состояние с новым статусом
                                    setSelectedTaskForCard({
                                      ...selectedTaskForCard,
                                      status: fullTask.status || selectedTaskForCard.status,
                                      player1NotificationResponse: fullTask.player1NotificationResponse || selectedTaskForCard.player1NotificationResponse,
                                      player2NotificationResponse: newResponse
                                    });
                                    
                                    // Обновляем задачу в списке задач
                                    setOpponentMatchingTasks(prevTasks =>
                                      prevTasks.map(t =>
                                        t.id === selectedTaskForCard.id
                                          ? {
                                              ...t,
                                              status: fullTask.status || t.status,
                                              player1NotificationResponse: fullTask.player1NotificationResponse || t.player1NotificationResponse,
                                              player2NotificationResponse: newResponse
                                            }
                                          : t
                                      )
                                    );
                                    
                                    // Перезагружаем историю
                                    setTaskCardHistory(Array.isArray(fullTask.statusHistory) ? fullTask.statusHistory : []);
                                  } catch (error: any) {
                                    console.error('Ошибка обновления статуса ответа:', error);
                                    alert('Ошибка обновления статуса: ' + (error.response?.data?.error || error.message));
                                  }
                                }}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="">-- Выберите статус --</option>
                                <option value="READY">Доступен</option>
                                <option value="REJECTED">Отказ</option>
                                <option value="THINKING">Думает</option>
                                <option value="IGNORED">Игнор</option>
                              </select>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ответ клуба на запрос резерва корта */}
                {(() => {
                  const hasReservationRequest = taskCardHistory.some(
                    h => h.comment && h.comment.includes('Отправлен запрос на резерв корта')
                  );
                  
                  if (hasReservationRequest) {
                    return (
                      <div className="bg-blue-50 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold mb-4">Ответ клуба на запрос резерва корта</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Статус ответа:</span>
                              {selectedTaskForCard.clubReservationResponse && (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  selectedTaskForCard.clubReservationResponse === 'CONFIRMED' ? 'bg-green-100 text-green-800' :
                                  selectedTaskForCard.clubReservationResponse === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  selectedTaskForCard.clubReservationResponse === 'ALTERNATIVE' ? 'bg-yellow-100 text-yellow-800' :
                                  selectedTaskForCard.clubReservationResponse === 'IGNORED' ? 'bg-gray-100 text-gray-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {selectedTaskForCard.clubReservationResponse === 'CONFIRMED' ? 'Подтверждено' :
                                   selectedTaskForCard.clubReservationResponse === 'REJECTED' ? 'Отклонено' :
                                   selectedTaskForCard.clubReservationResponse === 'ALTERNATIVE' ? 'Альтернатива' :
                                   selectedTaskForCard.clubReservationResponse === 'IGNORED' ? 'Игнор' : 'Думает'}
                                </span>
                              )}
                            </div>
                            <select
                              value={selectedTaskForCard.clubReservationResponse || ''}
                              onChange={async (e) => {
                                const newResponse = e.target.value;
                                if (!newResponse) return;
                                
                                try {
                                  await apiPut(`/opponent-matching-tasks/${selectedTaskForCard.id}/club-reservation-response`, {
                                    response: newResponse
                                  });
                                  
                                  // Обновляем локальное состояние
                                  setSelectedTaskForCard({
                                    ...selectedTaskForCard,
                                    clubReservationResponse: newResponse
                                  });
                                  
                                  // Обновляем список задач
                                  await fetchOpponentMatchingTasks();
                                  
                                  // Перезагружаем историю
                                  const fullTask = await apiGet(`/opponent-matching-tasks/${selectedTaskForCard.id}`);
                                  setTaskCardHistory(Array.isArray(fullTask.statusHistory) ? fullTask.statusHistory : []);
                                } catch (error: any) {
                                  console.error('Ошибка обновления статуса ответа клуба:', error);
                                  alert('Ошибка обновления статуса: ' + (error.response?.data?.error || error.message));
                                }
                              }}
                              className="text-xs border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="">-- Выберите статус --</option>
                              <option value="CONFIRMED">Подтверждено</option>
                              <option value="REJECTED">Отклонено</option>
                              <option value="ALTERNATIVE">Альтернатива</option>
                              <option value="IGNORED">Игнор</option>
                              <option value="THINKING">Думает</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Кнопка "Назначить матч" */}
                {(() => {
                  const bothPlayersReady = 
                    selectedTaskForCard.player1NotificationResponse === 'READY' &&
                    selectedTaskForCard.player2NotificationResponse === 'READY';
                  const courtConfirmed = selectedTaskForCard.clubReservationResponse === 'CONFIRMED';
                  const canAssignMatch = bothPlayersReady && courtConfirmed && !selectedTaskForCard.matchProposalId;

                  if (canAssignMatch) {
                    return (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-green-900 mb-1">Готово к назначению матча</h3>
                            <p className="text-sm text-green-700">
                              Оба игрока готовы и корт подтвержден. Можно назначить матч.
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                await handleCreateMatchProposalFromTask(selectedTaskForCard.id);
                                // Обновляем карточку задачи
                                const fullTask = await apiGet(`/opponent-matching-tasks/${selectedTaskForCard.id}`);
                                setSelectedTaskForCard({
                                  ...selectedTaskForCard,
                                  matchProposalId: fullTask.matchProposalId
                                });
                                setTaskCardHistory(Array.isArray(fullTask.statusHistory) ? fullTask.statusHistory : []);
                                // Обновляем список задач
                                await fetchOpponentMatchingTasks();
                                alert('Матч назначен успешно!');
                              } catch (error: any) {
                                console.error('Ошибка назначения матча:', error);
                                alert('Ошибка назначения матча: ' + (error.response?.data?.error || error.message));
                              }
                            }}
                            className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm"
                          >
                            Назначить матч
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* История движений */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">История движений</h3>
                  
                  {taskCardHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      История движений отсутствует
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {taskCardHistory.map((entry, index) => (
                        <div key={entry.id || index} className="border border-gray-200 rounded-lg p-4 bg-white">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  entry.newStatus === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                  entry.newStatus === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                  entry.newStatus === 'MATCH_PROPOSAL_CREATED' ? 'bg-blue-100 text-blue-800' :
                                  entry.newStatus === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                                  entry.newStatus === 'SKIPPED' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {getTaskStatusLabel(entry.newStatus)}
                                </span>
                                {entry.oldStatus && (
                                  <span className="text-xs text-gray-500">
                                    ← {getTaskStatusLabel(entry.oldStatus)}
                                  </span>
                                )}
                              </div>
                              {entry.comment && (
                                <div className="text-sm text-gray-700 mt-2 bg-gray-50 p-2 rounded">
                                  {entry.comment}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 ml-4">
                              {new Date(entry.createdAt).toLocaleString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                          {entry.changedBy && (
                            <div className="text-xs text-gray-500 mt-2">
                              Изменил: {entry.changedBy}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowTaskCardModal(false);
                      setSelectedTaskForCard(null);
                      setTaskCardHistory([]);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Закрыть
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно предпросмотра задач */}
      {showTaskPreviewModal && previewProposalId && (() => {
        const currentProposal = publicProposals.find(p => p.id === previewProposalId);
        const proposalInfo = currentProposal 
          ? `${formatDate(currentProposal.date)} ${currentProposal.startTime} - ${currentProposal.endTime}, ${currentProposal.clubName} - Корт ${currentProposal.courtName}`
          : '';
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-2">
              Предпросмотр задач подбора соперников
            </h2>
            {proposalInfo && (
              <div className="text-sm text-gray-600 mb-4 pb-3 border-b">
                Предложение: {proposalInfo}
              </div>
            )}
            
            {loadingPreview ? (
              <div className="text-center py-8">Загрузка...</div>
            ) : previewTasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Нет задач для создания
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-700 space-y-1">
                    <div><span className="font-medium">Всего задач:</span> {previewTasks.length}</div>
                    <div><span className="font-medium">Будет создано:</span> {previewTasks.filter(t => !t.wouldSkip).length}</div>
                    <div><span className="font-medium">Будет пропущено:</span> {previewTasks.filter(t => t.wouldSkip).length}</div>
                  </div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        const tasksToSelect = previewTasks
                          .map(t => `${t.activePlayerId}-${t.opponentPlayerId}-${t.groupId}`);
                        setSelectedPreviewTasks(new Set(tasksToSelect));
                      }}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Выбрать все
                    </button>
                    <button
                      onClick={() => setSelectedPreviewTasks(new Set())}
                      className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                    >
                      Снять все
                    </button>
                    <div className="flex gap-2 ml-auto">
                      <button
                        onClick={() => {
                          setShowTaskPreviewModal(false);
                          setPreviewProposalId(null);
                          setPreviewTasks([]);
                          setSelectedPreviewTasks(new Set());
                        }}
                        className="px-4 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                        disabled={creatingSelectedTasks}
                      >
                        Отмена
                      </button>
                      <button
                        onClick={async () => {
                          if (selectedPreviewTasks.size === 0) {
                            alert('Выберите хотя бы одну задачу для создания');
                            return;
                          }

                          if (!confirm(`Создать ${selectedPreviewTasks.size} выбранных задач?`)) {
                            return;
                          }

                          try {
                            setCreatingSelectedTasks(true);
                            const selectedTasks = Array.from(selectedPreviewTasks).map(taskKey => {
                              const [activePlayerId, opponentPlayerId, groupId] = taskKey.split('-');
                              return { activePlayerId, opponentPlayerId, groupId };
                            });

                            await apiPost('/opponent-matching-tasks/create-selected', {
                              proposalId: previewProposalId,
                              selectedTasks
                            });

                            alert(`Успешно создано ${selectedPreviewTasks.size} задач!`);
                            setShowTaskPreviewModal(false);
                            setPreviewProposalId(null);
                            setPreviewTasks([]);
                            setSelectedPreviewTasks(new Set());
                            await fetchPublicProposals();
                          } catch (error: any) {
                            console.error('Ошибка создания выбранных задач:', error);
                            alert('Ошибка создания задач: ' + (error.response?.data?.error || error.message));
                          } finally {
                            setCreatingSelectedTasks(false);
                          }
                        }}
                        disabled={creatingSelectedTasks || selectedPreviewTasks.size === 0}
                        className="px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {creatingSelectedTasks ? 'Создание...' : `Создать (${selectedPreviewTasks.size})`}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {previewTasks.map((task, index) => {
                    const taskKey = `${task.activePlayerId}-${task.opponentPlayerId}-${task.groupId}`;
                    const isSelected = selectedPreviewTasks.has(taskKey);

                    // Проверяем, нужно ли подсветить персиковым цветом
                    const isOpponentReadyOnOtherProposal = !task.wouldSkip && 
                      task.reason === 'Соперник READY на другое предложение' && 
                      task.opponentReadyProposalInfo;

                    return (
                      <div
                        key={taskKey}
                        className={`border rounded-lg p-3 ${
                          task.wouldSkip
                            ? 'bg-gray-100 border-gray-300'
                            : isOpponentReadyOnOtherProposal
                              ? 'bg-orange-50 border-orange-300'
                              : isSelected
                                ? 'bg-blue-50 border-blue-300'
                                : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSet = new Set(selectedPreviewTasks);
                              if (e.target.checked) {
                                newSet.add(taskKey);
                              } else {
                                newSet.delete(taskKey);
                              }
                              setSelectedPreviewTasks(newSet);
                            }}
                            className="mt-1 rounded border-gray-300"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-900">{task.activePlayerName}</span>
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium">
                                  ✓ Доступен
                                </span>
                              </div>
                              <span className="text-gray-500">vs</span>
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-gray-900">{task.opponentPlayerName}</span>
                                {!task.wouldSkip && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium">
                                    ✓ Доступен
                                  </span>
                                )}
                                {task.wouldSkip && (
                                  <span className="px-2 py-0.5 bg-gray-300 text-gray-700 text-xs rounded">
                                    Пропущено
                                  </span>
                                )}
                              </div>
                              {isSelected && !task.wouldSkip && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded">
                                  Выбрано
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>Группа: {task.groupName} ({task.groupLevel})</div>
                              {!task.wouldSkip && (
                                <>
                                  <div>Приоритет: {task.priority} (Игр: {task.gamesPlayedActive} + {task.gamesPlayedOpponent} = {task.gamesPlayedActive + task.gamesPlayedOpponent})</div>
                                  <div>Причина: {task.reason}</div>
                                  {task.reason === 'Соперник READY на другое предложение' && task.opponentReadyProposalInfo && (
                                    <div className="text-orange-700 font-medium mt-1">
                                      Соперник готов на: {task.opponentReadyProposalInfo.clubName} - {task.opponentReadyProposalInfo.courtName} ({task.opponentReadyProposalInfo.startTime} - {task.opponentReadyProposalInfo.endTime})
                                    </div>
                                  )}
                                </>
                              )}
                              {task.wouldSkip && task.skipReason && (
                                <div className="text-red-600">Причина пропуска: {task.skipReason}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Выбрано: {selectedPreviewTasks.size} из {previewTasks.filter(t => !t.wouldSkip).length} доступных
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowTaskPreviewModal(false);
                        setPreviewProposalId(null);
                        setPreviewTasks([]);
                        setSelectedPreviewTasks(new Set());
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                      disabled={creatingSelectedTasks}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={async () => {
                        if (selectedPreviewTasks.size === 0) {
                          alert('Выберите хотя бы одну задачу для создания');
                          return;
                        }

                        if (!confirm(`Создать ${selectedPreviewTasks.size} выбранных задач?`)) {
                          return;
                        }

                        try {
                          setCreatingSelectedTasks(true);
                          const selectedTasksData = Array.from(selectedPreviewTasks).map(key => {
                            const [activePlayerId, opponentPlayerId, groupId] = key.split('-');
                            return { activePlayerId, opponentPlayerId, groupId };
                          });

                          const result = await apiPost('/opponent-matching-tasks/create-selected', {
                            proposalId: previewProposalId,
                            selectedTasks: selectedTasksData
                          });

                          alert(result.message || `Создано ${result.created} задач`);
                          
                          // Закрываем модальное окно и обновляем список предложений
                          setShowTaskPreviewModal(false);
                          setPreviewProposalId(null);
                          setPreviewTasks([]);
                          setSelectedPreviewTasks(new Set());
                          await fetchPublicProposals();
                        } catch (error: any) {
                          console.error('Ошибка создания выбранных задач:', error);
                          alert('Ошибка создания задач: ' + (error.response?.data?.error || error.message));
                        } finally {
                          setCreatingSelectedTasks(false);
                        }
                      }}
                      disabled={creatingSelectedTasks || selectedPreviewTasks.size === 0}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {creatingSelectedTasks ? 'Создание...' : `Создать (${selectedPreviewTasks.size})`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {/* Модальное окно для отправки уведомлений о слотах */}
      {showNotificationModal && notificationProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Отправить уведомление о слоте
            </h2>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-700">
                <div><strong>Дата:</strong> {formatDate(notificationProposal.date)}</div>
                <div><strong>Время:</strong> {notificationProposal.startTime} - {notificationProposal.endTime}</div>
                <div><strong>Клуб:</strong> {notificationProposal.clubName}</div>
                <div><strong>Корт:</strong> Корт {notificationProposal.courtName}</div>
                {notificationProposal.tournamentName && (
                  <div><strong>Турнир:</strong> {notificationProposal.tournamentName}</div>
                )}
              </div>
            </div>

            {/* Табы для выбора получателей */}
            <div className="mb-4 border-b border-gray-200">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setNotificationTab('groups');
                    setSelectedPlayersForNotification(new Set());
                  }}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    notificationTab === 'groups'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Группы
                </button>
                <button
                  onClick={() => {
                    setNotificationTab('players');
                  }}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    notificationTab === 'players'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Игроки
                </button>
              </div>
            </div>

            {notificationTab === 'players' ? (
              loadingPlayers ? (
                <div className="text-center py-8">Загрузка списка игроков...</div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Выберите игроков для отправки уведомления:
                      </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const playersWithTelegram = allPlayers.filter(p => p.telegramChatId).map(p => p.id);
                          setSelectedPlayersForNotification(new Set(playersWithTelegram));
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Выбрать всех с Telegram
                      </button>
                      <button
                        onClick={() => setSelectedPlayersForNotification(new Set())}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Снять все
                      </button>
                    </div>
                  </div>
                  
                  <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                    {allPlayers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">Нет игроков</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {allPlayers.map((player) => {
                          const hasTelegram = !!player.telegramChatId;
                          const isSelected = selectedPlayersForNotification.has(player.id);
                          
                          return (
                            <label
                              key={player.id}
                              className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                                !hasTelegram ? 'opacity-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const newSet = new Set(selectedPlayersForNotification);
                                  if (e.target.checked) {
                                    newSet.add(player.id);
                                  } else {
                                    newSet.delete(player.id);
                                  }
                                  setSelectedPlayersForNotification(newSet);
                                }}
                                disabled={!hasTelegram}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{player.name}</div>
                                {!hasTelegram && (
                                  <div className="text-xs text-gray-500">Нет Telegram аккаунта</div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2 text-sm text-gray-600">
                    Выбрано: {selectedPlayersForNotification.size} из {allPlayers.filter(p => p.telegramChatId).length} игроков с Telegram
                  </div>
                </div>
                </>
              )
            ) : (
              loadingGroups ? (
                <div className="text-center py-8">Загрузка списка групп...</div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Выберите группы и топики для отправки уведомления:
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const allGroupsAndTopics = new Set<string>();
                            allGroups.forEach(group => {
                              // Добавляем основную группу (без топика)
                              allGroupsAndTopics.add(group.chatId);
                              // Добавляем все топики группы
                              group.topics.forEach(topic => {
                                allGroupsAndTopics.add(`${group.chatId}:${topic.threadId}`);
                              });
                            });
                            setSelectedGroupsAndTopics(allGroupsAndTopics);
                          }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          Выбрать все
                        </button>
                        <button
                          onClick={() => setSelectedGroupsAndTopics(new Set())}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          Снять все
                        </button>
                      </div>
                    </div>
                    
                    <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                      {allGroups.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">Нет групп</div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {[...allGroups]
                            .sort((a, b) => {
                              const getSelectedWeight = (group: GroupWithTopics): number => {
                                let weight = selectedGroupsAndTopics.has(group.chatId) ? 1 : 0;
                                group.topics.forEach((topic) => {
                                  if (selectedGroupsAndTopics.has(`${group.chatId}:${topic.threadId}`)) {
                                    weight += 1;
                                  }
                                });
                                return weight;
                              };

                              const aWeight = getSelectedWeight(a);
                              const bWeight = getSelectedWeight(b);
                              const aHasSelection = aWeight > 0;
                              const bHasSelection = bWeight > 0;
                              if (aHasSelection !== bHasSelection) return aHasSelection ? -1 : 1;
                              if (aWeight !== bWeight) return bWeight - aWeight;

                              const aName = String(a.title || a.username || a.chatId);
                              const bName = String(b.title || b.username || b.chatId);
                              return aName.localeCompare(bName, 'ru');
                            })
                            .map((group) => {
                            const groupKey = group.chatId;
                            const isGroupSelected = selectedGroupsAndTopics.has(groupKey);
                            const groupDisplayName = group.title || group.username || group.chatId;
                            
                            return (
                              <div key={group.chatId} className="p-3">
                                {/* Группа (основной поток) */}
                                <label className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isGroupSelected}
                                    onChange={(e) => {
                                      const newSet = new Set(selectedGroupsAndTopics);
                                      if (e.target.checked) {
                                        newSet.add(groupKey);
                                      } else {
                                        newSet.delete(groupKey);
                                        // При снятии группы также снимаем все топики
                                        group.topics.forEach(topic => {
                                          newSet.delete(`${group.chatId}:${topic.threadId}`);
                                        });
                                      }
                                      setSelectedGroupsAndTopics(newSet);
                                    }}
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {groupDisplayName}
                                      {!group.title && !group.username && (
                                        <span className="text-xs text-gray-500 ml-2">(приватная группа)</span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500">Основной поток</div>
                                  </div>
                                </label>
                                
                                {/* Топики группы */}
                                {group.topics.length > 0 && (
                                  <div className="ml-8 mt-2 space-y-1">
                                    {group.topics.map((topic) => {
                                      const topicKey = `${group.chatId}:${topic.threadId}`;
                                      const isTopicSelected = selectedGroupsAndTopics.has(topicKey);
                                      
                                      return (
                                        <label
                                          key={topic.id}
                                          className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isTopicSelected}
                                            onChange={(e) => {
                                              const newSet = new Set(selectedGroupsAndTopics);
                                              if (e.target.checked) {
                                                newSet.add(topicKey);
                                              } else {
                                                newSet.delete(topicKey);
                                              }
                                              setSelectedGroupsAndTopics(newSet);
                                            }}
                                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                          />
                                          <div className="flex-1">
                                            <div className="text-sm text-gray-700">
                                              {topic.name || `Топик ${topic.threadId}`}
                                            </div>
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-2 text-sm text-gray-600">
                      Выбрано: {selectedGroupsAndTopics.size} групп/топиков
                    </div>
                  </div>
                </>
              )
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowNotificationModal(false);
                  setNotificationProposal(null);
                  setSelectedPlayersForNotification(new Set());
                  setNotificationTab('groups');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                disabled={sendingNotifications}
              >
                Отмена
              </button>
              <button
                onClick={async () => {
                  if (notificationTab === 'players') {
                    if (selectedPlayersForNotification.size === 0) {
                      alert('Выберите хотя бы одного игрока для отправки уведомления');
                      return;
                    }

                    try {
                      setSendingNotifications(true);
                      
                      const result = await apiPost('/public-proposal-responses/send-notifications', {
                        proposalId: notificationProposal.id,
                        playerIds: Array.from(selectedPlayersForNotification)
                      });

                      alert(result.message || `Уведомления отправлены ${result.sent || selectedPlayersForNotification.size} игрокам`);
                      
                      // Обновляем количество отправленных уведомлений
                      await fetchNotificationCounts();
                      
                      setShowNotificationModal(false);
                      setNotificationProposal(null);
                      setSelectedPlayersForNotification(new Set());
                      setNotificationTab('groups');
                    } catch (error: any) {
                      console.error('Ошибка отправки уведомлений:', error);
                      alert('Ошибка отправки уведомлений: ' + (error.response?.data?.error || error.message));
                    } finally {
                      setSendingNotifications(false);
                    }
                  } else {
                    // Отправка в группы/топики
                    if (selectedGroupsAndTopics.size === 0) {
                      alert('Выберите хотя бы одну группу или топик для отправки уведомления');
                      return;
                    }

                    try {
                      setSendingNotifications(true);
                      
                      // Разделяем на группы и топики
                      const groups: string[] = [];
                      const topics: Array<{ chatId: string; threadId: string }> = [];
                      
                      selectedGroupsAndTopics.forEach(key => {
                        if (key.includes(':')) {
                          // Это топик
                          const [chatId, threadId] = key.split(':');
                          topics.push({ chatId, threadId });
                        } else {
                          // Это группа (основной поток)
                          groups.push(key);
                        }
                      });
                      
                      const result = await apiPost('/public-proposal-responses/send-notifications-to-groups', {
                        proposalId: notificationProposal.id,
                        groups,
                        topics
                      });

                      alert(result.message || `Уведомления отправлены в ${groups.length + topics.length} групп/топиков`);
                      
                      // Сохраняем выбранные группы/топики в localStorage
                      try {
                        const arrayToSave = Array.from(selectedGroupsAndTopics);
                        localStorage.setItem('notificationSelectedGroupsAndTopics', JSON.stringify(arrayToSave));
                      } catch (e) {
                        console.error('Ошибка сохранения групп/топиков:', e);
                      }
                      
                      // Обновляем количество отправленных уведомлений
                      await fetchNotificationCounts();
                      
                      setShowNotificationModal(false);
                      setNotificationProposal(null);
                      // НЕ очищаем selectedGroupsAndTopics - они сохранены в localStorage
                      setSelectedPlayersForNotification(new Set());
                      setNotificationTab('groups');
                    } catch (error: any) {
                      console.error('Ошибка отправки уведомлений в группы:', error);
                      alert('Ошибка отправки уведомлений: ' + (error.response?.data?.error || error.message));
                    } finally {
                      setSendingNotifications(false);
                    }
                  }
                }}
                disabled={sendingNotifications || 
                  (notificationTab === 'players' && selectedPlayersForNotification.size === 0) ||
                  (notificationTab === 'groups' && selectedGroupsAndTopics.size === 0)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sendingNotifications ? 'Отправка...' : 
                  notificationTab === 'players' 
                    ? `Отправить (${selectedPlayersForNotification.size})`
                    : `Отправить (${selectedGroupsAndTopics.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для пакетной отправки уведомлений */}
      {showBatchNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Пакетное уведомление
            </h2>
            
            {/* Выбор слотов */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Выберите слоты:
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const allProposalIds = groupedBatchProposals.flatMap((p) => p.proposalIds);
                      setSelectedProposalsForBatch(new Set(allProposalIds));
                    }}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    Выбрать все
                  </button>
                  <button
                    onClick={() => setSelectedProposalsForBatch(new Set())}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Снять все
                  </button>
                </div>
              </div>
              
              <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                {groupedBatchProposals.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">Нет доступных предложений</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {groupedBatchProposals.map((proposalGroup) => {
                      const selectedInGroup = proposalGroup.proposalIds.filter((id) => selectedProposalsForBatch.has(id)).length;
                      const isSelected = selectedInGroup === proposalGroup.proposalIds.length && proposalGroup.proposalIds.length > 0;
                      const sentCount = proposalGroup.proposalIds.reduce((acc, id) => acc + (notificationCounts[id] || 0), 0);
                      
                      return (
                        <label
                          key={proposalGroup.key}
                          className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSet = new Set(selectedProposalsForBatch);
                              if (e.target.checked) {
                                proposalGroup.proposalIds.forEach((id) => newSet.add(id));
                              } else {
                                proposalGroup.proposalIds.forEach((id) => newSet.delete(id));
                              }
                              setSelectedProposalsForBatch(newSet);
                            }}
                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex-1 text-sm">
                            <div className="font-medium text-gray-900">
                              {formatDate(proposalGroup.date)} {proposalGroup.startTime} - {proposalGroup.endTime} {proposalGroup.clubName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {proposalGroup.tournamentName || 'Без турнира'}
                              {proposalGroup.level ? ` • ${proposalGroup.level}` : ''}
                              {proposalGroup.groupName ? ` • ${proposalGroup.groupName}` : ''}
                              {proposalGroup.proposalIds.length > 1 ? ` • ${proposalGroup.proposalIds.length} кортов` : ''}
                            </div>
                            {sentCount > 0 && (
                              <div className="text-xs text-blue-600">Отправлено: {sentCount}</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="mt-2 text-sm text-gray-600">
                Выбрано: {selectedGroupedBatchProposals.length} из {groupedBatchProposals.length} временных слотов
              </div>
            </div>

            {/* Выбор адресатов */}
            <div className="mb-6">
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setBatchNotificationTab('groups')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      batchNotificationTab === 'groups'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    type="button"
                  >
                    Группы
                  </button>
                  <button
                    onClick={() => setBatchNotificationTab('players')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      batchNotificationTab === 'players'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    type="button"
                  >
                    Игроки
                  </button>
                </nav>
              </div>

              {batchNotificationTab === 'players' ? (
              <>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Выберите игроков:
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      if (selectedGroupedBatchProposals.length === 0) {
                        alert('Сначала выберите слоты');
                        return;
                      }
                      await fetchBatchPlayers();
                    }}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    Загрузить игроков
                  </button>
                  <button
                    onClick={() => {
                      const playersWithTelegram = batchAllPlayers.filter(p => p.telegramChatId).map(p => p.id);
                      setSelectedPlayersForBatch(new Set(playersWithTelegram));
                    }}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    disabled={batchAllPlayers.length === 0}
                  >
                    Выбрать всех с Telegram
                  </button>
                  <button
                    onClick={() => setSelectedPlayersForBatch(new Set())}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    Снять все
                  </button>
                </div>
              </div>
              
              {loadingBatchPlayers ? (
                <div className="text-center py-8 text-gray-500">Загрузка списка игроков...</div>
              ) : (
                <>
                  <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                    {batchAllPlayers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {selectedGroupedBatchProposals.length === 0 
                          ? 'Выберите слоты и нажмите "Загрузить игроков"'
                          : 'Нет игроков с Telegram аккаунтами'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {batchAllPlayers.map((player) => {
                          const hasTelegram = !!player.telegramChatId;
                          const isSelected = selectedPlayersForBatch.has(player.id);
                          
                          return (
                            <label
                              key={player.id}
                              className={`flex items-center p-2 hover:bg-gray-50 cursor-pointer ${
                                !hasTelegram ? 'opacity-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const newSet = new Set(selectedPlayersForBatch);
                                  if (e.target.checked) {
                                    newSet.add(player.id);
                                  } else {
                                    newSet.delete(player.id);
                                  }
                                  setSelectedPlayersForBatch(newSet);
                                }}
                                disabled={!hasTelegram}
                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="flex-1 text-sm">
                                <div className="font-medium text-gray-900">{player.name}</div>
                                {!hasTelegram && (
                                  <div className="text-xs text-gray-500">Нет Telegram аккаунта</div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2 text-sm text-gray-600">
                    Выбрано: {selectedPlayersForBatch.size} из {batchAllPlayers.filter(p => p.telegramChatId).length} игроков с Telegram
                  </div>
                </>
              )}
              </>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Выберите группы и топики для отправки:
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const allGroupsAndTopics = new Set<string>();
                          allGroups.forEach(group => {
                            allGroupsAndTopics.add(group.chatId);
                            group.topics.forEach(topic => {
                              allGroupsAndTopics.add(`${group.chatId}:${topic.threadId}`);
                            });
                          });
                          setSelectedGroupsAndTopics(allGroupsAndTopics);
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Выбрать все
                      </button>
                      <button
                        onClick={() => setSelectedGroupsAndTopics(new Set())}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Снять все
                      </button>
                    </div>
                  </div>

                  <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                    {loadingGroups ? (
                      <div className="p-4 text-center text-gray-500">Загрузка групп...</div>
                    ) : allGroups.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">Нет доступных групп</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {[...allGroups]
                          .sort((a, b) => {
                            const weightA = selectedGroupsAndTopics.has(a.chatId) ? 1 : 0;
                            const weightB = selectedGroupsAndTopics.has(b.chatId) ? 1 : 0;
                            if (weightA !== weightB) return weightB - weightA;
                            return (a.title || a.username || '').localeCompare(b.title || b.username || '', 'ru');
                          })
                          .map((group) => {
                            const groupKey = group.chatId;
                            const isGroupSelected = selectedGroupsAndTopics.has(groupKey);
                            const groupDisplayName = group.title || (group.username ? `@${group.username}` : `ID: ${group.chatId}`);
                            return (
                              <div key={group.chatId} className="p-2">
                                <label className="flex items-center p-2 hover:bg-gray-50 cursor-pointer rounded">
                                  <input
                                    type="checkbox"
                                    checked={isGroupSelected}
                                    onChange={(e) => {
                                      const newSet = new Set(selectedGroupsAndTopics);
                                      if (e.target.checked) newSet.add(groupKey);
                                      else newSet.delete(groupKey);
                                      setSelectedGroupsAndTopics(newSet);
                                    }}
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{groupDisplayName}</div>
                                    <div className="text-xs text-gray-500">Основной поток</div>
                                  </div>
                                </label>

                                {group.topics.length > 0 && (
                                  <div className="ml-8 mt-2 space-y-1">
                                    {group.topics.map((topic) => {
                                      const topicKey = `${group.chatId}:${topic.threadId}`;
                                      const isTopicSelected = selectedGroupsAndTopics.has(topicKey);
                                      return (
                                        <label key={topic.id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer rounded">
                                          <input
                                            type="checkbox"
                                            checked={isTopicSelected}
                                            onChange={(e) => {
                                              const newSet = new Set(selectedGroupsAndTopics);
                                              if (e.target.checked) newSet.add(topicKey);
                                              else newSet.delete(topicKey);
                                              setSelectedGroupsAndTopics(newSet);
                                            }}
                                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                          />
                                          <div className="flex-1 text-sm text-gray-700">
                                            {topic.name || `Топик ${topic.threadId}`}
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-gray-600">
                    Выбрано: {selectedGroupsAndTopics.size} групп/топиков
                  </div>
                </>
              )}
            </div>

            {/* Кнопки действий */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowBatchNotificationModal(false);
                  setSelectedProposalsForBatch(new Set());
                  setSelectedPlayersForBatch(new Set());
                  setBatchAllPlayers([]);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                disabled={sendingBatchNotifications}
              >
                Отмена
              </button>
              <button
                onClick={async () => {
                  if (selectedGroupedBatchProposals.length === 0) {
                    alert('Выберите хотя бы один слот');
                    return;
                  }
                  try {
                    setSendingBatchNotifications(true);

                    if (batchNotificationTab === 'players') {
                      if (selectedPlayersForBatch.size === 0) {
                        alert('Выберите хотя бы одного игрока');
                        return;
                      }

                      const result = await apiPost('/public-proposal-responses/send-batch-notifications', {
                        proposalIds: Array.from(selectedProposalsForBatch),
                        playerIds: Array.from(selectedPlayersForBatch)
                      });

                      let message = result.message || `Уведомления отправлены ${result.sent || 0} игрокам`;
                      if (result.errors && result.errors.length > 0) {
                        message += '\n\nОшибки:\n' + result.errors.slice(0, 10).join('\n');
                        if (result.errors.length > 10) {
                          message += `\n... и еще ${result.errors.length - 10} ошибок`;
                        }
                      }
                      alert(message);
                    } else {
                      if (selectedGroupsAndTopics.size === 0) {
                        alert('Выберите хотя бы одну группу или топик');
                        return;
                      }

                      const groups: string[] = [];
                      const topics: Array<{ chatId: string; threadId: string }> = [];
                      selectedGroupsAndTopics.forEach((key) => {
                        if (key.includes(':')) {
                          const [chatId, threadId] = key.split(':');
                          topics.push({ chatId, threadId });
                        } else {
                          groups.push(key);
                        }
                      });

                      const selectedRepresentativeProposalIds = selectedGroupedBatchProposals
                        .map((proposalGroup) => proposalGroup.proposalIds[0])
                        .filter(Boolean);

                      let sentTo = 0;
                      const errors: string[] = [];
                      for (const proposalId of selectedRepresentativeProposalIds) {
                        try {
                          const result = await apiPost('/public-proposal-responses/send-notifications-to-groups', {
                            proposalId,
                            groups,
                            topics
                          });
                          sentTo += Number(result?.sent || 0);
                        } catch (error: any) {
                          errors.push(`${proposalId}: ${error?.response?.data?.error || error?.message || 'ошибка'}`);
                        }
                      }

                      let message = `Отправка завершена. Слотов: ${selectedRepresentativeProposalIds.length}, получателей: ${groups.length + topics.length}, отправок: ${sentTo}.`;
                      if (errors.length > 0) {
                        message += `\n\nОшибки:\n${errors.slice(0, 10).join('\n')}`;
                        if (errors.length > 10) {
                          message += `\n... и еще ${errors.length - 10} ошибок`;
                        }
                      }
                      alert(message);
                    }

                    await fetchNotificationCounts();
                    setShowBatchNotificationModal(false);
                    setSelectedProposalsForBatch(new Set());
                    setSelectedPlayersForBatch(new Set());
                    setBatchAllPlayers([]);
                  } catch (error: any) {
                    console.error('Ошибка отправки пакетных уведомлений:', error);
                    const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
                    alert('Ошибка отправки уведомлений: ' + errorMessage);
                  } finally {
                    setSendingBatchNotifications(false);
                  }
                }}
                disabled={sendingBatchNotifications || selectedGroupedBatchProposals.length === 0 || (batchNotificationTab === 'players' ? selectedPlayersForBatch.size === 0 : selectedGroupsAndTopics.size === 0)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sendingBatchNotifications
                  ? 'Отправка...'
                  : batchNotificationTab === 'players'
                    ? `Отправить (${selectedGroupedBatchProposals.length} слотов, ${selectedPlayersForBatch.size} игроков)`
                    : `Отправить (${selectedGroupedBatchProposals.length} слотов, ${selectedGroupsAndTopics.size} групп/топиков)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtReservations;

